// The table screen: the 3D view plus everything laid over it —
// game log, turn bar, buttons, your hand, the spawn menu and pop-ups.
import { CONFIG } from '../../config.ts';
import { TOKEN_CATEGORIES } from '../../shared/tokens.ts';
import { PHASES, laneOrder, slotAt, abilityWindowRefusal, arrivesFaceDown, countsAgainstHealth, cureWords, sacrificeWindowRefusal, sacrificeWorth, countedSacrificeWorth, sitsSideways, tokensOnCard, triggerPayout, type CardView, type GameView, type HeldCard, type PileName, type Seat, type Token, type Where, darkgaleMultiplier } from '../../shared/engine.ts';
import type { Deck } from '../../shared/deck.ts';
import { EMPEROR_ABILITY_SLOTS, cardLabel, costWords, formOf, formWords, spanMode, isEmperor, roman, tieredDamage, tieredRemoval, titleCase, type CardDef, type EmperorAbility, cardNo } from '../../shared/cards.ts';
import { describeFormula, priceOf, wordsOf } from '../../shared/costs.ts';
import { CARD_DB, DATA_FINGERPRINT, TOKEN_DB } from '../data.ts';
import { cardBackSrc, cardFrontSrc, setImg, setImgLazy, tokenColor, tokenSrc } from '../images.ts';
import { el, button, toast, modal, floatingMenu, closeFloatMenu, showCardLarge, downloadText, copyText, type CardBadge, type MenuItem } from '../ui.ts';
import { clock, matchFileName, type MatchFile } from '../../shared/match.ts';
import { useLayout } from '../../shared/layouts.ts';
import { LocalConnection, ReplayConnection, type Connection, OnlineConnection, type ReplayStatus } from './connection.ts';
import { TableView } from './TableView.ts';
import { startTutorial } from './tutorial.ts';
import { cardRulesNode } from '../cardRules.ts';
import { chooseBackground, chosenBackground } from '../prefs.ts';
import { hasRules, ruleFor, showRules } from '../rules.ts';
import { isTouchScreen } from '../touch.ts';
import { chatBox } from '../chat.ts';

/** Start a new game with `deck`, or watch a recorded one (`replay`). */
export interface TableOptions {
  names: string[];
  deck?: Deck;
  replay?: { file: MatchFile; seat?: Seat; at?: number; playing?: boolean; speed?: number };
  online?: OnlineConnection; // a game on the game server (Single Versus)
  onExit: () => void; // where leaving the table goes
  onMenu?: () => void; // where "Main Menu" on the end screen goes (onExit if not given)
}

// On narrower screens (where the turn bar also moves down above the hand) the open log would cover the
// opponent's Spell slots, so it starts folded there. Once the player opens or folds it, their choice is kept.
let logCollapsed = typeof matchMedia === 'function' && matchMedia('(max-width: 1180px)').matches;
let guideOpen = false;
let touchTipShown = false; // the touch controls tip shows once per visit // the phase guide under the turn bar starts folded away

export function startTable(opts: TableOptions) {
  // the 1v1 or the 2v2 table (mat, slots, piles) has to be in place before anything is set up
  useLayout(!!(opts.replay ? opts.replay.file.teams : opts.online ? opts.online.teams : false));
  const replay = opts.replay ? new ReplayConnection(opts.replay.file, opts.replay.seat ?? 0, opts.replay.at ?? 0) : null;
  const online = replay ? null : (opts.online ?? null);
  const local = replay || online ? null : new LocalConnection({ name: opts.names[0], deck: opts.deck! });
  const conn: Connection = replay ?? online ?? local!;
  const spectating = !!online?.spectating; // watching someone else's game live
  const watching = !!replay || spectating; // a replay, or watching live: everything can be looked at, nothing can be changed
  const app = document.getElementById('app')!;
  const stage = el('div', { class: 'table-stage' });
  const overlay = el('div', { class: 'hud' });
  const screen = el('div', { class: 'table-screen' }, stage, overlay);
  app.replaceChildren(screen);
  document.body.classList.add('in-game');

  let view: GameView | null = null;
  /** In a game (not the solo table) the table plays the rules itself: nothing is drawn, searched, moved or changed by hand. */
  const handsOff = () => CONFIG.lockHandEdits && view?.mode !== 'solo';
  let leaving = false;

  // ---------------- 3D view ----------------
  const table = new TableView(
    stage,
    {
      // (a card clicked for split damage takes as many points as the stepper says)
      send: (a) => conn.send(a.type === 'pick' && a.card && view?.pick?.left !== undefined ? { ...a, amount: Math.min(splitAmount, view.pick.left) } : a),
      cardMenu: (c, x, y, expired) => openCardView(c, expired),
      cardAct: (c, expired, x, y) => cardAct(c, expired, x, y),
      unitClick: (c, x, y) => unitClick(c, x, y),
      askReplace: (old, expired, def, x, y, how) => askReplace(old, expired, def, x, y, how),
      askAbility: (tokenId, c, kind, x, y) => askAbility(tokenId, c, kind, x, y),
      handPoint: () => {
        const r = handCards.getBoundingClientRect();
        return r.width ? { x: r.left + r.width / 2, y: r.top + r.height * 0.45 } : null;
      },
      tokenMenu: (t, x, y) => openTokenMenu(t, x, y),
      pileMenu: (seat, pile, x, y) => openPileMenu(seat, pile, x, y),
      askWhere: (pile, x, y) => askWhere(pile, x, y),
      askCost: (cardId, def, x, y, how) => askCost(cardId, def, x, y, how),
      deckClick: (x, y) => deckClick(x, y),
      discardClick: (x, y) => discardClick(x, y),
      showOpenHand: (seat) => showOpenHand(seat),
      cardChip: (c) => conn.send({ type: 'payout', id: c.id }),
      notice: (text) => toast(text, 'info'),
      // The Remaining is on its way to your hand: the hand bar glows deep red until it has arrived and settled
      remainingToHand: () => {
        handBar.classList.remove('remaining-arrives');
        void handBar.offsetWidth; // (so the glow starts over if it was already running)
        handBar.classList.add('remaining-arrives');
        window.setTimeout(() => handBar.classList.remove('remaining-arrives'), 4200);
      },
      // a card was revealed (hold R and click it): whoever did it is told it went out; everyone else is shown the card
      revealed: (seat, num, text) => {
        const def = CARD_DB.byNumber.get(num);
        if (view && seat === view.you && !watching) toast(`You revealed ${def?.name ?? 'a card'} to your opponent.`, 'info');
        else showCardLarge(cardFrontSrc(num), def ? cardLabel(def) : 'Card', text, [], { rules: cardRulesNode(def) });
      },
      placeClick: (x, y, shift, free) => placeSelected(x, y, shift, free),
      placeCancel: () => selectCard(null),
      placeHover: (x, y, shift, free) => {
        if (selectedCard) table.hoverFromHand(x, y, selectedCard, shift, free, darkArmed);
      },
      isOverHand: (x, y) => overElement(handBar, x, y),
      setHandHighlight: (on) => handBar.classList.toggle('drop-target', on),
    },
    conn.seat,
    watching,
  );  if (location.search.includes('debug')) Object.assign(window, { table }); // (for checking the table by hand)


  function overElement(node: HTMLElement, x: number, y: number) {
    const r = node.getBoundingClientRect();
    return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
  }

  // ---------------- game log (top-left) ----------------
  const logList = el('ol', { class: 'log-list', 'aria-live': 'polite' });
  const logCount = el('span', { class: 'log-count' });
  const logToggle = el(
    'button',
    { class: 'log-head', type: 'button', 'aria-expanded': String(!logCollapsed) },
    el('span', { class: 'log-title' }, 'Game Log'),
    logCount,
    el('span', { class: 'chev', 'aria-hidden': 'true' }, '▾'),
  );
  // online: a Chat tab beside the Game Log (people watching read it, the players write in it too)
  let unread = 0;
  const chatCount = el('span', { class: 'chat-unread', hidden: true });
  const chatTab = el('button', { class: 'log-tab chat-tab', type: 'button', title: 'Talk to the other players' }, 'Chat', chatCount);
  const chat = online ? chatBox(online, { readOnly: spectating, onNew: (line) => { if (!logPanel.classList.contains('show-chat') || logCollapsed) { unread++; chatCount.textContent = String(unread); chatCount.hidden = false; toast(`${line.name}: ${line.text}`, 'info'); } } }) : null;
  const logPanel = el('section', { class: `log ${logCollapsed ? 'collapsed' : ''}` }, el('div', { class: 'log-tabs' }, logToggle, chat ? chatTab : null), logList, chat ? chat.root : null);
  /** Show the Game Log or the Chat (and unfold the panel). */
  function showLogTab(which: 'log' | 'chat') {
    logPanel.classList.toggle('show-chat', which === 'chat');
    chatTab.classList.toggle('is-on', which === 'chat');
    if (which === 'chat') { unread = 0; chatCount.hidden = true; }
  }
  logToggle.addEventListener('click', () => {
    if (logPanel.classList.contains('show-chat') && !logCollapsed) { showLogTab('log'); return; } // (from the chat: back to the log)
    logCollapsed = !logCollapsed;
    logPanel.classList.toggle('collapsed', logCollapsed);
    logToggle.setAttribute('aria-expanded', String(!logCollapsed));
    logList.scrollTop = logList.scrollHeight;
  });
  chatTab.addEventListener('click', () => {
    if (logCollapsed) {
      logCollapsed = false;
      logPanel.classList.remove('collapsed');
      logToggle.setAttribute('aria-expanded', 'true');
    }
    showLogTab('chat');
    chat?.focus();
  });
  let lastLogId = 0;
  function renderLog(v: GameView) {
    if ((v.log.at(-1)?.id ?? 0) < lastLogId) {
      // a replay jumped back in time: start the list again
      logList.replaceChildren();
      lastLogId = 0;
      logCount.textContent = String(v.log.length);
    }
    const fresh = v.log.filter((l) => l.id > lastLogId);
    if (!fresh.length) return;
    lastLogId = v.log.at(-1)!.id;
    for (const l of fresh) {
      // (2v2: your teammate's lines are your team's colour too)
      const seatCls = l.seat === null ? 'sys' : l.seat === v.you || (v.teams && l.seat % 2 === v.you % 2) ? 'me' : 'opp';
      const li = el('li', { class: `log-item log-${seatCls}` }, l.text);
      if (l.seat !== null) li.style.borderLeftColor = l.seat % 2 === v.first % 2 ? CONFIG.playerColors.first : CONFIG.playerColors.second; // each line in the colour its player plays (blue or red)
      logList.append(li);
    }
    while (logList.children.length > 300) logList.firstElementChild!.remove();
    logList.scrollTop = logList.scrollHeight;
    logCount.textContent = String(v.log.length);
  }

  // ---------------- turn bar (top-center) ----------------
  const whose = el('div', { class: 'turn-whose' });
  const phases = el(
    'ol',
    { class: 'phases' },
    PHASES.map((p) => el('li', { class: 'phase' }, p.replace(' Phase', ''))),
  );
  const counts = el('div', { class: 'turn-counts' });
  // the phase guide's switch sits in the bar itself, so nothing hangs below the bar over the opponent's hand
  const guideToggle = el('button', { class: 'guide-head', type: 'button', 'aria-expanded': String(guideOpen), title: 'What this phase is for, step by step' }, 'Phase guide', el('span', { class: 'chev', 'aria-hidden': 'true' }, '▾'));
  const overtimeChip = el('span', { class: 'overtime-chip', hidden: true, title: 'Overtime' }, 'Overtime');
  // the Battle Timer (1v1 and 2v2 online): off, it asks to turn it on; on, it shows the time left in this turn
  const timerChip = el('button', { class: 'btn battle-timer', type: 'button', hidden: true });
  timerChip.addEventListener('click', () => askTimer());
  const nextBtn = button('Next Phase', () => conn.send({ type: 'nextPhase' }), 'btn-primary turn-btn');
  const fractalNote = el('span', { class: 'fractal-note', hidden: true });
  const turnBar = el(
    'section',
    { class: 'turnbar' },
    el('div', { class: 'turn-main' }, el('div', { class: 'turn-top' }, whose, overtimeChip), phases, el('div', { class: 'turn-foot' }, counts, guideToggle)),
    el('div', { class: 'turn-actions' }, nextBtn, fractalNote),
  );

  // ---------------- phase guide (folds out under the turn bar; its switch is in the bar) ----------------
  const guideTitle = el('span', { class: 'guide-title' });
  const guideList = el('ul', { class: 'guide-list' });
  const guide = el('section', { class: `guide ${guideOpen ? '' : 'collapsed'}`, 'aria-label': 'Phase guide' }, guideTitle, guideList);
  guide.hidden = !guideOpen;
  guideToggle.classList.toggle('is-open', guideOpen);
  guideToggle.addEventListener('click', () => {
    guideOpen = !guideOpen;
    guide.classList.toggle('collapsed', !guideOpen);
    guide.hidden = !guideOpen;
    guideToggle.classList.toggle('is-open', guideOpen);
    guideToggle.setAttribute('aria-expanded', String(guideOpen));
  });
  const notices = el('div', { class: 'notices', 'aria-live': 'polite' });
  const turnStack = el('div', { class: 'turn-stack' }, turnBar, guide, notices);

  /** "Bleed", "Decay, Frenzy and Divinity": the names of some token effects, as a phrase. */
  function effectNames(effects: string[]) {
    const names = effects.map((e) => TOKEN_DB.list.find((t) => t.effect === e)?.name).filter(Boolean) as string[];
    return names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names.at(-1)}` : (names[0] ?? '');
  }

  /** Darkgale summons are on when the setting names a token that exists. */
  const darkgaleOn = !!CONFIG.turn.darkgale.token && TOKEN_DB.list.some((t) => t.effect === CONFIG.turn.darkgale.token);

  type Step = { text: string; by?: 'table' | 'end' }; // by: the table does it now / when you press End Turn
  /** What happens in a phase: what the table does by itself, and what is still yours to do. */
  function guideSteps(v: GameView, phase: number): Step[] {
    const T = CONFIG.turn;
    const gerzite = (normal: number) => (v.doubleOvertime ? CONFIG.overtime.doubleGerzite : v.overtime ? CONFIG.overtime.gerzite : normal);
    const ticks = (list: string[]) => effectNames(list.filter((e) => e !== CONFIG.tokenRules.entrenchedToken && !T.timers.includes(e)));
    const timers = (list: string[]) => effectNames(list.filter((e) => T.timers.includes(e)));
    const steps: (Step | false | '')[] = [];
    // what status tokens do by themselves at this moment of the turn
    const acts = (when: string): Step[] =>
      T.statusActs.filter((x) => x.when === when).map((x) => {
        const name = effectNames([x.effect]) || titleCase(x.effect);
        if (x.does === 'damage') return { text: `${name} deals ${x.amount === 'value' ? 'its number in' : x.amount} damage${x.thenDrop ? `, then drops by ${x.thenDrop}` : ''}${x.then ? `, then you ${x.then}` : ''}`, by: 'table' as const };
        if (x.does === 'heal') return { text: `${name} heals ${x.amount} damage`, by: 'table' as const };
        return { text: (x.text ?? `${name}: time to deal with it`).replace(' on {card}', '').replace('{card}', 'the card') + ' (its token pulses)' };
      });
    if (phase === 0) {
      steps.push(
        gerzite(T.gerziteAtStart) > 0 && { text: `Gain ${gerzite(T.gerziteAtStart)} Gerzite`, by: 'table' },
        T.untapAtStart && { text: 'Your tapped cards untap', by: 'table' },
        ...acts('start'),
        ticks(T.tickDown.start) && { text: `${ticks(T.tickDown.start)} counters go down by 1${T.buildingsShedOverheal ? ' (Buildings lose all of it)' : ''}`, by: 'table' },
        timers(T.tickDown.start) && { text: `Each ${timers(T.tickDown.start)} goes down by 1`, by: 'table' },
        T.countUp.enabled && !!effectNames([T.countUp.token]) && { text: `Each ${effectNames([T.countUp.token])} goes up by 1`, by: 'table' },
        T.durations.enabled && T.durations.drainWhen === 'start' && { text: 'Counters with a wear-off timer (a temporary boost) lose 1 of it', by: 'table' },
        { text: 'Effects that happen “before your Overdraw Phase” go off now', by: 'table' },
        { text: T.overdrawCost > 0 ? `Overdraw if you wish: right-click your deck. Each card costs ${T.overdrawCost} Gerzite (${CONFIG.handLimit} in hand at most)` : `Overdraw if you wish, up to ${CONFIG.handLimit} cards in hand` },
      );
    } else if (phase === 1) {
      steps.push(
        ...acts('summon'),
        ticks(T.tickDown.summon) && { text: `${ticks(T.tickDown.summon)} counters go down by 1`, by: 'table' },
        T.tickDown.summon.includes(CONFIG.tokenRules.entrenchedToken) && { text: 'Units summoned last turn are freed from Entrenched', by: 'table' },
        T.payToPlay && { text: 'Playing a card takes its cost off your Gerzite bar (Ctrl or ⌘ + drop = free)', by: 'table' },
        T.payToPlay && darkgaleOn && { text: 'Darkgale summon: press the button on your hand bar first. The card is paid in Dark Gerzite and gets the Darkgale token', by: 'table' },
        { text: T.playPhases.enabled ? 'This is where you play cards from your hand, of every type: one unit per lane, one Spell per slot. After this phase only Spells can be played' : 'Summon units and play Spells: one unit per lane, one Spell per slot' },
        { text: '“Entrance” abilities happen as their card lands' },
        { text: CONFIG.tokenRules.ambushEndsOnTap ? 'Units with Ambush may attack now: tapping one takes its Ambush token off' : 'Units with Ambush may attack now, then lose Ambush' },
      );
    } else if (phase === 2) {
      steps.push(
        ...acts('attack'),
        ticks(T.tickDown.attack) && { text: `${ticks(T.tickDown.attack)} counters go down by 1`, by: 'table' },
        { text: '“Passive” abilities come first' },
        { text: 'Attack with your units: click one, then click the card it attacks · the table deals the damage and taps it' },
        { text: T.playPhases.enabled ? 'Emperor abilities (click your Emperor) · Spells can still be played (to boost an attack, say); no other cards · Stun takes effect' : 'Emperor abilities (click your Emperor) and Spells · Stun takes effect' },
        CONFIG.linkedSlots.length === 2 && { text: `Damage on your Emperor or your Minion is copied to the other${CONFIG.linkMirrorsStatus ? ', and so are statuses your opponent puts on them' : ''}`, by: 'table' },
        { text: 'A unit that doesn’t attack earns you a Fractal in the End Phase' },
      );
    } else {
      const offer = v.turn === v.you ? v.fractalOffer : null;
      const draw = Math.max(0, Math.min(T.passiveDrawMax, T.passiveDrawTo - v.players[v.turn].handCount));
      const missed = Math.max(0, draw - v.players[v.turn].deckCount);
      steps.push(
        { text: T.playPhases.enabled ? 'No more cards from your hand, Spells included (unless a card effect says so: Ctrl or ⌘ plays it free)' : 'No more summoning or Spells' },
        ...acts('end'),
        ticks(T.tickDown.end) && { text: `${ticks(T.tickDown.end)} counters go down by 1`, by: 'table' },
        // (the counters that hold an amount carry a little turn clock instead: CONFIG.turn.durations)
        T.durations.enabled && T.durations.when === 'end' && { text: `Turn clocks on ${effectNames(T.durations.effects) || 'counters'} go down by 1; a counter whose clock reaches 0 comes off`, by: 'table' },
        T.spDecay.enabled && T.spDecay.amount > 0 && { text: `Your Buildings that aren’t Entrenched lose ${T.spDecay.amount} SP${T.spDecay.more.crumble ? ` (${T.spDecay.more.crumble} with Crumble)` : ''}: it goes on their blue SP Decay counter`, by: 'end' },
        timers(T.tickDown.turnEnd) && { text: `Each ${timers(T.tickDown.turnEnd)} goes down by 1`, by: 'end' },
        CONFIG.tokenRules.ambushEndsWithTurn && { text: 'Ambush tokens still on the table are removed: Ambush lasts for one turn', by: 'end' },
        { text: `Fractals: ${offer ?? 0} from your idle units, counted as the turn ends (a mistaken tap can still be undone)`, by: 'end' },
        T.passiveDrawMax > 0 && { text: draw - missed > 0 ? `Draw ${draw - missed} card${draw - missed === 1 ? '' : 's'} (never past ${T.passiveDrawTo} in hand)` : draw > 0 ? 'No cards left to draw' : `No passive draw: ${T.passiveDrawTo} or more cards in hand`, by: 'end' },
        missed > 0 && T.deckOutDamage > 0 && { text: `Deck-Out: the deck is ${missed} card${missed === 1 ? '' : 's'} short, so the Emperor takes ${missed * T.deckOutDamage} damage`, by: 'end' },
        gerzite(T.gerziteAtEnd) > 0 && { text: `Gain ${gerzite(T.gerziteAtEnd)} Gerzite`, by: 'end' },
        v.overtime && CONFIG.overtime.emperorDamage > 0 && { text: `${v.doubleOvertime ? 'Double Overtime' : 'Overtime'}: both Emperors take ${v.doubleOvertime ? CONFIG.overtime.doubleDamage : CONFIG.overtime.emperorDamage} damage`, by: 'end' },
      );
    }
    return steps.filter(Boolean) as Step[];
  }

  let guideKey = '';
  function renderGuide(v: GameView) {
    const mine = v.turn === v.you;
    const steps = guideSteps(v, v.phase);
    const key = `${v.turn}|${v.phase}|${JSON.stringify(steps)}`;
    if (key === guideKey) return;
    guideKey = key;
    guideTitle.textContent = `${mine ? '' : `${v.players[v.turn]?.name ?? 'Opponent'}’s `}${PHASES[v.phase]} guide`;
    guideList.replaceChildren(
      ...steps.map((st) =>
        el(
          'li',
          { class: `guide-step ${st.by ? 'is-auto' : ''}` },
          el('span', { class: 'guide-mark', 'aria-hidden': 'true' }, st.by ? '✓' : '•'),
          el('span', { class: 'guide-text' }, st.text),
          st.by ? el('span', { class: 'guide-by' }, st.by === 'end' ? 'on End Turn' : 'done for you') : null,
        ),
      ),
    );
  }

  // ---------------- Overtime and fallen Emperors ----------------
  let wasOvertime: boolean | null = null;
  let wasDouble: boolean | null = null;
  let fallenKey = '';
  function renderEndgame(v: GameView) {
    overlay.classList.toggle('overtime', v.overtime);
    overlay.style.setProperty('--ember', CONFIG.overtime.ember);
    overtimeChip.hidden = !v.overtime;
    const O = CONFIG.overtime;
    overtimeChip.textContent = v.doubleOvertime ? 'Double Overtime' : 'Overtime';
    overtimeChip.title = v.doubleOvertime ? `Double Overtime: ${O.doubleGerzite} Gerzite at the start and the end of every turn, and both Emperors take ${O.doubleDamage} damage at the end of every turn` : `Overtime: ${O.gerzite} Gerzite at the start and the end of every turn, and both Emperors take ${O.emperorDamage} damage at the end of every turn (Double Overtime from global turn ${O.doubleFrom})`;
    const splashFor = (title: string, line: string) => {
      // the moment (Double) Overtime begins: one big line across the table that fades by itself
      const splash = el('div', { class: 'overtime-splash', role: 'status' }, el('strong', {}, title), el('span', {}, line));
      overlay.append(splash);
      setTimeout(() => splash.remove(), 5200);
    };
    if (wasOvertime === false && v.overtime && !v.doubleOvertime && !jumped) splashFor('Overtime', `${O.gerzite} Gerzite at the start and end of each turn · both Emperors take ${O.emperorDamage} damage every turn`);
    if (wasDouble === false && v.doubleOvertime && !jumped) splashFor('Double Overtime', `${O.doubleGerzite} Gerzite at the start and end of each turn · both Emperors take ${O.doubleDamage} damage every turn`);
    wasOvertime = v.overtime;
    wasDouble = !!v.doubleOvertime;
    jumped = false;
    const ask = v.endRequest;
    const peekAsk = v.peekRequest;
    const cures = v.tokens.filter((t) => t.cure !== undefined);
    const rolls = v.field.filter((c) => c.roll !== undefined);
    const tAsk = v.battleTimer?.ask ?? null;
    const key = `${tAsk ? `${tAsk.from}${tAsk.on}${tAsk.yes.join('')}` : ''}|${rolls.map((c) => `${c.id}:${c.roll}`).join(',')}|${v.fallen.join(',')}|${ask ? `${ask.kind}${ask.from}` : ''}|${peekAsk ? `${peekAsk.from}${peekAsk.pile}${peekAsk.count}` : ''}|${v.result ? 'over' : ''}|${replayNote}|${cures.map((t) => `${t.id}:${t.cure}`).join(',')}|${v.players.map((p) => (p.out ? 1 : 0)).join('')}`;
    if (key !== noticeKey) {
      noticeKey = key;
      const fallen = v.fallen.flatMap((seat) => {
        const emperor = v.field.find((c) => c.owner === seat && !c.spawned && c.num !== null && CARD_DB.byNumber.get(c.num)?.types.includes('emperor'));
        const def = emperor?.num != null ? CARD_DB.byNumber.get(emperor.num) : undefined;
        const whoseEmperor = seat === v.you && !watching ? 'Your Emperor' : `${v.players[seat]?.name ?? 'Opponent'}’s Emperor`;
        // 2v2: a player whose Emperor has fallen is out once the turn ends (unless it is healed before then). Once
        // they are out it is said once, in a message that fades away: their teammate plays on, nothing to do about it
        if (v.teams && v.players[seat]?.out) return [];
        const out = v.teams ? ' Unless it is healed, they are out as this turn ends.' : '';
        return el('div', { class: `notice notice-fallen ${seat === v.you ? 'is-mine' : ''}` }, el('strong', {}, `${whoseEmperor} has fallen`), el('span', {}, ` — ${def?.name ?? 'it'} has taken damage equal to its health (${def?.health ?? '?'}).${out}`));
      });
      if (v.teams && !v.result) {
        v.players.forEach((pl, seat) => {
          if (!pl?.out || outSaid.has(seat)) return;
          outSaid.add(seat);
          const mate = (seat + 2) % 4;
          const mine = seat === v.you && !watching;
          toast(mine ? `You’re out: your Emperor has fallen. You’re watching ${v.players[mate]?.name ?? 'your teammate'} play on; their hand is a click away.` : `${pl.name}’s Emperor has fallen: they are out, and ${mate === v.you && !watching ? 'you play' : `${v.players[mate]?.name ?? 'their teammate'} plays`} on alone.`, 'info');
        });
      }
      notices.replaceChildren(...fallen, ...(!v.result ? cures.map((t) => cureNotice(v, t)).filter((n): n is HTMLDivElement => !!n) : []), ...(!v.result ? rolls.map((c) => cardRollNotice(v, c)) : []), ...(peekAsk && !v.result && !watching ? [peekRequestNotice(v, peekAsk)] : []), ...(ask && !v.result && !watching ? [endRequestNotice(v, ask)] : []), ...(tAsk && !v.result && !watching ? [timerRequestNotice(v, tAsk)] : []), ...(watching && replayNote ? [el('div', { class: 'notice notice-info' }, replayNote)] : []));
    }
    if (v.result && !watching) showEndScreen(v);
  }
  let noticeKey = '';
  const outSaid = new Set<number>(); // 2v2: the players whose being out has been said (once, in a message that fades)
  let jumped = false; // a replay just skipped to another moment: no one-off splashes for that update
  let replayNote = '';

  // ---------------- ending the game ----------------
  const endWords = { forfeit: 'forfeit', win: 'claim the win', call: 'call the game here' } as const;
  /** Is this seat you, or (2v2) your teammate? */
  const allyOf = (v: GameView, seat: Seat) => (v.teams ? seat % 2 === v.you % 2 : seat === v.you);
  /** 2v2: your teammate's seat. */
  const mateOf = (v: GameView): Seat | null => (v.teams ? (((v.you + 2) % 4) as Seat) : null);
  /** "Ana & Cam" in 2v2, "Ana" in 1v1: the side a seat plays for. */
  const sideName = (v: GameView, seat: Seat) => (v.teams ? v.players.filter((_, i) => i % 2 === seat % 2).map((p) => p.name).join(' & ') : (v.players[seat]?.name ?? 'Player'));
  /** A seat on the other side from you. */
  const foeOf = (v: GameView): Seat => (v.teams ? (((v.you + 1) % 2) as Seat) : ((v.you === 0 ? 1 : 0) as Seat));

  /** One player has asked to end the game: the other answers, the asker can take it back. */
  function endRequestNotice(v: GameView, ask: NonNullable<GameView['endRequest']>) {
    const who = v.players[ask.from]?.name ?? 'Your opponent';
    if (ask.from === v.you) {
      return el('div', { class: 'notice notice-ask' }, el('span', {}, `Waiting for ${sideName(v, foeOf(v))} to answer your request to ${endWords[ask.kind]}…`), button('Take it back', () => conn.send({ type: 'answerEnd', accept: false }), 'btn-ghost btn-small'));
    }
    if (allyOf(v, ask.from)) return el('div', { class: 'notice notice-ask' }, el('span', {}, `${who} asked to ${endWords[ask.kind]}. Waiting for ${sideName(v, foeOf(v))} to answer…`));
    const what =
      ask.kind === 'forfeit' ? `${who} wants to forfeit. Accept and you win.` : ask.kind === 'win' ? `${who} says they have won. Accept and the game is theirs.` : `${who} proposes to call the game here: the Emperor with the larger share of its health left wins (even: whoever dealt more damage to Emperors).`;
    return el(
      'div',
      { class: 'notice notice-ask' },
      el('span', {}, what),
      el('span', { class: 'notice-buttons' }, button('Accept', () => conn.send({ type: 'answerEnd', accept: true }), 'btn-primary btn-small'), button('Decline', () => conn.send({ type: 'answerEnd', accept: false }), 'btn-ghost btn-small')),
    );
  }

  // ---------------- the Battle Timer ----------------
  const BT = CONFIG.battleTimer;
  const timerWords = `${Math.floor(BT.seconds / 60)}:${String(BT.seconds % 60).padStart(2, '0')}`;
  /** The Battle Timer can be asked for here: an online 1v1 or 2v2 game you are playing in (not the tutorial). */
  const timerHere = (v: GameView) => !!online && !watching && v.mode === 'versus' && !v.tutorial && !v.result;
  function askTimer() {
    const v = view;
    if (!v || !timerHere(v)) return;
    if (v.battleTimer?.ask) return toast('A Battle Timer request is already waiting for an answer.', 'info');
    if (v.players[v.you]?.out) return toast('Your Emperor has fallen: your teammate decides that now.', 'info');
    const on = !!v.battleTimer?.on;
    const r = timerChip.getBoundingClientRect();
    floatingMenu(r.right - 260, r.bottom + 6, 'Battle Timer', [
      el('div', { class: 'float-note' }, on ? `The Battle Timer is on: ${timerWords} for each turn. Turning it off needs ${sideName(v, foeOf(v))} to agree.` : `Each player gets ${timerWords} for each of their turns, fresh each time the turn comes back to them. Run out before you pass the turn on and you lose by forfeit. ${sideName(v, foeOf(v))} ${v.teams ? 'both have' : 'has'} to agree${v.bots?.length ? ' (bots always do)' : ''}.`),
      { label: on ? 'Ask to turn it off' : 'Ask to turn it on', onClick: () => conn.send({ type: 'timerAsk', on: !on }) },
    ]);
  }
  /** Someone asked to turn the Battle Timer on or off: the other side answers (2v2: both of them), the asker can take it back. */
  function timerRequestNotice(v: GameView, ask: NonNullable<NonNullable<GameView['battleTimer']>['ask']>) {
    const who = v.players[ask.from]?.name ?? 'Your opponent';
    const what = ask.on ? 'turn on the Battle Timer' : 'turn off the Battle Timer';
    // who still has to say yes (bots say yes at once; a 2v2 player who is out has no say)
    const left = v.players.map((_, i) => i as Seat).filter((i) => (v.teams ? i % 2 !== ask.from % 2 : i !== ask.from) && !v.players[i]?.out && !v.bots?.includes(i) && !ask.yes.includes(i));
    const waitingFor = left.map((i) => v.players[i]?.name ?? 'Player').join(' & ') || sideName(v, foeOf(v));
    if (allyOf(v, ask.from)) {
      return el('div', { class: 'notice notice-ask' }, el('span', {}, `${ask.from === v.you ? 'You asked' : `${who} asked`} to ${what}. Waiting for ${waitingFor} to answer…`), button('Take it back', () => conn.send({ type: 'timerAnswer', accept: false }), 'btn-ghost btn-small'));
    }
    if (ask.yes.includes(v.you)) return el('div', { class: 'notice notice-ask' }, el('span', {}, `You agreed to ${what}. Waiting for ${waitingFor}…`));
    const why = ask.on ? ` Each player gets ${timerWords} per turn; running out loses by forfeit.${v.teams ? ' Both of you have to agree.' : ''}` : v.teams ? ' Both of you have to agree.' : '';
    return el(
      'div',
      { class: 'notice notice-ask' },
      el('span', {}, `${who} asks to ${what}.${why}`),
      el('span', { class: 'notice-buttons' }, button('Accept', () => conn.send({ type: 'timerAnswer', accept: true }), 'btn-primary btn-small'), button('Decline', () => conn.send({ type: 'timerAnswer', accept: false }), 'btn-ghost btn-small')),
    );
  }
  const mmss = (ms: number) => { const t = Math.max(0, Math.ceil(ms / 1000)); return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`; };
  let warnedKey = '';
  /** Several times a second: the timer chip's countdown, the warning at 30 seconds, and how long players who left have to come back. */
  function tickTimers() {
    const v = view;
    if (!v) return;
    const bt = v.battleTimer;
    const showChip = !!online && v.mode === 'versus' && !v.tutorial && (timerHere(v) || !!bt?.on);
    timerChip.hidden = !showChip;
    const clk = online?.clock ?? null;
    if (showChip) {
      if (bt?.on && clk && !v.result) {
        const left = clk.paused ? clk.left : clk.endsAt - Date.now();
        const mine = clk.seat === v.you && !watching;
        timerChip.textContent = `⏱ ${mine ? 'You' : (v.players[clk.seat]?.name ?? 'Player')} ${mmss(left)}${clk.paused ? ' · paused' : ''}`;
        timerChip.classList.toggle('is-low', !clk.paused && left <= BT.warnAt * 1000);
        timerChip.classList.toggle('is-mine', mine);
        timerChip.title = `Battle Timer: ${timerWords} per turn. Click to ask to turn it off.`;
        const key = `${clk.seat}:${Math.round(clk.endsAt / 1000)}`;
        if (mine && !clk.paused && left <= BT.warnAt * 1000 && left > 0 && warnedKey !== key) {
          warnedKey = key;
          toast(`${BT.warnAt} seconds left in your turn! Pass the turn on before the Battle Timer runs out, or you lose by forfeit.`, 'error');
        }
      } else {
        timerChip.textContent = bt?.on ? '⏱ Timer on' : '⏱ Battle Timer';
        timerChip.classList.remove('is-low', 'is-mine');
        timerChip.title = bt?.on ? `Battle Timer: ${timerWords} per turn (bots aren’t timed). Click to ask to turn it off.` : `Ask to play with a Battle Timer: ${timerWords} per turn`;
      }
      timerChip.classList.toggle('is-on', !!bt?.on);
    }
    // players who left part-way: a countdown until they lose by forfeit
    const away = (online?.awayUntil ?? []).map((until, i) => ({ until, i })).filter((x) => x.until !== null && !v.result && (watching || x.i !== v.you));
    const text = away.map(({ until, i }) => `${v.players[i]?.name ?? 'A player'} left the table: ${mmss(until! - Date.now())} to come back, or ${goneOutcome(v, i as Seat)}.`).join(' ');
    if (awayNote.textContent !== text) awayNote.textContent = text;
    awayNote.hidden = !text;
  }
  /** What happens to a player who doesn't come back: they lose, or (2v2, teammate still in) they drop out. */
  function goneOutcome(v: GameView, seat: Seat) {
    const mate = v.teams ? (((seat + 2) % 4) as Seat) : null;
    if (mate !== null && !v.players[mate]?.out) return `they are out and ${mate === v.you && !watching ? 'you take' : `${v.players[mate]?.name ?? 'their teammate'} takes`} over their side`;
    return 'they lose by forfeit';
  }
  const awayNote = el('div', { class: 'notice notice-info away-note', hidden: true });
  turnStack.append(awayNote);
  const timerTick = window.setInterval(tickTimers, 250);

  /** Total damage counters on a player's Emperor, and its full health, for the summaries. */
  function emperorLine(v: GameView, seat: Seat) {
    const emperor = v.field.find((c) => c.owner === seat && !c.spawned && c.num !== null && CARD_DB.byNumber.get(c.num)?.types.includes('emperor'));
    const def = emperor?.num != null ? CARD_DB.byNumber.get(emperor.num) : undefined;
    const hurt = emperor ? tokensOnCard(emperor, v.tokens).filter((t) => CONFIG.tokenRules.damageTokens.includes(TOKEN_DB.byId.get(t.tokenId)?.effect ?? '')).reduce((n, t) => n + t.value, 0) : 0;
    return { name: def?.name ?? 'no Emperor', hurt, health: def?.health ?? null, num: def?.number ?? null };
  }

  /** The match so far: kept here on the solo table, fetched from the game server online. */
  function matchFile(): Promise<MatchFile | null> {
    if (local) return Promise.resolve(local.match.toFile());
    if (online) return online.file?.finished ? Promise.resolve(online.file) : online.requestFile();
    return Promise.resolve(null);
  }

  /** The replay of a finished game, downloaded as a file (games aren't saved part-way: only replays are kept). */
  async function saveReplay() {
    const file = await matchFile();
    if (!file?.finished) return;
    downloadText(matchFileName(file), JSON.stringify(file));
    toast('Replay downloaded. Open it from Watch a Replay on the main menu.', 'ok');
  }

  function openGameMenu() {
    const v = view;
    if (!v || watching) return;
    const r = gameBtn.getBoundingClientRect();
    const versus = v.mode === 'versus';
    const busy = !!v.endRequest;
    const hurt = v.players.map((_, i) => emperorLine(v, i as Seat).hurt);
    const callNote = versus ? `Damage so far: ${v.players.map((p, i) => `${p.name}’s Emperor ${hurt[i]}`).join(', ')}.` : `Damage on your Emperor so far: ${hurt[0]}.`;
    const items: (MenuItem | Node)[] = [
      { label: 'Background…', onClick: openBackgrounds },
      el('div', { class: 'float-note' }, `${callNote} Calling it here ends the game on that score${versus ? ', if your opponent agrees' : ''}.`),
      { label: 'Call it here', disabled: busy, onClick: () => conn.send({ type: 'requestEnd', kind: 'call' }) },
    ];
    if (versus) {
      items.push({ label: 'I won', disabled: busy, onClick: () => conn.send({ type: 'requestEnd', kind: 'win' }) });
      items.push({ label: 'Forfeit', danger: true, disabled: busy, onClick: () => conn.send({ type: 'requestEnd', kind: 'forfeit' }) });
    } else items.push({ label: 'End the game', onClick: () => conn.send({ type: 'requestEnd', kind: 'end' }) });
    items.push({ label: 'Leave table', danger: true, onClick: confirmLeave });
    floatingMenu(r.right - 250, r.bottom + 6, 'Game menu', items);
  }

  /**
   * The room around the table: pick a background. It is this player's own choice. Nothing is sent to the
   * other player, whose table keeps the background they chose. Only the room changes, never the table.
   */
  function openBackgrounds() {
    const B = CONFIG.backgrounds;
    const tiles: HTMLElement[] = [];
    const mark = () => {
      for (const t of tiles) t.setAttribute('aria-pressed', String(t.dataset.id === chosenBackground()));
    };
    const sections = B.groups.map((g) => {
      const list = B.list.filter((b) => b.group === g.id);
      return el(
        'section',
        { class: 'bg-section' },
        el('h3', {}, g.name),
        list.length
          ? el(
              'div',
              { class: 'bg-grid' },
              list.map((b) => {
                const tile = el(
                  'button',
                  {
                    class: 'bg-tile',
                    type: 'button',
                    'data-id': b.id,
                    onclick: () => {
                      chooseBackground(b.id);
                      table.setBackground(b.id);
                      mark();
                    },
                  },
                  el('span', { class: 'bg-swatch', style: b.sky ? `background: ${b.color} url('${b.sky}') center / cover` : `background: linear-gradient(180deg, ${b.color} 0 58%, ${b.floor ?? b.color} 58% 100%)` }, el('span', { class: 'bg-swatch-table' })),
                  el('span', { class: 'bg-name' }, b.name),
                );
                tiles.push(tile);
                return tile;
              }),
            )
          : el('p', { class: 'muted small' }, 'Nothing here yet.'),
      );
    });
    let close = () => {};
    close = modal(
      el(
        'div',
        { class: 'stack' },
        el('h2', {}, 'Background'),
        el('p', { class: 'muted' }, 'The room around the table. Only you see this: your opponent keeps the background they chose.'),
        ...sections,
        el('div', { class: 'row-end' }, button('Done', () => close(), 'btn-primary')),
      ),
      { className: 'bg-modal' },
    );
    mark();
  }

  let endShown = false;
  /**
   * The game is over: who won and how, then watch the replay, keep it, or go back to the menu. It doesn't pop up at
   * once: the last blow is given time to land (CONFIG.endScreen), the table darkens, and then the screen comes in.
   */
  function showEndScreen(v: GameView) {
    if (endShown || !v.result || watching) return;
    endShown = true;
    const E = CONFIG.endScreen;
    const fell = v.result.kind === 'win' && v.players.some((p) => p.out);
    const wait = (fell ? E.afterFall : E.afterOther) * 1000;
    const dusk = el('div', { class: 'end-dusk' });
    dusk.style.animationDelay = `${Math.max(0, wait - E.dusk * 1000)}ms`;
    dusk.style.animationDuration = `${E.dusk * 1000}ms`;
    document.body.append(dusk);
    void Promise.all([matchFile(), new Promise((r) => setTimeout(r, wait))]).then(([file]) => {
      if (file && !leaving) endScreen(v, file);
      setTimeout(() => dusk.remove(), 400);
    });
  }
  function endScreen(v: GameView, file: MatchFile) {
    const res = v.result;
    if (leaving || !res) return;
    closeFloatMenu();
    const versus = v.mode === 'versus';
    const won = res.winner !== null && allyOf(v, res.winner);
    const headline = !versus ? 'Game over' : res.winner === null ? 'It’s a draw' : won ? (v.teams ? 'Your team wins!' : 'You win!') : v.teams ? `${sideName(v, res.winner)} win` : `${v.players[res.winner].name} wins`;
    const loser = res.winner === null ? null : v.teams ? ((res.winner + 1) % 2) as Seat : ((res.winner === 0 ? 1 : 0) as Seat);
    const how =
      res.kind === 'call' ? (versus ? (v.teams ? 'Called where it stood: the team whose Emperors have the larger share of their health left takes it.' : 'Called where it stood: the Emperor with the larger share of its health left takes it.') : 'You called the game here.') : res.kind === 'forfeit' ? (res.timeUp ? `${res.timeUp.seat === v.you && !watching ? 'You' : (v.players[res.timeUp.seat]?.name ?? 'A player')} ${res.timeUp.why === 'clock' ? 'ran out of time on the Battle Timer' : 'left the game and didn’t come back in time'}, which counts as a forfeit.` : `${loser === null ? 'The other side' : sideName(v, loser)} forfeited.`) : res.kind === 'win' ? (v.players.some((p) => p.out) ? (v.players.every((p) => p.out) ? 'Every Emperor fell at once: the side that dealt more damage to Emperors takes it.' : v.teams ? 'A whole team’s Emperors fell.' : 'An Emperor fell.') : 'The win was agreed by both sides.') : res.timeUp?.why === 'everyone' ? 'Everyone left the game and nobody came back in time, so it ended in a draw.' : 'You ended the game.';
    const ran = file.actions.at(-1)?.t ?? 0;
    const rows = v.players.map((p, i) => {
      const e = emperorLine(v, i as Seat);
      return el('tr', { class: res.winner !== null && (v.teams ? i % 2 === res.winner % 2 : res.winner === i) ? 'is-winner' : '' }, el('th', { scope: 'row' }, p.name), el('td', {}, e.name), el('td', {}, e.health ? `${res.damage[i] ?? e.hurt} of ${e.health}` : String(res.damage[i] ?? e.hurt)), el('td', {}, String(p.turns)));
    });
    // the mood: a win, a loss, a draw, or (the solo table) just the end
    const mood = !versus ? 'neutral' : res.winner === null ? 'draw' : won ? 'win' : 'loss';
    const eyebrow = { win: 'Victory', loss: 'Defeat', draw: 'Draw', neutral: 'The End' }[mood];
    // the winning Emperor's card (or, on a loss, the one that beat you; a draw or the solo table: yours)
    const showSeat = (res.winner !== null && versus ? res.winner : v.you) as Seat;
    const champ = emperorLine(v, showSeat);
    const crest = champ.num !== null
      ? el('figure', { class: 'end-champion' }, setImg(el('img', { alt: champ.name, width: '500', height: '700' }), cardFrontSrc(champ.num)), el('figcaption', {}, champ.name))
      : '';
    const close = modal(
      el(
        'div',
        { class: 'stack end-screen' },
        el('div', { class: 'end-banner' }, el('span', { class: 'end-eyebrow' }, eyebrow), el('h2', { class: 'end-headline' }, headline)),
        el(
          'div',
          { class: 'end-body' },
          crest,
          el(
            'div',
            { class: 'stack end-details' },
            el('p', { class: 'muted' }, `${how} The match ran ${clock(ran)} over ${v.globalTurn} full turn${v.globalTurn === 1 ? '' : 's'}.`),
            el('table', { class: 'end-table' }, el('thead', {}, el('tr', {}, el('th', {}, 'Player'), el('th', {}, 'Emperor'), el('th', {}, 'Damage taken'), el('th', {}, 'Turns'))), el('tbody', {}, rows)),
            el('p', { class: 'muted small' }, 'The replay is a small file. Keep it, and open it any time from Watch a Replay on the main menu to watch the whole match again.'),
          ),
        ),
        el(
          'div',
          { class: 'row-end' },
          button('Watch Replay', () => { stopFx(); switchTo({ names: opts.names, replay: { file }, onExit: opts.onMenu ?? opts.onExit }); }, ''),
          button('Download Replay', () => void saveReplay(), 'btn-primary'),
          button('Main Menu', () => { stopFx(); online?.close(true); teardown(); (opts.onMenu ?? opts.onExit)(); }, 'btn-ghost'),
        ),
      ),
      { dismissable: false, className: `end-modal end-${mood}` },
    );
    // confetti for a win, slow embers for a loss
    const stopFx = mood === 'win' ? celebrate('confetti') : mood === 'loss' ? celebrate('embers') : () => {};
    void close;
  }

  /**
   * Bits falling over the whole screen behind the end screen: 'confetti' (a burst from both sides, then a gentle
   * fall in gold, Gerzite blue, violet and white) or 'embers' (a few dim red sparks drifting down). Returns a stop.
   */
  function celebrate(kind: 'confetti' | 'embers'): () => void {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return () => {};
    const E = CONFIG.endScreen;
    const cv = el('canvas', { class: 'end-confetti', 'aria-hidden': 'true' }) as HTMLCanvasElement;
    document.body.append(cv);
    const ctx = cv.getContext('2d')!;
    const fit = () => { cv.width = innerWidth * devicePixelRatio; cv.height = innerHeight * devicePixelRatio; };
    fit();
    addEventListener('resize', fit);
    const W = () => cv.width, H = () => cv.height, d = devicePixelRatio;
    type Bit = { x: number; y: number; vx: number; vy: number; r: number; vr: number; w: number; h: number; color: string; sway: number; life: number };
    const bits: Bit[] = [];
    const colors = kind === 'confetti' ? E.confettiColors : E.emberColors;
    const make = (fromLeft: boolean | null): Bit => {
      const c = colors[Math.floor(Math.random() * colors.length)];
      if (kind === 'embers') return { x: Math.random() * W(), y: -20 * d, vx: (Math.random() - 0.5) * 0.3 * d, vy: (0.4 + Math.random() * 0.6) * d, r: 0, vr: 0, w: (2 + Math.random() * 2.5) * d, h: 0, color: c, sway: Math.random() * 6.28, life: 1 };
      if (fromLeft === null) return { x: Math.random() * W(), y: -20 * d, vx: (Math.random() - 0.5) * 1.5 * d, vy: (1.5 + Math.random() * 2) * d, r: Math.random() * 6.28, vr: (Math.random() - 0.5) * 0.3, w: (6 + Math.random() * 6) * d, h: (3 + Math.random() * 4) * d, color: c, sway: Math.random() * 6.28, life: 1 };
      const ang = (fromLeft ? -1 : 1) * (0.35 + Math.random() * 0.55);
      const sp = (14 + Math.random() * 12) * d;
      return { x: fromLeft ? -10 * d : W() + 10 * d, y: H() * (0.55 + Math.random() * 0.25), vx: Math.cos(ang) * sp * (fromLeft ? 1 : -1) * (fromLeft ? 1 : 1), vy: -Math.abs(Math.sin(ang)) * sp - 6 * d, r: Math.random() * 6.28, vr: (Math.random() - 0.5) * 0.4, w: (6 + Math.random() * 6) * d, h: (3 + Math.random() * 4) * d, color: c, sway: Math.random() * 6.28, life: 1 };
    };
    if (kind === 'confetti') for (let i = 0; i < E.confettiBurst; i++) bits.push(make(i % 2 === 0));
    const t0 = performance.now();
    let raf = 0;
    let last = t0;
    const tick = (now: number) => {
      const dt = Math.min(3, (now - last) / 16.7);
      last = now;
      const age = (now - t0) / 1000;
      // a steady fall for a while after the burst (fewer and fewer), and embers all along, thinly
      const spawning = kind === 'confetti' ? age < E.confettiSeconds : true;
      if (spawning && Math.random() < (kind === 'confetti' ? 0.9 * (1 - age / E.confettiSeconds) : 0.12) * dt) bits.push(make(null));
      ctx.clearRect(0, 0, W(), H());
      for (let i = bits.length - 1; i >= 0; i--) {
        const b = bits[i];
        if (kind === 'confetti') {
          b.vy += 0.35 * d * dt; // gravity
          b.vx *= Math.pow(0.97, dt); b.vy = Math.min(b.vy, 3.2 * d); // air
          b.sway += 0.08 * dt;
          b.x += (b.vx + Math.sin(b.sway) * 0.8 * d) * dt; b.y += b.vy * dt; b.r += b.vr * dt;
          ctx.save();
          ctx.translate(b.x, b.y);
          ctx.rotate(b.r);
          ctx.scale(1, Math.abs(Math.cos(b.sway * 1.7)) * 0.8 + 0.2); // the flutter of a turning piece of paper
          ctx.fillStyle = b.color;
          ctx.fillRect(-b.w / 2, -b.h / 2, b.w, b.h);
          ctx.restore();
        } else {
          b.sway += 0.03 * dt;
          b.x += (b.vx + Math.sin(b.sway) * 0.3 * d) * dt; b.y += b.vy * dt;
          b.life = Math.max(0, 1 - b.y / H());
          ctx.globalAlpha = 0.25 + 0.55 * b.life * (0.6 + 0.4 * Math.sin(age * 6 + b.sway * 3));
          ctx.fillStyle = b.color;
          ctx.beginPath(); ctx.arc(b.x, b.y, b.w, 0, Math.PI * 2); ctx.fill();
          ctx.globalAlpha = 1;
        }
        if (b.y > H() + 40 * d || b.x < -80 * d || b.x > W() + 80 * d) bits.splice(i, 1);
      }
      if (kind === 'confetti' && age > E.confettiSeconds && !bits.length) { stop(); return; }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    let stopped = false;
    const stop = () => {
      if (stopped) return;
      stopped = true;
      cancelAnimationFrame(raf);
      removeEventListener('resize', fit);
      cv.remove();
    };
    return stop;
  }

  // ---------------- watching a replay ----------------
  const replayBar = el('section', { class: 'replay-bar', 'aria-label': 'Replay controls', hidden: !watching });
  if (replay) {
    const file = replay.file;
    const playBtn = button('Play', () => (replay.status.playing ? replay.pause() : replay.play()), 'btn-primary replay-play');
    const stepBtn = button('Next', () => replay.step(), '', { title: 'Play just the next action (→)' });
    const backBtn = button('Start', () => replay.seek(0), 'btn-ghost', { title: 'Back to the start of the match' });
    const speed = el('select', { class: 'replay-speed', 'aria-label': 'Playback speed' }, [0.5, 1, 2, 4, 8].map((x) => el('option', { value: String(x), selected: x === (opts.replay?.speed ?? 1) }, `${x}×`)));
    speed.addEventListener('change', () => replay.setSpeed(Number(speed.value)));
    const scrub = el('input', { class: 'replay-scrub', type: 'range', min: '0', max: String(file.actions.length), step: '1', value: '0', 'aria-label': 'Position in the match' });
    let pending: number | null = null;
    scrub.addEventListener('input', () => {
      // dragging the slider asks for many positions in a row: take the latest once per frame
      const first = pending === null;
      pending = Number(scrub.value);
      if (first) requestAnimationFrame(() => { const to = pending!; pending = null; replay.seek(to); });
    });
    const where = el('span', { class: 'replay-where' });
    const seats =
      file.names.length > 1
        ? el('span', { class: 'replay-seats' }, el('span', { class: 'muted' }, 'Watch as'), file.names.map((n, i) => button(n, () => i !== conn.seat && switchTo({ ...opts, replay: { file, seat: i as Seat, at: replay.status.index, playing: replay.status.playing, speed: replay.status.speed } }), i === conn.seat ? 'btn-small is-on' : 'btn-small btn-ghost')))
        : null;
    replayBar.append(backBtn, playBtn, stepBtn, speed, scrub, where, ...(seats ? [seats] : []));
    replay.onStatus((st: ReplayStatus, jump) => {
      if (jump) {
        jumped = true;
        table.forgetEffects();
      }
      playBtn.textContent = st.playing ? 'Pause' : st.index >= st.total ? 'Replay' : 'Play';
      stepBtn.disabled = st.index >= st.total;
      scrub.max = String(st.total);
      if (pending === null) scrub.value = String(st.index);
      where.textContent = `Action ${st.index} of ${st.total} · ${clock(st.time)} / ${clock(st.length)}`;
      const res = file.result;
      replayNote =
        st.brokeAt !== null
          ? `This replay stops at action ${st.brokeAt}: it was recorded with a different card list or rules than the ones loaded now.`
          : st.index >= st.total
            ? file.finished && res
              ? `End of the replay. ${res.winner === null ? (file.names.length > 1 ? 'The match was a draw.' : 'The game ended here.') : `${file.names[res.winner]} won.`}`
              : 'End of the recording: this game was saved part-way.'
            : file.fingerprint && file.fingerprint !== DATA_FINGERPRINT
              ? 'Recorded with a different card list or rules than the ones loaded now, so it may not play back exactly.'
              : '';
    });
    if (opts.replay?.speed) replay.setSpeed(opts.replay.speed);
  }

  // ---------------- watching a game live ----------------
  const watchCount = el('span', { class: 'replay-where' });
  if (spectating && online) {
    const live = el('span', { class: 'replay-where' }, el('strong', {}, 'Watching live'), ` · ${online.names.join(' vs ')} · code ${online.code}`);
    const sides = el(
      'span',
      { class: 'replay-seats' },
      el('span', { class: 'muted' }, 'Look from'),
      online.names.map((n, i) =>
        button(n, () => {
          if (i === conn.seat) return;
          switchTo({ ...opts, online: new OnlineConnection({ watch: { code: online.code, side: i as Seat, names: online.names } }) });
        }, i === conn.seat ? 'btn-small is-on' : 'btn-small btn-ghost'),
      ),
    );
    replayBar.append(live, sides, watchCount);
  }

  /** Leaves this table and opens another one in its place (a replay, or the same replay from the other seat). */
  function switchTo(next: TableOptions) {
    teardown();
    startTable(next);
  }

  /** Blue for whoever goes first, red for the other player. */
  const seatColor = (v: GameView, seat: Seat) => (seat % 2 === v.first % 2 ? CONFIG.playerColors.first : CONFIG.playerColors.second);

  function renderTurn(v: GameView) {
    const mine = v.turn === v.you;
    // the HUD picks up your color, your opponent's, and the color of whoever's turn it is
    overlay.style.setProperty('--me', seatColor(v, v.you));
    overlay.style.setProperty('--them', seatColor(v, foeOf(v)));
    overlay.style.setProperty('--turn', seatColor(v, v.turn));
    const turnName = v.players[v.turn]?.name ?? '';
    whose.textContent = mine && !watching ? 'Your turn' : `${turnName}’s turn`;
    turnBar.classList.toggle('is-mine', mine);
    turnBar.classList.toggle('is-theirs', !mine);
    [...phases.children].forEach((li, i) => {
      li.classList.toggle('current', i === v.phase);
      li.classList.toggle('done', i < v.phase);
    });
    const me = v.players[v.you];
    const parts = [`${watching ? `${me.name}’s` : 'Your'} turns ${me.turns}`];
    v.players.forEach((p, i) => i !== v.you && parts.push(`${p.name} ${p.turns}`));
    parts.push(`Global turn ${v.globalTurn}`);
    const soon = v.teams ? CONFIG.teams.overtimeAfter : CONFIG.overtime.afterTurns; // 1v1, bot games and the solo table: 12
    if (soon > 0 && !v.overtime && v.globalTurn >= soon - 5) parts.push(`Overtime at ${soon}`);
    counts.textContent = parts.join(' · ');
    nextBtn.hidden = !mine || watching || !!v.result;
    nextBtn.textContent = v.phase === PHASES.length - 1 ? 'End Turn' : 'Next Phase';
    // in your End Phase: the Fractals your idle units will earn when you end the turn. Nothing is counted
    // before that, so a card tapped by mistake can still be turned back.
    const offer = v.fractalOffer ?? 0;
    fractalNote.hidden = !(mine && offer > 0) || watching;
    fractalNote.textContent = `+${offer} Fractal${offer === 1 ? '' : 's'} on End Turn`;
    fractalNote.title = 'One for each of your face-up units that is not tapped, not your Minion and free of negative statuses, counted at the moment you end your turn.';
  }

  // ---------------- clicking your deck ----------------
  /**
   * The top card of one of your piles goes straight to the other pile, never through your hand (some card
   * effects do this). Asks where in the pile it lands, like putting any card into a pile.
   */
  async function pileToPile(from: PileName, x: number, y: number) {
    const where = await askWhere(from === 'deck' ? 'discard' : 'deck', x, y);
    if (where) conn.send({ type: 'pileMove', from, where });
  }

  /**
   * Right-click on your deck: where does its top card go? To your hand (a draw: past 7 cards it asks which
   * kind) or straight to your discard pile. With an empty deck it offers Deck-Out.
   */
  function deckClick(x: number, y: number) {
    const v = view;
    if (!v) return;
    const me = v.players[v.you];
    const T = CONFIG.turn;
    const toDiscard: MenuItem = { label: 'Discard pile', onClick: () => void pileToPile('deck', x, y) };
    // in a game the table draws for you: the one draw you make yourself is an Overdraw, in your Overdraw Phase
    if (handsOff()) {
      const myOverdraw = v.turn === v.you && v.phase === 0;
      const canPay = me.gerzite >= T.overdrawCost;
      const full = v.hand.length >= CONFIG.handLimit;
      const why = me.deckCount === 0 ? 'Your deck is empty.' : full ? `Your hand is full (${CONFIG.handLimit} cards).` : !myOverdraw ? 'Overdraw is only in your own Overdraw Phase.' : !canPay ? 'You have no Gerzite to pay for an Overdraw.' : `In your Overdraw Phase, each card you Overdraw costs ${T.overdrawCost} Gerzite (up to ${CONFIG.handLimit} in hand).`;
      floatingMenu(x, y, `Your deck · ${me.deckCount} card${me.deckCount === 1 ? '' : 's'}`, [
        el('div', { class: 'float-note' }, `${why} Cards that draw or search do it for you.`),
        ...(T.overdrawCost > 0 && me.deckCount > 0 ? [{ label: `Overdraw · pay ${T.overdrawCost} Gerzite`, disabled: !myOverdraw || !canPay || full, onClick: () => conn.send({ type: 'draw', pay: true }) }] : []),
      ]);
      return;
    }
    if (me.deckCount === 0) {
      floatingMenu(x, y, 'Your deck is empty', [
        el('div', { class: 'float-note' }, 'Deck-Out from the end-of-turn draw is dealt automatically when you end your turn. This button is only for a card effect that forces a draw you can’t make. (An Overdraw never causes Deck-Out.)'),
        { label: `A card effect made me draw: ${T.deckOutDamage || 1} damage to my Emperor`, danger: true, onClick: () => conn.send({ type: 'deckOut' }) },
      ]);
      return;
    }
    const O = T.effectDrawOverflow;
    if (v.hand.length >= CONFIG.handLimit && O.enabled) {
      // a full hand can't draw by choice, but a card effect can still force a draw
      floatingMenu(x, y, `Your hand is full (${v.hand.length} cards)`, [
        el('div', { class: 'float-note' }, `You can’t Overdraw past ${CONFIG.handLimit} cards. If a card’s effect makes you draw anyway, the card can’t stay in your hand: you choose to discard it for ${O.discardGerzite} Gerzite, or put it on the bottom of your deck for ${O.bottomFractals} Fractal${O.bottomFractals === 1 ? '' : 's'}.`),
        { label: 'Effect Draw with a full hand', onClick: () => conn.send({ type: 'draw', overflow: true }) },
        toDiscard,
      ]);
      return;
    }
    if (T.overdrawCost <= 0 || v.hand.length >= CONFIG.handLimit) {
      floatingMenu(x, y, 'The top card of your deck goes to…', [{ label: 'Hand', onClick: () => conn.send({ type: 'draw' }) }, toDiscard]);
      return;
    }
    // An Overdraw works at any hand size (it is also the one way past the passive draw's 7): each one costs Gerzite
    const myOverdraw = v.turn === v.you && v.phase === 0;
    const canPay = me.gerzite >= T.overdrawCost;
    const why = !myOverdraw ? 'Overdraw is only in your own Overdraw Phase.' : !canPay ? 'You have no Gerzite to pay for an Overdraw.' : `In your Overdraw Phase, each card you Overdraw costs ${T.overdrawCost} Gerzite (up to ${CONFIG.handLimit} in hand).`;
    floatingMenu(x, y, `You have ${v.hand.length} cards`, [
      el('div', { class: 'float-note' }, `${why} A draw from a card’s effect is free.`),
      { label: `Overdraw · pay ${T.overdrawCost} Gerzite`, disabled: !myOverdraw || !canPay, onClick: () => conn.send({ type: 'draw', pay: true }) },
      { label: 'Effect Draw · free', onClick: () => conn.send({ type: 'draw' }) },
      toDiscard,
    ]);
  }

  /** Right-click on your discard pile: its top card goes back to your hand, or straight to your deck. */
  function discardClick(x: number, y: number) {
    const v = view;
    if (!v) return;
    const top = v.players[v.you].discard[0];
    if (!top) {
      toast('Your discard pile is empty.', 'info');
      return;
    }
    if (revealClick(top.id)) return; // R + click: reveal the top card
    const name = CARD_DB.byNumber.get(top.num)?.name ?? 'the top card';
    if (handsOff()) {
      floatingMenu(x, y, `Your discard pile · ${v.players[v.you].discard.length} cards · ${name} on top`, [el('div', { class: 'float-note' }, 'Cards come back from your discard pile only through a card that brings them back. In your Overdraw Phase you may drag cards from your hand onto it to make room.')]);
      return;
    }
    floatingMenu(x, y, `${name}, on top of your discard pile, goes to…`, [
      { label: 'Hand', disabled: v.hand.length >= CONFIG.handLimit, onClick: () => conn.send({ type: 'takeDiscard' }) },
      { label: 'Deck', onClick: () => void pileToPile('discard', x, y) },
    ]);
  }

  /** A card in play keeps both hands in plain view: your opponent's hand, large enough to read. */
  function showOpenHand(seat?: Seat) {
    const v = view;
    if (!v) return;
    // whose: the one asked for (2v2: your teammate), or an opponent whose hand a card keeps in plain view
    const who = seat ?? (v.players.findIndex((p, i) => i !== v.you && !allyOf(v, i as Seat) && !!p.openHand) as Seat);
    const opp = who >= 0 ? v.players[who] : undefined;
    if (!opp?.openHand) return;
    const mate = v.teams && allyOf(v, who);
    const cards = opp.openHand;
    let close = () => {};
    close = modal(
      el(
        'div',
        { class: 'stack' },
        el('h2', {}, `${opp?.name ?? 'Opponent'}’s hand · ${cards.length} card${cards.length === 1 ? '' : 's'}`),
        el('p', { class: 'muted' }, mate ? `Your teammate’s hand: teammates can always see each other’s. Click a card to see it up close.` : `${v.handsOpen} keeps both hands in plain view while it is in play. They can see yours the same way. Click a card to see it up close.`),
        el(
          'div',
          { class: 'card-grid card-grid-small search-grid' },
          cards.map((c) => {
            const def = CARD_DB.byNumber.get(c.num);
            const t = el(
              'button',
              { class: 'card-tile', type: 'button', title: 'Click to enlarge' },
              setImg(el('img', { class: 'card-thumb', alt: def?.name ?? 'Card', width: '500', height: '700' }), cardFrontSrc(c.num)),
              el('span', { class: 'tile-name' }, def?.name ?? `#${c.num}`),
            );
            const look = () => enlargeHeld(c, `In ${opp?.name ?? 'your opponent'}’s hand`);
            t.addEventListener('click', look);
            t.addEventListener('contextmenu', (e) => {
              e.preventDefault();
              look();
            });
            return t;
          }),
        ),
        cards.length ? null : el('p', { class: 'empty-note' }, 'Their hand is empty.'),
        el('div', { class: 'row-end' }, button('Done', () => close(), 'btn-primary')),
      ),
      { wide: cards.length > 3 },
    );
  }

  // ---------------- toolbar (top-right) ----------------
  const spawnBtn = button('Counters/Summon', () => toggleSpawn(), '', { 'aria-pressed': 'false', title: 'Bring in counters and tokens, or summon a card' });
  const gameBtn = button('Game Menu', () => openGameMenu(), 'btn-ghost', { title: 'Call it here, forfeit, change the background, or leave the table' });
  // an online game's code, so players can tell friends who want to watch it (Join with a Code)
  const codeBtn = button('', () => {
    if (!online?.code) return;
    void copyText(online.code);
  }, 'btn-ghost game-code', { hidden: true, title: 'This game’s code: anyone who enters it in Join with a Code watches the game. Click to copy it' });
  const showCode = () => {
    const code = online && !spectating ? online.code : '';
    codeBtn.hidden = !code;
    if (code) codeBtn.textContent = `Code ${code}`;
  };
  showCode();
  // one button for the camera: Top-Down while you're at your seat, Seat View while you look from above (T and R too)
  let fromAbove = false;
  const viewBtn = button('Top-Down', () => (fromAbove ? seatView() : topView()), '');
  function topView() {
    table.topDown();
    fromAbove = true;
    viewBtn.textContent = 'Seat View';
    viewBtn.title = 'Back to your seat (R)';
  }
  function seatView() {
    table.resetCamera();
    fromAbove = false;
    viewBtn.textContent = 'Top-Down';
    viewBtn.title = 'View the whole table from above (T)';
  }
  viewBtn.title = 'View the whole table from above (T)';
  // three rows: the game's code and the camera · Controls and Rules · the Game Menu and the Battle Timer
  // on a small screen the toolbar folds into one Menu button (the Battle Timer stays in sight)
  const toolbarToggle = button('☰ Menu', () => {
    const open = toolbar.classList.toggle('open');
    toolbarToggle.setAttribute('aria-expanded', String(open));
  }, 'toolbar-toggle', { 'aria-expanded': 'false', title: 'Camera, controls, rules and the Game Menu' });
  const toolbar = el(
    'nav',
    { class: 'toolbar' },
    el('div', { class: 'toolbar-row toolbar-row-compact' }, toolbarToggle),
    el('div', { class: 'toolbar-row' }, codeBtn, viewBtn),
    el('div', { class: 'toolbar-row' }, watching ? null : spawnBtn, button('Controls', () => showControls(), '', { title: 'Mouse and keyboard controls (?)' }), hasRules ? button('Rules', () => showRules(), '', { title: 'The rules of the game: turns, status effects, how to play' }) : null),
    el('div', { class: 'toolbar-row' }, watching ? button(spectating ? 'Stop Watching' : 'Close Replay', () => leave(), 'btn-ghost') : gameBtn, timerChip),
  );

  toolbar.addEventListener('click', (e) => {
    // (small screens) picking something from the folded toolbar folds it away again
    const b = (e.target as HTMLElement).closest('.btn');
    if (b && b !== toolbarToggle && !b.classList.contains('battle-timer')) {
      toolbar.classList.remove('open');
      toolbarToggle.setAttribute('aria-expanded', 'false');
    }
  });

  // ---------------- hand (bottom) ----------------
  const handCards = el('div', { class: 'hand-cards' });
  const handCount = el('span', { class: 'hand-count' });
  // Darkgale summon: press it, then play a card. That card is paid for with Dark Gerzite and gets the Darkgale token.
  let darkArmed = false;
  let darkCard: string | null = null; // the card just dropped as a Darkgale summon: once it has left the hand, the button switches off
  const darkBtn = button('Darkgale summon', () => setDark(!darkArmed), 'btn-small dark-toggle', {
    'aria-pressed': 'false',
    title: 'Press, then play a card: it is paid for with Dark Gerzite instead of Gerzite (Spells cost double) and gets the Darkgale token',
  });
  darkBtn.hidden = watching || !darkgaleOn;
  function setDark(on: boolean) {
    darkArmed = on;
    if (!on) darkCard = null;
    darkBtn.setAttribute('aria-pressed', String(on));
    handBar.classList.toggle('darkgale', on);
    if (view) renderDark(view);
  }
  function renderDark(v: GameView) {
    if (darkCard && !v.hand.some((c) => c.id === darkCard)) {
      darkArmed = false;
      darkCard = null;
      darkBtn.setAttribute('aria-pressed', 'false');
      handBar.classList.remove('darkgale');
    }
    darkBtn.textContent = darkArmed ? 'Darkgale summon is ON · click to cancel' : 'Darkgale summon';
  }
  const openChip = el('span', { class: 'hand-open', hidden: true }); // shown while a card keeps both hands in plain view
  // 2v2: your teammate's hand, a click away
  const mateBtn = button('', () => view && mateOf(view) !== null && showOpenHand(mateOf(view)!), 'btn-small btn-ghost hand-mate', { hidden: true, title: 'Look at your teammate’s hand' });
  const handBar = el(
    'section',
    { class: `hand ${watching ? 'is-watching' : ''}`, 'aria-label': 'Your hand' },
    el('div', { class: 'hand-head' }, el('span', { class: 'hand-title' }, spectating ? `Watching from ${online!.names[conn.seat] ?? 'Player'}’s side` : watching ? `${replay!.file.names[conn.seat] ?? 'Player'}’s hand` : 'Your hand'), handCount, openChip, mateBtn, darkBtn),
    handCards,
  );
  let handIds: string | null = null;
  function renderHand(v: GameView) {
    renderDark(v);
    // watching live: how many cards each player holds (the cards themselves stay hidden)
    handCount.textContent = spectating ? v.players.map((p) => `${p.name}: ${p.handCount} in hand`).join(' · ') : `${v.hand.length}/${CONFIG.handLimit}`;
    handCount.classList.toggle('full', !spectating && v.hand.length >= CONFIG.handLimit);
    if (selectedCard && !v.hand.some((c) => c.id === selectedCard)) selectCard(null); // it was played, or left the hand some other way
    const ids = v.hand.map((c) => c.id).join(',');
    const greyOut = () => {
      // a card a card effect stops you playing right now (Glacial Creep once you have played something): greyed out
      for (const n of handCards.querySelectorAll<HTMLElement>('.hand-card')) {
        const why = v.handBlocked?.[n.dataset.id ?? ''];
        n.classList.toggle('blocked', !!why);
        if (why) n.title = why; else n.removeAttribute('title');
        // a free summon held from Okrum's Bellowing Horn: the cards it can bring in this turn glow
        n.classList.toggle('is-horn', !why && !!v.hornCards?.includes(n.dataset.id ?? ''));
      }
    };
    if (ids === handIds) { greyOut(); return; }
    handIds = ids;
    handCards.replaceChildren(...v.hand.map((c) => handCard(c)));
    greyOut();
    handCards.style.setProperty('--n', String(Math.max(1, v.hand.length)));
    if (!v.hand.length) handCards.append(el('p', { class: 'hand-empty' }, spectating ? 'The players’ hands stay hidden while you watch.' : watching ? 'No cards in hand.' : 'Right-click your deck to draw.'));
    paintSelected();
  }
  /** The Unit a card like Tribunal Trigger paid for wears its own highlight in your hand: it will be played face-down, for free. */
  /** While a card keeps both hands in plain view, your hand bar says so: your opponent can read every card in it. */
  function renderOpenHands(v: GameView) {
    const mate = mateOf(v);
    const mp = mate !== null ? v.players[mate] : undefined;
    mateBtn.hidden = !mp?.openHand || spectating;
    if (mp) mateBtn.textContent = `${mp.name}’s hand · ${mp.handCount}${mp.out ? ' · out' : ''}`;
    openChip.hidden = !v.handsOpen;
    openChip.textContent = v.handsOpen ? (v.mode === 'solo' ? `In plain view · ${v.handsOpen}` : `Your opponent can see this hand · ${v.handsOpen}`) : '';
  }
  function renderPledged(v: GameView) {
    for (const n of handCards.querySelectorAll<HTMLElement>('.hand-card')) {
      const on = !!v.freeUnit && n.dataset.id === v.freeUnit;
      n.classList.toggle('pledged', on);
      n.dataset.tag = on ? 'Free · face-down' : '';
    }
    paintSelected();
  }

  // A left-click on a hand card selects it: it stands up out of the hand with a bright edge, and the
  // next click on the table places it there (the same as dragging it to that spot).
  // While Ctrl / ⌘ is held, the selected card wears the light blue of Gerzite instead of gold: placed now, it goes
  // down free. Let go of the key and it is gold again. (The Unit another card paid for is always light blue.)
  let selectedCard: string | null = null;
  let freeKey = false; // Ctrl / ⌘ is down right now
  function selectCard(id: string | null) {
    selectedCard = id;
    paintSelected();
    handBar.classList.toggle('has-selected', !!id);
    table.setPlacing(!!id, id ?? undefined);
  }
  function paintSelected() {
    for (const n of handCards.querySelectorAll<HTMLElement>('.hand-card')) {
      const on = n.dataset.id === selectedCard;
      n.classList.toggle('selected', on);
      n.classList.toggle('free', on && (freeKey || view?.freeUnit === selectedCard));
    }
  }
  const onFreeKey = (e: KeyboardEvent) => {
    const down = e.ctrlKey || e.metaKey;
    if (down === freeKey) return;
    freeKey = down;
    paintSelected();
  };
  const onBlur = () => {
    freeKey = false;
    paintSelected();
  };
  window.addEventListener('keydown', onFreeKey);
  window.addEventListener('keyup', onFreeKey);
  window.addEventListener('blur', onBlur);
  // R held down + a click on one of your own cards (in your hand, in a pile you are searching, or on top of your
  // discard pile) reveals it to your opponent. R tapped by itself still resets the camera, as it is let go.
  let revealKey = false;
  let revealUsed = false;
  const typing = () => document.activeElement instanceof HTMLInputElement || document.activeElement instanceof HTMLTextAreaElement;
  const onRevealKey = (e: KeyboardEvent) => {
    if (e.code !== 'KeyR' || e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.type === 'keydown') {
      if (!e.repeat && !typing()) { revealKey = true; revealUsed = false; }
      return;
    }
    if (revealKey && !revealUsed && !typing() && !document.querySelector('.backdrop')) seatView();
    revealKey = false;
  };
  const onRevealBlur = () => { revealKey = false; };
  window.addEventListener('keydown', onRevealKey);
  window.addEventListener('keyup', onRevealKey);
  window.addEventListener('blur', onRevealBlur);
  /** R is held: this click reveals the card instead of doing what a click usually does. True if it did. */
  function revealClick(id: string | undefined) {
    if (!revealKey || watching || !id) return false;
    revealUsed = true;
    conn.send({ type: 'revealCard', id });
    return true;
  }

  // Resting the cursor on a card in your hand shows its name just above the cursor, as it does on the table.
  let handTipEl: HTMLElement | null = null;
  function handTip(text: string | null, x = 0, y = 0) {
    if (!text || !CONFIG.hoverNames) {
      handTipEl?.remove();
      handTipEl = null;
      return;
    }
    if (!handTipEl) {
      handTipEl = el('div', { class: 'hover-tip' });
      document.body.append(handTipEl);
    }
    if (handTipEl.textContent !== text) handTipEl.textContent = text;
    const left = Math.max(8, Math.min(x + 14, window.innerWidth - handTipEl.offsetWidth - 8));
    const top = y - 34 < 8 ? y + 22 : y - 34;
    handTipEl.style.transform = `translate(${left}px, ${top}px)`;
  }
  function placeSelected(x: number, y: number, shift: boolean, free: boolean) {
    const id = selectedCard;
    if (!id) return;
    table.endHandDrag();
    if (darkArmed) darkCard = id;
    // the card stays selected if the table refuses the spot, so another one can be tried right away
    void table.dropFromHand(id, x, y, shift, free, darkArmed);
  }

  function handCard(c: HeldCard) {
    const def = CARD_DB.byNumber.get(c.num);
    const img = setImg(el('img', { class: 'hand-img', alt: def?.name ?? 'Card', draggable: 'false' }), cardFrontSrc(c.num));
    const card = el('div', { class: 'hand-card', tabindex: '0', 'data-id': c.id, 'aria-label': def?.name ?? 'Card' }, img);
    const name = def?.name ?? 'Card';
    let dragging = false;
    const tip = (e: PointerEvent) => handTip(dragging || handCards.classList.contains('reordering') ? null : name, e.clientX, e.clientY);
    card.addEventListener('pointerenter', tip);
    card.addEventListener('pointermove', tip);
    card.addEventListener('pointerleave', () => handTip(null));
    card.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      handTip(null);
      enlargeHeld(c, 'In your hand');
    });
    card.addEventListener('keydown', (e) => e.key === 'Enter' && enlargeHeld(c, 'In your hand'));
    if (watching) {
      // a replay: cards in the hand can be looked at, not played
      card.addEventListener('click', () => {
        handTip(null);
        enlargeHeld(c, 'In the hand');
      });
      return card;
    }
    card.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      e.preventDefault();
      if (revealClick(c.id)) return; // R + click: reveal it
      const startX = e.clientX;
      const startY = e.clientY;
      let ghost: HTMLElement | null = null;
      const move = (ev: PointerEvent) => {
        if (!ghost && Math.hypot(ev.clientX - startX, ev.clientY - startY) > 6) {
          ghost = el('img', { class: 'drag-ghost', src: img.src, alt: '' });
          document.body.append(ghost);
          card.classList.add('lifted');
          handCards.classList.add('reordering'); // the other cards stay put (no lift, no name) while one is dragged
          dragging = true;
          handTip(null);
        }
        if (ghost) {
          ghost.style.transform = `translate(${ev.clientX}px, ${ev.clientY}px) rotate(-4deg)`;
          ghost.classList.toggle('face-down', ev.shiftKey || arrivesFaceDown(def) || view?.freeUnit === c.id);
          const overHand = overElement(handBar, ev.clientX, ev.clientY);
          if (overHand) table.endHandDrag();
          else table.hoverFromHand(ev.clientX, ev.clientY, c.id, ev.shiftKey, ev.ctrlKey || ev.metaKey, darkArmed);
          markHandSpot(overHand ? handSpot(c.id, ev.clientX) : null);
        }
      };
      const up = (ev: PointerEvent) => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        window.removeEventListener('pointercancel', up);
        card.classList.remove('lifted');
        handCards.classList.remove('reordering');
        dragging = false;
        table.endHandDrag();
        if (ev.type === 'pointercancel') {
          // the finger was taken over (a long-press opens the close-up instead, or the browser scrolled): nothing is played
          ghost?.remove();
          markHandSpot(null);
          return;
        }
        if (!ghost) {
          // a click selects it; a second click lets go. (Free play follows the Ctrl / ⌘ key itself: the card is
          // light blue while the key is down and gold again the moment it is let go.)
          selectCard(selectedCard === c.id ? null : c.id);
          return;
        }
        ghost.remove();
        markHandSpot(null);
        if (selectedCard) selectCard(null); // a drag is its own way of playing: nothing stays selected
        if (overElement(handBar, ev.clientX, ev.clientY)) {
          // let go over the hand: the card moves to that place in it
          const spot = handSpot(c.id, ev.clientX);
          if (spot && spot.to !== spot.from) conn.send({ type: 'handMove', id: c.id, to: spot.to });
          return;
        }
        if (darkArmed) darkCard = c.id;
        void table.dropFromHand(c.id, ev.clientX, ev.clientY, ev.shiftKey, ev.ctrlKey || ev.metaKey, darkArmed); // Ctrl / ⌘ = play it free
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
      window.addEventListener('pointercancel', up);
    });
    return card;
  }

  /**
   * Where a card dragged along your hand would go: the place among the other cards nearest the cursor
   * (`to`, in the hand as it will be), and the card it would sit before (null = at the right end).
   */
  function handSpot(id: string, x: number): { from: number; to: number; before: HTMLElement | null } | null {
    const all = [...handCards.querySelectorAll<HTMLElement>('.hand-card')];
    const from = all.findIndex((n) => n.dataset.id === id);
    if (from < 0) return null;
    const others = all.filter((n) => n.dataset.id !== id);
    let to = others.findIndex((n) => {
      const r = n.getBoundingClientRect();
      return x < r.left + r.width / 2;
    });
    if (to < 0) to = others.length;
    return { from, to, before: others[to] ?? null };
  }
  /** While a card is dragged along your hand, a bright line shows where it will go. */
  function markHandSpot(spot: { from: number; to: number; before: HTMLElement | null } | null) {
    for (const n of handCards.querySelectorAll<HTMLElement>('.hand-card')) n.classList.remove('drop-before', 'drop-end');
    if (!spot || spot.to === spot.from) return;
    if (spot.before) spot.before.classList.add('drop-before');
    else handCards.querySelector<HTMLElement>('.hand-card:last-child')?.classList.add('drop-end');
  }

  function enlargeHeld(c: HeldCard, note: string) {
    const def = CARD_DB.byNumber.get(c.num);
    showCardLarge(cardFrontSrc(c.num), def ? cardLabel(def) : 'Card', note, [], { rules: cardRulesNode(def) });
  }

  // ---------------- right-click: cards, tokens, piles ----------------
  function openCardView(c: CardView, expired = false) {
    const v = view;
    if (!v) return;
    const mine = (c.spawned && !c.swarm) || allyOf(v, c.owner); // 2v2: your teammate's cards are yours to handle too
    const def = c.num !== null ? CARD_DB.byNumber.get(c.num) : undefined;
    const peek = !c.faceUp && c.num !== null;
    const src = c.faceUp || peek ? cardFrontSrc(c.num!, false, c.faceUp ? (c.form ?? 0) : 0) : cardBackSrc();
    const caption = c.faceUp || peek ? (def ? cardLabel(def) : 'Card') : 'Face-down card';
    const owner = c.owner === v.you ? 'Yours' : `${v.players[c.owner]?.name ?? 'Opponent'}’s`;
    const known = c.faceUp || peek ? def : undefined;
    // What can be read off the card itself (its tier, ground or flying, which way it faces) is left out:
    // the close-up is for what changes during the game.
    const note = [
      peek ? 'Face-down — only you can see this side.' : '',
      `${owner}${c.spanOf ? ' · extra Lanespanner unit (cleared away, not discarded)' : c.swarm ? ' · one of a swarm' : c.spawned ? ' · spawned' : ''} · ${stateOf(c, known)}`,
      known?.discardAura ? `Scales with its owner’s discard pile (${v.players[c.owner]?.discard.length ?? 0} cards).` : '',
      known?.costDark ? `Costs ${known.cost ?? 0} Dark Gerzite (summoned with Dark Gerzite, so it can’t be a Darkgale summon)` : '',
      known?.forms ? (c.faceUp ? `Summoned for ${formOf(known, c.form).cost} Gerzite as ${formWords(formOf(known, c.form))} (it can also come in as ${known.forms.filter((_, k) => k !== (c.form ?? 0)).map((f) => `${formWords(f)} for ${f.cost}`).join(' or ')})` : `Cost: ${costWords(known)}`) : '',
      known?.costFormula ? `Cost ${costWords(known)}` : '',
      known?.turns ? `Lasts ${known.turns} of its owner’s turns (its timer counts them down ${known.turnsTimer === 'start' ? 'as each of those turns begins' : 'as each of those turns ends'})` : '',
      c.faceUp && known && c.tier >= 3 && tieredDamage(known, c.tier) !== known.damage ? `Tier ${roman(c.tier)}: deals ${tieredDamage(known, c.tier)} damage (the card says ${known.damage})` : '',
      known?.removalCost ? `Removal cost: ${tieredRemoval(known, c.tier)} ${known.removalDark ? 'Dark Gerzite' : 'Gerzite'}, for either player to take it out of play early` : '',
      known?.replayCost ? `Replay cost: ${known.replayCost} ${known.replayDark ? 'Dark Gerzite' : 'Gerzite'}, for its owner to restart it once its turns are over (each restart lasts 1 turn less)` : '',
      c.restarts ? `Restarted ${c.restarts} time${c.restarts === 1 ? '' : 's'}` : '',
      c.clone ? `A clone, made by ${c.clone.by}${c.clone.health !== undefined ? ` (${c.clone.health} health)` : ''}` : '',
      c.attachedTo ? `Attached to ${cardNameOf(c.attachedTo)}: it follows that card (drag it off to detach it)` : '',
      // (its abilities and description are printed in full just above, from the card sheet)
      known?.abilities.length && !known.text ? `Abilities: ${known.abilities.map(titleCase).join(', ')}` : '',
      healthNote(c, known),
    ]
      .filter(Boolean)
      .join(' · ');
    const actions: MenuItem[] = [];
    const extra = { ...effectsOn(c), rules: cardRulesNode(known) };
    if (watching) {
      showCardLarge(src, caption, note, [], extra);
      return;
    }
    if (mine && !handsOff()) actions.push({ label: c.faceUp ? 'Flip face-down' : 'Flip face-up', onClick: () => conn.send({ type: 'flip', id: c.id }) });
    if (mine && c.tier > 1) actions.push({ label: 'Separate one copy', onClick: () => conn.send({ type: 'split', id: c.id }) });
    if (allyOf(v, c.owner) && c.faceUp && known?.types.includes('trigger')) {
      const pays = triggerPayout(known);
      actions.push({ label: `Pay out ${pays} Gerzite and discard`, title: `A sprung Trigger pays its cost + ${CONFIG.trigger.payoutBonus}`, onClick: () => conn.send({ type: 'payout', id: c.id }) });
    }
    const worth = allyOf(v, c.owner) ? sacrifice(c, known) : null; // 2v2: a teammate's unit can be sacrificed too
    // a sacrifice is made in your own Summon or Attack Phase
    const tooSoon = worth ? sacrificeWindowRefusal(v.turn, v.phase, v.you) : null;
    const phases = CONFIG.turn.sacrifice.phases.map((i) => PHASES[i].replace(' Phase', '')).join(' or ');
    if (worth && !worth.darkgale) {
      actions.push({
        label: worth.entrenched ? 'Sacrifice (not while Entrenched)' : tooSoon ? `Sacrifice (in your ${phases || 'own'} ${phases ? 'Phase' : 'turn'})` : `Sacrifice → ${worth.gives} Dark Gerzite`,
        danger: true,
        disabled: worth.entrenched || !!tooSoon,
        title: worth.entrenched ? 'A unit that is still Entrenched can’t be sacrificed' : (tooSoon ?? `Discards it and gives you ${worth.gives} Dark Gerzite`),
        onClick: () => conn.send({ type: 'sacrifice', id: c.id }),
      });
    }
    // your Emperor: its abilities can be used from here too
    if (c.owner === v.you && c.faceUp && known && CONFIG.emperorAbilities.enabled) {
      for (const ab of known.emperorAbilities) {
        const st = abilityState(c, ab);
        actions.push({ label: `Ability ${ab.slot}: ${ab.name} · ${ab.costVaries ? `? ${st.coin}` : ab.cost > 0 ? `${ab.cost} ${st.coin}` : 'free'}`, disabled: !st.ok, title: st.ok ? ab.text : st.state, onClick: () => conn.send({ type: 'emperorAbility', id: c.id, slot: ab.slot }) });
      }
    }
    // a Biome: either player may pay to take it out of play. A Tome of yours whose turns are over: restart or discard
    const removal = c.faceUp && known ? removalOf(known, expired, c.tier) : null;
    if (removal) actions.push({ label: removal.label, danger: true, disabled: !removal.canPay, title: removal.why, onClick: () => conn.send({ type: 'removeCard', id: c.id }) });
    const again = c.owner === v.you && c.faceUp && expired && known ? restartOf(c, known) : null;
    if (again) {
      actions.push({ label: again.label, disabled: !again.can, title: again.why, onClick: () => conn.send({ type: 'restart', id: c.id }) });
      actions.push({ label: 'Discard it', danger: true, onClick: () => conn.send({ type: 'toPile', id: c.id, pile: 'discard', where: 'top' }) });
    }
    // a Unit can be cloned by the cards that make clones (From the Mirror Realm, Spectral Clone, Illusionist)
    if (c.faceUp && known && (known.types.includes('troop') || known.types.includes('building')) && !handsOff()) {
      for (const src of CONFIG.cloneSources) {
        actions.push({ label: `Clone · ${src.name}`, title: `A light blue clone in the same lane${src.health !== undefined ? `, with ${src.health} health` : ''}${src.statuses ? ', with its status effects' : ''}${src.entrenched ? ', Entrenched' : ''}`, onClick: () => conn.send({ type: 'clone', id: c.id, by: src.name }) });
      }
    }
    if (c.spawned && !c.swarm && (v.mode === 'solo' || CONFIG.deleteInGames)) actions.push({ label: 'Delete', danger: true, onClick: () => conn.send({ type: 'delete', id: c.id }) });
    showCardLarge(src, caption, note, actions, extra);
  }

  /**
   * Everything that is on a card right now, for its close-up: one small colored tag per token (statuses first,
   * then damage, then the rest), and a plain sentence for the effects that change its numbers (Weakness:
   * "Deals 2 less damage"; Susceptible: "Takes 3 more damage").
   */
  function effectsOn(c: CardView): { badges: CardBadge[]; alerts: string[]; counters: { label: string; value: number; color: string }[] } {
    const v = view;
    if (!v) return { badges: [], alerts: [], counters: [] };
    const order = ['status', 'damage', 'misc', 'affiliation', 'tier', 'resource', 'dice'];
    const on = tokensOnCard(c, v.tokens)
      .map((t) => ({ t, def: TOKEN_DB.byId.get(t.tokenId) }))
      .filter((x) => !!x.def)
      .sort((a, b) => order.indexOf(a.def!.category) - order.indexOf(b.def!.category) || a.def!.name.localeCompare(b.def!.name));
    const cardDef = c.num !== null ? CARD_DB.byNumber.get(c.num) : undefined;
    const badges = on.map(({ t, def }) => {
      const d = def!;
      // a timer or Lock that belongs to one of an Emperor's abilities says which
      const ability = t.ability?.card === c.id ? cardDef?.emperorAbilities.find((a) => a.slot === t.ability!.slot) : undefined;
      const lock = d.effect === CONFIG.tokenRules.lockToken;
      let label = d.counter ? `${d.name} ${t.value}` : d.name;
      if (t.turns) label += ` · ${t.turns} turn${t.turns === 1 ? '' : 's'} left`; // its turn clock
      if (t.drain) label += ` · ${t.drain} wearing off`; // its wear-off timer
      if (t.half) label += ' · takes half'; // a Link counter whose card takes half the damage copied to it
      if (ability) label = lock ? `Locked: ${ability.name}` : `${ability.name}: cooldown ${t.value}`;
      if (lock && t.price) label += ` · unlock ${t.price}${t.priceDark ? ' Dark' : ''}`;
      return { label, color: tokenColor(d), title: d.name };
    });
    // the effects that change what the card deals or takes, in words
    const alerts: string[] = [];
    if (c.faceUp) {
      for (const [effect, text] of Object.entries(CONFIG.tokenRules.previewNotes)) {
        const n = on.filter((x) => x.def!.effect === effect).reduce((sum, x) => sum + (x.def!.counter ? x.t.value : 1), 0);
        if (n > 0) alerts.push(text.replace(/\{n\}/g, String(n)));
      }
    }
    // every counter on the card with its number, for the side of the close-up: these change as the game goes
    // (Thorns 2, Buffer 3, Shield 1…). Several of one kind are added up. Damage, SP decay and Entrenched are
    // told elsewhere in the close-up, and so are the ones spelled out just above.
    const counters: { label: string; value: number; color: string }[] = [];
    const hidden = CONFIG.tokenRules.previewHidesCounters;
    for (const { t, def } of on) {
      const d = def!;
      if (!d.counter || hidden.includes(d.effect) || (c.faceUp && CONFIG.tokenRules.previewNotes[d.effect])) continue;
      const ability = t.ability?.card === c.id ? cardDef?.emperorAbilities.find((a) => a.slot === t.ability!.slot) : undefined;
      // one with a turn clock keeps its own line, with how long it has left
      const label = ability ? `${ability.name} cooldown` : d.name + (t.turns ? ` (${t.turns} turn${t.turns === 1 ? '' : 's'} left)` : '') + (t.drain ? ` (${t.drain} wearing off)` : '');
      const have = counters.find((k) => k.label === label);
      if (have) have.value += t.value;
      else counters.push({ label, value: t.value, color: tokenColor(d) });
    }
    return { badges, alerts, counters };
  }

  /** A Biome's "pay to remove": what it costs right now, and whether you have it. null = not a card that is removed this way. */
  function removalOf(def: CardDef, expired: boolean, tier = 1) {
    const v = view;
    if (!v || !def.types.some((t) => CONFIG.turn.removable.includes(t))) return null;
    const cost = expired ? 0 : Math.max(0, tieredRemoval(def, tier) ?? 0);
    const coin = def.removalDark ? 'Dark Gerzite' : 'Gerzite';
    const have = def.removalDark ? v.players[v.you].darkGerzite : v.players[v.you].gerzite;
    const canPay = have >= cost;
    return {
      cost,
      coin,
      have,
      canPay,
      label: expired ? 'Clear it away · free (its turns are over)' : cost > 0 ? `Remove from play · pay ${cost} ${coin}` : 'Remove from play · free',
      why: canPay ? 'It goes to its owner’s discard pile' : `You have ${have} ${coin}`,
    };
  }
  /** A run-down Tome's "pay to restart": how long the next run would last, what it costs, and whether it can be done. */
  function restartOf(c: CardView, def: CardDef) {
    const v = view;
    if (!v || !def.types.some((t) => CONFIG.turn.restartable.includes(t))) return null;
    const coin = def.replayDark ? 'Dark Gerzite' : 'Gerzite';
    const have = def.replayDark ? v.players[v.you].darkGerzite : v.players[v.you].gerzite;
    const turns = (def.turns ?? 0) - ((c.restarts ?? 0) + 1);
    if (!def.turns || def.replayCost === null) return { can: false, turns: 0, label: 'Can’t be restarted (no replay cost on this card)', why: 'Its turns or replay cost aren’t set in cards.csv' };
    if (turns <= 0) return { can: false, turns, label: 'Can’t be restarted again (it would last 0 turns)', why: 'Each restart lasts 1 turn less than the one before' };
    const can = have >= def.replayCost;
    return { can, turns, label: `Restart for ${turns} turn${turns === 1 ? '' : 's'} · pay ${def.replayCost} ${coin}`, why: can ? 'Each restart lasts 1 turn less than the one before' : `You have ${have} ${coin}` };
  }

  /**
   * A left-click on a card on the table. A face-up Biome, anyone's: pay its removal cost to take it out of
   * play. A Tome of yours whose turns are over: pay its replay cost to restart it, or discard it. Anything
   * else: false, and the click does what it always did.
   */
  /** The "Attack" row for one of your cards (and why not, if it can't right now), for its menu. */
  function attackItems(c: CardView): (MenuItem | Node)[] {
    const info = view?.combat[c.id];
    if (!info) return [];
    // a unit that can never attack (Skeleton Dropship): no Attack row at all, only what it can do
    if (info.never) return info.tap ? [] : [el('div', { class: 'float-note' }, info.why ?? 'It never attacks.')];
    const none = !info.why && !info.targets.length;
    const label = info.left !== undefined && info.left > 0 && !info.why ? `Attack (${info.left} hit${info.left === 1 ? '' : 's'} left)` : 'Attack';
    return [
      { label, disabled: !!info.why || none, onClick: () => table.beginStrike(c.id) },
      ...(info.why || none ? [el('div', { class: 'float-note' }, info.why ?? 'Nothing is in its reach right now.')] : []),
    ];
  }
  /**
   * A left-click on one of your units. With only an attack to make, it starts aiming at once (the cards it can
   * hit light up: click one). With a Tap ability too, or when it can't attack, a small menu says what it can do.
   */
  function unitClick(c: CardView, x: number, y: number): boolean {
    const v = view;
    const info = v?.combat[c.id];
    const def = c.num !== null ? CARD_DB.byNumber.get(c.num) : undefined;
    if (!v || !info || !def || watching) return false;
    // a once-a-turn ability it is used for when you choose to (The River Keeper: 3 damage to your Emperor for a Bell)
    const ability = def.effects.some((e) => e.when === 'click') && c.owner === v.you;
    if (!info.tap && !ability && !info.why && info.targets.length) {
      table.beginStrike(c.id);
      return true;
    }
    const items: (MenuItem | Node)[] = [...attackItems(c)];
    if (handsOff()) for (const t of locksOn(v, c)) items.push(lockPayItem(t));
    if (ability) {
      const line = def.text.split('\n').find((l) => /once per turn|you may/i.test(l) && /^passive/i.test(l.trim()))?.replace(/^passive:\s*/i, '') ?? 'Use its ability';
      items.push({ label: 'Use its ability', disabled: v.turn !== v.you, onClick: () => conn.send({ type: 'activate', id: c.id }) });
      items.push(el('div', { class: 'float-note' }, line.charAt(0).toUpperCase() + line.slice(1)));
    }
    if (info.tap) {
      items.push({ label: 'Use Tap ability', disabled: !!info.tapWhy, onClick: () => conn.send({ type: 'tap', id: c.id, dir: 1, as: 'ability' }) });
      if (info.tapWhy) items.push(el('div', { class: 'float-note' }, info.tapWhy));
    }
    if (!handsOff()) items.push(el('div', { class: 'float-note' }, 'Shift + click turns a card without using it.'));
    floatingMenu(x, y, def.name, items);
    return true;
  }

  function cardAct(c: CardView, expired: boolean, x: number, y: number): boolean {
    const v = view;
    if (!v || watching || c.num === null || !c.faceUp) return false;
    const def = CARD_DB.byNumber.get(c.num);
    if (!def) return false;
    if (CONFIG.emperorAbilities.enabled && isEmperor(def)) {
      emperorMenu(c, def, x, y);
      return true;
    }
    // a card of yours with an effect you set off by clicking it, once a turn (Moldering Tome: then click the Unit it is for)
    // (a Troop or Building's is offered in its click menu instead, with its attack, so it is never used by accident: The River Keeper)
    if (!expired && c.owner === v.you && def.effects.some((e) => e.when === 'click') && !(def.types.includes('troop') || def.types.includes('building'))) {
      conn.send({ type: 'activate', id: c.id });
      return true;
    }
    // one of a Lanespanner's extra units, destroyed: it is a copy, so clicking it simply clears it away
    if (expired && c.spanOf && (c.owner === v.you || v.mode === 'solo')) {
      conn.send({ type: 'delete', id: c.id });
      return true;
    }
    const ownerName = c.owner === v.you ? 'your' : `${v.players[c.owner]?.name ?? 'its owner'}’s`;
    const removal = removalOf(def, expired, c.tier);
    if (removal) {
      const note = expired
        ? `Its turns are over. Clearing it away sends it to ${ownerName} discard pile.`
        : `Either player can take a Biome out of play by paying its removal cost. It goes to ${ownerName} discard pile.${removal.canPay ? '' : ` You have ${removal.have} ${removal.coin}.`}`;
      // a Biome either player may use once a turn (Coral Reef: swap two of your Units)
      const shared = !expired && def.effects.some((e) => e.when === 'click' && e.conds.some((k) => k[0] === 'anyone'));
      const use: MenuItem[] = shared ? [{ label: 'Swap two of your Units (1 Gerzite, free with a Submerged one)', disabled: v.turn !== v.you, onClick: () => conn.send({ type: 'activate', id: c.id }) }] : [];
      floatingMenu(x, y, def.name, [el('div', { class: 'float-note' }, note), ...use, { label: removal.label, danger: !expired, disabled: !removal.canPay, onClick: () => conn.send({ type: 'removeCard', id: c.id }) }]);
      return true;
    }
    const again = expired && c.owner === v.you ? restartOf(c, def) : null;
    if (again) {
      const note = again.can || again.turns <= 0 || def.replayCost === null ? `Its turns are over. ${again.turns > 0 && def.replayCost !== null ? 'Pay its replay cost to start it again (each restart lasts 1 turn less than the one before), or discard it.' : 'It can only be discarded now.'}` : `Its turns are over. ${again.why}.`;
      floatingMenu(x, y, def.name, [
        el('div', { class: 'float-note' }, note),
        { label: again.label, disabled: !again.can, onClick: () => conn.send({ type: 'restart', id: c.id }) },
        { label: 'Discard it', danger: true, onClick: () => conn.send({ type: 'toPile', id: c.id, pile: 'discard', where: 'top' }) },
      ]);
      return true;
    }
    if (expired && c.owner === v.you && def.types.includes('spell')) {
      // a Spell of yours that has ended (its turns are over, its Lock was paid…): discard it, or recast it if it can be
      const recast = def.ends.includes('recast');
      const cost = Math.max(0, def.cost ?? 0);
      const coin = def.costDark ? 'Dark Gerzite' : 'Gerzite';
      const have = def.costDark ? v.players[v.you].darkGerzite : v.players[v.you].gerzite;
      floatingMenu(x, y, def.name, [
        el('div', { class: 'float-note' }, recast ? `It has ended. Pay its cost again to recast it (it brings a new token to place), or discard it.${have < cost ? ` You have ${have} ${coin}.` : ''}` : 'It has ended and is no longer active.'),
        ...(recast ? [{ label: `Recast · pay ${cost} ${coin}`, disabled: have < cost, onClick: () => conn.send({ type: 'recast', id: c.id }) } as MenuItem] : []),
        { label: 'Discard it', danger: true, onClick: () => conn.send({ type: 'toPile', id: c.id, pile: 'discard', where: 'top' }) },
      ]);
      return true;
    }
    return false;
  }

  /** Where one of an Emperor's abilities stands right now: ready, on cooldown, or why it can't be used. */
  function abilityState(c: CardView, ab: EmperorAbility) {
    const v = view!;
    const E = CONFIG.emperorAbilities;
    const mine = c.owner === v.you;
    const coin = ab.dark ? 'Dark Gerzite' : 'Gerzite';
    const have = ab.dark ? v.players[v.you].darkGerzite : v.players[v.you].gerzite;
    const effectOf = (t: Token) => TOKEN_DB.byId.get(t.tokenId)?.effect ?? '';
    const isLock = (t: Token) => effectOf(t) === CONFIG.tokenRules.lockToken;
    const on = tokensOnCard(c, v.tokens);
    // Frozen, Stunned or Entrenched: no abilities at all while the token is on the Emperor
    const held = on.find((t) => E.blockedBy.includes(effectOf(t)));
    if (held) return { ok: false, kind: 'short', coin, state: TOKEN_DB.byId.get(held.tokenId)?.name ?? 'Held' };
    // a Lock on this ability (or one on the Emperor not yet given to an ability)
    const lock = v.tokens.find((t) => isLock(t) && t.ability?.card === c.id && t.ability.slot === ab.slot);
    if (lock) return { ok: false, kind: 'short', coin, state: lock.price ? `Locked · ${lock.price} ${lock.priceDark ? 'Dark Gerzite' : 'Gerzite'} to unlock` : 'Locked' };
    if (on.some((t) => isLock(t) && !t.ability)) return { ok: false, kind: 'short', coin, state: 'A Lock is waiting for its ability' };
    const timer = v.tokens.find((t) => !isLock(t) && t.ability?.card === c.id && t.ability.slot === ab.slot && t.value > 0);
    if (timer) return { ok: false, kind: 'wait', coin, state: `On cooldown · ${timer.value} turn${timer.value === 1 ? '' : 's'}` };
    if (!mine) return { ok: false, kind: 'ready', coin, state: 'Ready' };
    // the right moment: its own `when`, or the usual Attack Phase
    if (abilityWindowRefusal(ab, v.turn, v.phase, v.you, v.mode === 'solo')) {
      const phases = (ab.phases ?? E.phases).map((i) => PHASES[i].replace(' Phase', '')).filter(Boolean);
      const mineNow = v.turn === v.you || v.mode === 'solo';
      const state = mineNow ? (phases.length ? `Ready · in your ${phases.join(' or ')} Phase` : 'Ready · on your opponent’s turn') : 'Ready · on your turn';
      return { ok: false, kind: 'wait', coin, state };
    }
    if (have < ab.cost) return { ok: false, kind: 'short', coin, state: `You have ${have} ${coin}` };
    return { ok: true, kind: 'ready', coin, state: 'Ready' };
  }

  /**
   * A left-click on an Emperor: its two abilities, each with its Gerzite cost, its cooldown in turns and a
   * few words on what it does. Your own can be used from here: the cost comes off your bar and a Start of
   * Turn Timer goes on the Emperor as the cooldown. Your opponent's are there to read.
   */
  function emperorMenu(c: CardView, def: CardDef, x: number, y: number) {
    const v = view!;
    const mine = c.owner === v.you;
    const blocks: Node[] = [];
    for (let slot = 1; slot <= EMPEROR_ABILITY_SLOTS; slot++) {
      const ab = def.emperorAbilities.find((a) => a.slot === slot);
      if (!ab) {
        blocks.push(el('div', { class: 'ability-card is-empty' }, el('span', { class: 'ability-slot' }, `Ability ${slot}`), el('p', { class: 'ability-text' }, 'Not written yet. Abilities are set in data/emperor_abilities.csv.')));
        continue;
      }
      const st = abilityState(c, ab);
      const turns = ab.cooldown > 0 ? `Cooldown ${ab.cooldown} turn${ab.cooldown === 1 ? '' : 's'}` : 'No cooldown';
      // when it can be used, if that isn't the usual Attack Phase
      const names = (ab.phases ?? []).map((i) => PHASES[i].replace(' Phase', ''));
      const when = ab.phases === null && !ab.opponentTurn ? '' : ab.phases?.length === PHASES.length ? (ab.opponentTurn ? 'Any time' : 'Any phase') : [names.join(' or '), ab.opponentTurn ? 'opponent’s turn' : ''].filter(Boolean).join(' · ');
      blocks.push(
        el(
          'button',
          {
            class: `ability-card is-${st.kind}`,
            type: 'button',
            disabled: !st.ok,
            onclick: () => {
              closeFloatMenu();
              conn.send({ type: 'emperorAbility', id: c.id, slot });
            },
          },
          el('span', { class: 'ability-top' }, el('span', { class: 'ability-slot' }, `Ability ${slot}`), el('span', { class: 'ability-state' }, st.state)),
          el('span', { class: 'ability-name' }, ab.name),
          el('span', { class: 'ability-meta' }, el('span', { class: `ability-chip cost ${ab.dark ? 'dark' : ''}`, title: ab.costVaries ? 'This cost is worked out as the ability is used: pay it yourself' : '' }, ab.costVaries ? `? ${st.coin}` : ab.cost > 0 ? `${ab.cost} ${st.coin}` : 'Free'), el('span', { class: 'ability-chip' }, turns), when ? el('span', { class: 'ability-chip when' }, when) : null),
          ab.text ? el('span', { class: 'ability-text' }, ab.text) : null,
        ),
      );
    }
    const note = mine ? null : el('div', { class: 'float-note' }, `${v.players[c.owner]?.name ?? 'Your opponent'}’s Emperor: its abilities, to read.`);
    const unlocks = handsOff() ? locksOn(v, c).map((t) => lockPayItem(t)) : [];
    floatingMenu(x, y, def.name, [...(note ? [note] : []), ...blocks, ...unlocks, ...(mine ? attackItems(c) : [])]);
  }

  /** The rows that give a Lock or a turn timer lying on an Emperor to one of its abilities (or take it back). */
  function abilityLinkItems(t: Token, c: CardView, kind: 'lock' | 'cooldown'): MenuItem[] {
    const def = c.num !== null ? CARD_DB.byNumber.get(c.num) : undefined;
    if (!def) return [];
    const items: MenuItem[] = def.emperorAbilities.map((ab) => ({
      label: `Ability ${ab.slot}: ${ab.name}${t.ability?.card === c.id && t.ability.slot === ab.slot ? '  ✓' : ''}`,
      onClick: () => conn.send({ type: 'linkAbility', id: t.id, slot: ab.slot }),
    }));
    items.push({ label: kind === 'lock' ? 'Not yet' : 'Neither: it is just a timer', onClick: () => conn.send({ type: 'linkAbility', id: t.id, slot: null }) });
    return items;
  }

  /**
   * You just put a Lock or a turn timer on an Emperor. A Lock locks ONE ability; a timer is the cooldown of
   * one (for a card effect that adds cooldown, or to put back a timer that was deleted by mistake). Which?
   */
  function askAbility(tokenId: string, c: CardView, kind: 'lock' | 'cooldown', x: number, y: number) {
    const v = view;
    const def = c.num !== null ? CARD_DB.byNumber.get(c.num) : undefined;
    // the token itself arrives with the same update: look it up once that has been taken in
    window.setTimeout(() => {
      const t = view?.tokens.find((tk) => tk.id === tokenId);
      if (!v || !def || !t || leaving) return;
      const note =
        kind === 'lock'
          ? 'A Lock on an Emperor locks one of its abilities until the Lock is taken off or paid for. The other ability stays free.'
          : `The timer becomes that ability’s cooldown: it can’t be used for ${t.value} turn${t.value === 1 ? '' : 's'} (click the timer to change the number).`;
      floatingMenu(x, y, kind === 'lock' ? `Lock which ability of ${def.name}?` : `A cooldown for which ability of ${def.name}?`, [el('div', { class: 'float-note' }, note), ...abilityLinkItems(t, c, kind)]);
    }, 60);
  }

  /** A Biome is being played where another lies: the old one's removal cost comes first. Asks; true = go ahead. */
  function askReplace(old: CardView, expired: boolean, def: CardDef, x: number, y: number, how: { free: boolean; dark: boolean }): Promise<boolean> {
    return new Promise((resolve) => {
      const v = view;
      const oldDef = old.num !== null ? CARD_DB.byNumber.get(old.num) : undefined;
      const removal = oldDef ? removalOf(oldDef, expired, old.tier) : null;
      if (!v || !oldDef || !removal) return resolve(true); // nothing to ask: the table will say if it minds
      let answered = false;
      const done = (yes: boolean) => {
        if (answered) return;
        answered = true;
        resolve(yes);
      };
      const me = v.players[v.you];
      // what the new card itself will cost, and from which bar
      const playDark = how.dark || def.costDark;
      const playCost = !CONFIG.turn.payToPlay || how.free ? 0 : (def.cost ?? 0) * (how.dark ? darkgaleMultiplier(def) : 1);
      const playCoin = playDark ? 'Dark Gerzite' : 'Gerzite';
      const needGerzite = (removal.coin === 'Gerzite' ? removal.cost : 0) + (playDark ? 0 : playCost);
      const needDark = (removal.coin === 'Dark Gerzite' ? removal.cost : 0) + (playDark ? playCost : 0);
      const canPay = me.gerzite >= needGerzite && me.darkGerzite >= needDark;
      const ownerName = old.owner === v.you ? 'your' : `${v.players[old.owner]?.name ?? 'its owner'}’s`;
      const then = playCost > 0 ? `, then ${playCost} ${playCoin} for ${def.name}` : how.free ? `, then ${def.name} for free` : '';
      const short = canPay ? '' : ` You have ${me.gerzite} Gerzite${needDark ? ` and ${me.darkGerzite} Dark Gerzite` : ''}.`;
      floatingMenu(
        x,
        y,
        `${oldDef.name} is already in play`,
        [
          el('div', { class: 'float-note' }, expired ? `Its turns are over, so it is cleared away for nothing. It goes to ${ownerName} discard pile.${short}` : `Before ${def.name} can take its place, ${oldDef.name}’s removal cost has to be paid. It goes to ${ownerName} discard pile.${short}`),
          { label: expired ? `Clear it away${then}` : `Pay ${removal.cost} ${removal.coin} to remove it${then}`, disabled: !canPay, onClick: () => done(true) },
          { label: 'Leave it', onClick: () => done(false) },
        ],
        () => done(false),
      );
    });
  }

  /** The name of a card on the table, by its id ("a face-down card" if you can't see it). */
  function cardNameOf(id: string) {
    const f = view?.field.find((o) => o.id === id);
    return f?.num != null ? (CARD_DB.byNumber.get(f.num)?.name ?? 'a card') : 'a face-down card';
  }
  /** What sacrificing one of your face-up Troops or Buildings would give right now (null if it can't be worked out). */
  function sacrifice(c: CardView, def: ReturnType<typeof CARD_DB.byNumber.get>) {
    const v = view;
    if (!v || !def || !c.faceUp || (c.spawned && !c.swarm) || !(def.types.includes('troop') || def.types.includes('building'))) return null;
    // (a swarm is sacrificed whole: what all of its units have on them, and how many of them are left)
    const units = c.swarm ? v.field.filter((o) => o.swarm === c.swarm) : [c];
    const on = units.flatMap((u) => tokensOnCard(u, v.tokens));
    const sum = (effect: (e: string) => boolean) => on.filter((t) => effect(TOKEN_DB.byId.get(t.tokenId)?.effect ?? '')).reduce((n, t) => n + t.value, 0);
    const damage = sum((e) => countsAgainstHealth(e)); // damage, and for a Building the SP it has lost to decay
    const decay = sum((e) => e === CONFIG.tokenRules.spDecayToken);
    const overheal = sum((e) => e === 'overheal');
    const gained = sum((e) => e === CONFIG.tokenRules.healthToken); // health it has gained counts like health it never lost
    const as = { ...formOf(def, c.form, c.tier), ...(c.clone?.health !== undefined ? { health: c.clone.health } : {}) }; // a two-cost card is worth what it was summoned at (a tiered one has more health)
    const alive = c.swarm ? units.length : on.find((t) => TOKEN_DB.byId.get(t.tokenId)?.effect === CONFIG.tokenRules.countToken)?.value ?? null; // a Count: the units still alive
    const gives = c.legionWorth ?? countedSacrificeWorth(as, as.count, alive, damage, overheal, gained); // (Undead Legion: the whole Legion, as the table works it out)
    if (gives === null) return null;
    const entrenched = on.some((t) => TOKEN_DB.byId.get(t.tokenId)?.effect === CONFIG.tokenRules.entrenchedToken);
    // a unit summoned with Dark Gerzite can't be turned back into Dark Gerzite
    const darkgale = !!CONFIG.turn.darkgale.token && on.some((t) => TOKEN_DB.byId.get(t.tokenId)?.effect === CONFIG.turn.darkgale.token);
    return { gives, damage, decay, overheal, health: as.health!, entrenched, darkgale, building: def.types.includes('building') };
  }
  /** A card's health (or SP) right now, for its close-up, and what a sacrifice would give if it can be sacrificed. */
  function healthNote(c: CardView, def: ReturnType<typeof CARD_DB.byNumber.get>) {
    const v = view;
    const full = def && c.faceUp ? (c.clone?.health ?? formOf(def, c.form, c.tier).health) : null;
    if (!v || !def || !full) return '';
    const on = tokensOnCard(c, v.tokens);
    const sum = (effect: (e: string) => boolean) => on.filter((t) => effect(TOKEN_DB.byId.get(t.tokenId)?.effect ?? '')).reduce((n, t) => n + t.value, 0);
    const damage = sum((e) => countsAgainstHealth(e));
    const decay = sum((e) => e === CONFIG.tokenRules.spDecayToken);
    const overheal = sum((e) => e === 'overheal');
    const gained = sum((e) => e === CONFIG.tokenRules.healthToken);
    const now = Math.max(0, full + gained - damage) + overheal;
    const lost = decay > 0 ? ` (${damage > decay ? `${damage - decay} damage, ` : ''}${decay} SP decay)` : '';
    const w = sacrifice(c, def);
    const worth = !w ? '' : w.darkgale ? ' · Darkgale: can’t be sacrificed' : ` · sacrifice worth ${w.gives} Dark Gerzite`;
    return `${def.types.includes('building') ? 'SP' : 'Health'} ${now}/${full + gained}${gained ? ` (${gained} gained)` : ''}${lost}${overheal ? ` (with ${overheal} Overheal)` : ''}${worth}`;
  }

  /**
   * The one word for how a card stands right now: Active, Tapped or Entrenched; or, with a status on it
   * that stops it acting, "Inactive: Frozen". (Which way it is turned can be seen on the table.)
   */
  function stateOf(c: CardView, def: ReturnType<typeof CARD_DB.byNumber.get>) {
    const v = view;
    if (!c.faceUp) return 'Face-down';
    // a Lock a card put on itself (Persistent Shield) holds it in its slot: it doesn't make the card inactive
    const on = v ? tokensOnCard(c, v.tokens).filter((t) => t.from !== c.id).map((t) => TOKEN_DB.byId.get(t.tokenId)).filter((d) => !!d) : [];
    if (on.some((d) => d!.effect === CONFIG.turn.lifetime.expiredToken)) return def?.ends.length ? 'It has ended' : def?.turns || def?.turnsUnset ? 'Its turns are over' : 'Destroyed';
    // a Lock on an Emperor locks one ability, not the Emperor
    const abilityLock = !!def && isEmperor(def) && CONFIG.emperorAbilities.enabled;
    const held = on.filter((d) => CONFIG.tokenRules.inactiveStatuses.includes(d!.effect) && !(abilityLock && d!.effect === CONFIG.tokenRules.lockToken));
    if (held.length) return `Inactive: ${[...new Set(held.map((d) => d!.name))].join(', ')}`;
    if (sitsSideways(def) || c.asBiome) return 'In play'; // (Eldritch Realm Crosser as the Biome too)
    if (on.some((d) => d!.effect === CONFIG.tokenRules.entrenchedToken) || c.rot === CONFIG.arrival.entrenchedRotation) return 'Entrenched';
    return c.rot % 2 === 1 ? 'Tapped' : 'Active';
  }

  function openTokenMenu(t: Token, x: number, y: number) {
    const def = TOKEN_DB.byId.get(t.tokenId);
    if (watching) {
      floatingMenu(x, y, (t.unset ? `${def?.name ?? 'Timer'} · not set yet` : def?.counter || def?.category === 'dice' ? `${def.name} · ${t.value}` : (def?.name ?? 'Token')) + (t.turns ? ` · ${t.turns} turn${t.turns === 1 ? '' : 's'} left` : '') + (t.drain ? ` · ${t.drain} wearing off` : ''), []);
      return;
    }
    const items: (MenuItem | Node)[] = [];
    // a status waiting for its cure roll (Burn, after its damage)
    const faces = t.cure !== undefined ? cureFaces(t) : null;
    if (faces && view && (t.cure === view.you || view.mode === 'solo')) items.push({ label: `Roll to cure it (${cureWords(faces)} cures)`, onClick: () => conn.send({ type: 'cureRoll', id: t.id }) });
    // in a game the table keeps the counters itself: what it is, and its rules, but nothing to change
    if (handsOff()) {
      if (def?.effect === CONFIG.tokenRules.lockToken) items.push(lockPayItem(t));
      const rule = ruleFor(def?.name, def?.effect);
      if (rule) items.push({ label: `Rules: ${rule}`, onClick: () => showRules(rule) });
      floatingMenu(x, y, (t.unset ? `${def?.name ?? 'Timer'} · not set yet` : def?.counter || def?.category === 'dice' ? `${def.name} · ${t.value}` : (def?.name ?? 'Token')) + (t.turns ? ` · ${t.turns} turn${t.turns === 1 ? '' : 's'} left` : '') + (t.drain ? ` · ${t.drain} wearing off` : ''), items);
      return;
    }
    if (def?.category === 'dice') items.push({ label: 'Roll', onClick: () => conn.send({ type: 'roll', id: t.id }) });
    if (def?.bar && t.asCounter) items.push(...gerziteCounterRows(t, def.name));
    if (def?.counter) {
      let value = t.value;
      // a turn timer that arrived blank (its card's turn count is "?") reads "?" until it is given a number
      let unset = !!t.unset;
      const shown = el('span', { class: `counter-value${unset ? ' counter-value-unset' : ''}` }, unset ? '?' : String(value));
      const change = (d: number) => {
        value = Math.max(0, Math.min(999, value + d));
        if (value > 0) unset = false;
        shown.textContent = unset ? '?' : String(value);
        shown.classList.toggle('counter-value-unset', unset);
        conn.send({ type: 'tokenValue', id: t.id, delta: d });
      };
      if (unset) items.push(el('div', { class: 'float-note' }, 'Turns it lasts (not set yet):'));
      // one that can also count turns has two numbers: say which is which
      if (canClock(def)) items.push(el('div', { class: 'float-note' }, def.effect === 'regeneration' ? 'Amount it heals each turn:' : 'Amount:'));
      if (def.effect === CONFIG.tokenRules.linkToken) items.push(el('div', { class: 'float-note' }, 'Pair number (the Link with the same number is its partner):'));
      items.push(
        el(
          'div',
          { class: 'counter-row' },
          el('button', { class: 'btn counter-btn', type: 'button', onclick: () => change(-1), 'aria-label': 'Minus one' }, '−'),
          shown,
          el('button', { class: 'btn counter-btn', type: 'button', onclick: () => change(1), 'aria-label': 'Plus one' }, '+'),
        ),
      );
    }
    // a counter that holds an amount can also count turns: a little clock on it, set here
    if (canClock(def)) {
      let turns = t.turns ?? 0;
      const words = () => (turns > 0 ? `${turns} turn${turns === 1 ? '' : 's'}` : '–');
      const shown = el('span', { class: 'counter-value counter-value-turns' }, words());
      const set = (n: number) => {
        turns = Math.max(0, Math.min(CONFIG.turn.durations.max, n));
        shown.textContent = words();
        conn.send({ type: 'tokenTurns', id: t.id, turns });
      };
      items.push(
        el('div', { class: 'float-note' }, 'Turns it lasts (– = until removed):'),
        el(
          'div',
          { class: 'counter-row counter-row-clock' },
          el('button', { class: 'btn counter-btn', type: 'button', onclick: () => set(turns - 1), 'aria-label': 'One turn fewer' }, '−'),
          shown,
          el('button', { class: 'btn counter-btn', type: 'button', onclick: () => set(turns + 1), 'aria-label': 'One turn more' }, '+'),
        ),
      );
      // …and part of its number can be temporary: it wears off 1 a turn, at the same moment a turn clock goes down
      let drain = t.drain ?? 0;
      const drainWords = () => (drain > 0 ? `${drain}` : '–');
      const drainShown = el('span', { class: 'counter-value counter-value-turns' }, drainWords());
      const setDrain = (n: number) => {
        drain = Math.max(0, Math.min(t.value, CONFIG.turn.durations.max, n));
        drainShown.textContent = drainWords();
        conn.send({ type: 'tokenDrain', id: t.id, drain });
      };
      items.push(
        el('div', { class: 'float-note' }, 'Wears off 1 a turn (how much of it; – = none):'),
        el(
          'div',
          { class: 'counter-row counter-row-clock' },
          el('button', { class: 'btn counter-btn', type: 'button', onclick: () => setDrain(drain - 1), 'aria-label': 'One less wears off' }, '−'),
          drainShown,
          el('button', { class: 'btn counter-btn', type: 'button', onclick: () => setDrain(drain + 1), 'aria-label': 'One more wears off' }, '+'),
        ),
      );
    }
    // a status whose moment its player picks (Regeneration): as the turn begins, or as it ends
    const picked = CONFIG.turn.statusActs.find((x) => x.choose && x.effect === def?.effect);
    if (picked) {
      const at = t.actAt ?? picked.when;
      const on = (yes: boolean) => (yes ? '✓ ' : '');
      const words: Record<string, string> = { start: 'As your turn begins', end: 'As your turn ends (End Phase)' };
      items.push(
        el('div', { class: 'float-note' }, `${picked.does === 'heal' ? 'Heals' : 'Acts'} each turn at (its clock goes down then too):`),
        ...picked.choose!.map((w) => ({ label: `${on(at === w)}${words[w] ?? w}`, onClick: () => conn.send({ type: 'tokenActAt', id: t.id, at: w }) })),
      );
    }
    if (def?.effect === CONFIG.tokenRules.lockToken) items.push(...lockRows(t));
    // a Harvest counter (Gerzite Farmer): spend it for Gerzite or Dark Gerzite
    if (CONFIG.harvest.token && t.tokenId === CONFIG.harvest.token) {
      items.push(
        el('div', { class: 'float-note' }, `Spend ${def?.name ?? 'Harvest'} (this counter has ${t.value}):`),
        ...CONFIG.harvest.spend.map((x) => {
          const gives = TOKEN_DB.byId.get(x.gives)?.name ?? x.gives;
          return { label: `Spend ${x.cost} → gain ${x.amount} ${gives}`, disabled: t.value < x.cost, title: t.value < x.cost ? `Needs ${x.cost}` : '', onClick: () => conn.send({ type: 'harvestSpend', id: t.id, gives: x.gives }) };
        }),
      );
    }
    // a Link counter: does the card under it take all of the damage copied across, or half?
    if (def?.effect === CONFIG.tokenRules.linkToken) {
      items.push(
        el('div', { class: 'float-note' }, 'Damage copied onto this card:'),
        { label: `${t.half ? '' : '✓ '}All of it`, onClick: () => conn.send({ type: 'linkHalf', id: t.id, half: false }) },
        { label: `${t.half ? '✓ ' : ''}Half (rounded up)`, onClick: () => conn.send({ type: 'linkHalf', id: t.id, half: true }) },
      );
    }
    // a Lock or a turn timer lying on an Emperor belongs to one of its abilities: say (or change) which
    const kind = def?.effect === CONFIG.tokenRules.lockToken ? 'lock' : def && CONFIG.turn.timers.includes(def.effect) ? 'cooldown' : null;
    const emperor = kind && view && CONFIG.emperorAbilities.enabled ? view.field.filter((c) => c.faceUp && c.num !== null && tokensOnCard(c, [t]).length > 0 && !!CARD_DB.byNumber.get(c.num)?.emperorAbilities.length).at(-1) : undefined;
    if (kind && emperor) items.push(el('div', { class: 'float-note' }, kind === 'lock' ? 'On an Emperor, a Lock locks one ability:' : 'On an Emperor, a timer is the cooldown of one ability:'), ...abilityLinkItems(t, emperor, kind));
    // with several things selected, Delete on one of them deletes them all
    const group = table.selectionWith(t.id);
    const canDelete = view?.mode === 'solo' || CONFIG.deleteInGames; // (only on the solo table: in a game the table keeps the tokens itself)
    if (canDelete && group > 1) items.push({ label: `Delete all ${group} selected`, danger: true, onClick: () => table.removeSelected() });
    else if (canDelete) items.push({ label: 'Delete', danger: true, onClick: () => conn.send({ type: 'delete', id: t.id }) });
    // a status effect: what the rules say about it
    const rule = ruleFor(def?.name, def?.effect);
    if (rule) items.push({ label: `Rules: ${rule}`, onClick: () => showRules(rule) });
    floatingMenu(x, y, def?.category === 'dice' ? `${def.name} · showing ${t.value}` : t.unset ? `${def?.name ?? 'Timer'} · not set yet` : (def?.name ?? 'Token'), items);
  }

  /**
   * A Gerzite (or Dark Gerzite) counter lying on the table: how much it holds, how many turns it lasts, and what
   * it does to its owner's bar each turn: add or remove its amount as their turn begins or ends.
   */
  function gerziteCounterRows(t: Token, name: string): (MenuItem | Node)[] {
    const rows: (MenuItem | Node)[] = [];
    const stepRow = (label: string, get: () => string, dec: () => void, inc: () => void) => {
      const shown = el('span', { class: 'counter-value' }, get());
      const redo = () => (shown.textContent = get());
      rows.push(
        el('div', { class: 'float-note' }, label),
        el(
          'div',
          { class: 'counter-row' },
          el('button', { class: 'btn counter-btn', type: 'button', onclick: () => { dec(); redo(); }, 'aria-label': 'One less' }, '−'),
          shown,
          el('button', { class: 'btn counter-btn', type: 'button', onclick: () => { inc(); redo(); }, 'aria-label': 'One more' }, '+'),
        ),
      );
    };
    let value = Math.max(1, t.value);
    stepRow('Amount:', () => String(value), () => { if (value > 1) { value--; conn.send({ type: 'tokenValue', id: t.id, delta: -1 }); } }, () => { value++; conn.send({ type: 'tokenValue', id: t.id, delta: 1 }); });
    if (CONFIG.turn.durations.enabled) {
      let turns = t.turns ?? 0;
      const set = (n: number) => {
        turns = Math.max(0, Math.min(CONFIG.turn.durations.max, n));
        conn.send({ type: 'tokenTurns', id: t.id, turns });
      };
      stepRow('Turns it lasts (– = until removed):', () => (turns > 0 ? `${turns} turn${turns === 1 ? '' : 's'}` : '–'), () => set(turns - 1), () => set(turns + 1));
    }
    const flow = t.flow;
    const pick = (mode: 'add' | 'remove' | null, when?: 'start' | 'end') => conn.send({ type: 'tokenFlow', id: t.id, mode, ...(when ? { when } : {}) });
    const on = (yes: boolean) => (yes ? '✓ ' : '');
    rows.push(
      el('div', { class: 'float-note' }, `Each of your turns, this counter…`),
      { label: `${on(flow?.mode === 'add')}Adds ${Math.max(1, t.value)} ${name} to your bar`, onClick: () => pick('add', flow?.when ?? 'start') },
      { label: `${on(flow?.mode === 'remove')}Removes ${Math.max(1, t.value)} ${name} from your bar`, onClick: () => pick('remove', flow?.when ?? 'start') },
      { label: `${on(!flow)}Does nothing (just a counter)`, onClick: () => pick(null) },
    );
    if (flow) {
      rows.push(
        el('div', { class: 'float-note' }, 'When:'),
        { label: `${on(flow.when === 'start')}As your turn begins`, onClick: () => pick(flow.mode, 'start') },
        { label: `${on(flow.when === 'end')}As your turn ends (your End Phase)`, onClick: () => pick(flow.mode, 'end') },
      );
    }
    rows.push({ label: `Put ${Math.max(1, t.value)} ${name} on my bar`, onClick: () => conn.send({ type: 'tokenToBar', id: t.id }) });
    return rows;
  }

  /** Can this kind of token carry a turn clock? (the counters that hold an amount: CONFIG.turn.durations.effects) */
  function canClock(def: (typeof TOKEN_DB.list)[number] | undefined) {
    return CONFIG.turn.durations.enabled && !!def?.counter && CONFIG.turn.durations.effects.includes(def.effect);
  }

  /**
   * Paying a Lock off, in a game (where its cost is set by the card that put it there): a button for the player who
   * may pay it, or a line saying who can. Shown on the Lock itself, and on the card or Emperor it holds.
   */
  function lockPayItem(t: Token): MenuItem | Node {
    const v = view!;
    const under = v.field.filter((c) => tokensOnCard(c, [t]).length).sort((a, b) => b.layer - a.layer)[0];
    const mine = t.payBy !== undefined && v.mode !== 'solo' ? allyOf(v, t.payBy) : !under || under.spawned || allyOf(v, under.owner) || v.mode === 'solo';
    const cost = t.price ?? 0;
    const coin = t.priceDark ? 'Dark Gerzite' : 'Gerzite';
    const have = t.priceDark ? v.players[v.you].darkGerzite : v.players[v.you].gerzite;
    const what = t.ability && under?.num != null ? `Ability ${t.ability.slot}` : under?.faceUp && under.num !== null ? (CARD_DB.byNumber.get(under.num)?.name ?? 'it') : 'it';
    if (cost <= 0) return el('div', { class: 'float-note' }, 'This Lock has no unlock cost: it stays until a card effect takes it off.');
    if (!mine) return el('div', { class: 'float-note' }, `Locked: ${cost} ${coin} to unlock. ${t.payBy !== undefined ? `Only ${sideName(v, t.payBy)} can pay it off.` : `Only ${v.players[under!.owner]?.name ?? 'the card’s owner'} can pay to unlock it.`}`);
    return { label: have < cost ? `Unlock ${what}: ${cost} ${coin} (you have ${have})` : `Pay ${cost} ${coin} to unlock ${what}`, disabled: have < cost, onClick: () => conn.send({ type: 'payLock', id: t.id }) };
  }
  /** The Locks holding a card (on it, or on one of an Emperor's abilities). */
  const locksOn = (v: GameView, c: CardView) => v.tokens.filter((t) => TOKEN_DB.byId.get(t.tokenId)?.effect === CONFIG.tokenRules.lockToken && (t.ability ? t.ability.card === c.id : tokensOnCard(c, [t]).length > 0));

  /**
   * A Lock's menu: set what it costs to take off, and in which resource; the owner of the locked card
   * then pays it here and the Lock is removed.
   */
  function lockRows(t: Token): Node[] {
    const v = view;
    if (!v) return [];
    let cost = t.price ?? 0;
    let dark = !!t.priceDark;
    const me = v.players[v.you];
    // whose card is it on? (only that player pays; a Lock lying loose can be paid by anyone)
    const under = v.field.filter((c) => tokensOnCard(c, [t]).length).sort((a, b) => b.layer - a.layer)[0];
    // …unless the Lock names who pays it (Persistent Shield's is its opponent's to pay)
    const mine = t.payBy !== undefined && v.mode !== 'solo' ? allyOf(v, t.payBy) : !under || under.spawned || allyOf(v, under.owner) || v.mode === 'solo';
    const coin = () => (dark ? 'Dark Gerzite' : 'Gerzite');
    const max = () => (dark ? CONFIG.zones.darkGerziteBar.slots : CONFIG.tokenRules.lockMaxCost);
    const shown = el('span', { class: 'counter-value' }, String(cost));
    const useG = el('button', { class: 'btn btn-small', type: 'button' }, 'Gerzite');
    const useD = el('button', { class: 'btn btn-small', type: 'button' }, 'Dark Gerzite');
    const pay = el('button', { class: 'float-item peek-go', type: 'button' });
    const refresh = () => {
      shown.textContent = String(cost);
      useG.classList.toggle('is-on', !dark);
      useD.classList.toggle('is-on', dark);
      useG.setAttribute('aria-pressed', String(!dark));
      useD.setAttribute('aria-pressed', String(dark));
      const have = dark ? me.darkGerzite : me.gerzite;
      pay.hidden = !mine;
      pay.disabled = cost <= 0 || have < cost;
      pay.textContent = cost <= 0 ? 'Set a cost to unlock it by paying' : have < cost ? `Unlocking costs ${cost} ${coin()}: you have ${have}` : `Pay ${cost} ${coin()} and unlock`;
    };
    const set = (next: number, nextDark: boolean) => {
      dark = nextDark;
      cost = Math.max(0, Math.min(max(), next));
      conn.send({ type: 'lockPrice', id: t.id, cost, dark });
      refresh();
    };
    useG.addEventListener('click', () => set(cost, false));
    useD.addEventListener('click', () => set(cost, true));
    pay.addEventListener('click', () => {
      closeFloatMenu();
      conn.send({ type: 'payLock', id: t.id });
    });
    refresh();
    return [
      el('div', { class: 'float-note' }, 'Cost to unlock, and what it is paid with:'),
      el(
        'div',
        { class: 'counter-row' },
        el('button', { class: 'btn counter-btn', type: 'button', onclick: () => set(cost - 1, dark), 'aria-label': 'Cost minus one' }, '−'),
        shown,
        el('button', { class: 'btn counter-btn', type: 'button', onclick: () => set(cost + 1, dark), 'aria-label': 'Cost plus one' }, '+'),
      ),
      el('div', { class: 'lock-coins' }, useG, useD),
      ...(mine ? [pay] : [el('div', { class: 'float-note' }, t.payBy !== undefined ? `Only ${v.players[t.payBy]?.name ?? 'your opponent'} can pay this Lock off.` : `Only ${v.players[under!.owner]?.name ?? 'the card’s owner'} can pay to unlock this card.`)]),
    ];
  }

  /** A "how many cards" picker and a button, for looking at the top of a pile. */
  function peekRows(seat: Seat, pile: PileName, count: number, mine: boolean, ownerName: string) {
    let n = Math.min(count, Math.max(1, Math.min(lastPeekCount, count)));
    const shown = el('span', { class: 'counter-value' }, String(n));
    const go = el('button', { class: 'float-item peek-go', type: 'button' });
    const label = () => (go.textContent = mine ? `Look at the top ${n === 1 ? 'card' : `${n} cards`}` : `Ask ${ownerName} to show the top ${n === 1 ? 'card' : `${n} cards`}`);
    const change = (d: number) => {
      n = Math.max(1, Math.min(count, n + d));
      shown.textContent = String(n);
      label();
    };
    label();
    go.addEventListener('click', () => {
      lastPeekCount = n;
      closeFloatMenu();
      conn.send({ type: 'peekAsk', seat, pile, count: n });
    });
    return [
      el(
        'div',
        { class: 'counter-row' },
        el('button', { class: 'btn counter-btn', type: 'button', onclick: () => change(-1), 'aria-label': 'One card fewer' }, '−'),
        shown,
        el('button', { class: 'btn counter-btn', type: 'button', onclick: () => change(1), 'aria-label': 'One card more' }, '+'),
      ),
      go,
    ];
  }
  let lastPeekCount = 3;

  function openPileMenu(seat: Seat, pile: PileName, x: number, y: number) {
    const v = view;
    if (!v) return;
    const p = v.players[seat];
    const count = pile === 'deck' ? p.deckCount : p.discard.length;
    const pileName = pile === 'deck' ? 'deck' : 'discard pile';
    // Heart of Limbo: cards set aside beside the discard pile, out of reach (only how many)
    const limbo = pile === 'discard' && p.limboCount ? ` · ${p.limboCount} in Limbo` : '';
    if (watching) {
      floatingMenu(x, y, `${p.name}’s ${pileName} · ${count} card${count === 1 ? '' : 's'}${limbo}`, []);
      return;
    }
    if (seat !== v.you && handsOff()) {
      floatingMenu(x, y, `${p.name}’s ${pileName} · ${count} card${count === 1 ? '' : 's'}${limbo}`, []);
      return;
    }
    if (seat !== v.you) {
      // your opponent's pile: you can ask to be shown its top cards, for a card effect that lets you
      const waiting = !!v.peekRequest;
      floatingMenu(x, y, `${p.name}’s ${pileName} · ${count} card${count === 1 ? '' : 's'}${limbo}`, [
        el('div', { class: 'float-note' }, count === 0 ? 'It is empty.' : waiting ? 'A request to peek is already waiting for an answer.' : `If a card lets you look at the top of this ${pileName}, choose how many cards and ask. ${p.name} has to agree.`),
        ...(count > 0 && !waiting ? peekRows(seat, pile, count, false, p.name) : []),
      ]);
      return;
    }
    if (handsOff()) {
      floatingMenu(x, y, `Your ${pileName} · ${count} card${count === 1 ? '' : 's'}${limbo}`, [el('div', { class: 'float-note' }, `The table draws and searches for you: a card that lets you search or look at your ${pileName} opens it for you.${pile === 'discard' ? ' In your Overdraw Phase you may drag cards from your hand onto it to make room.' : ''}`)]);
      return;
    }
    const busy = p.searching !== null;
    const items: (MenuItem | Node)[] = [
      { label: 'Search', disabled: busy, onClick: () => conn.send({ type: 'searchStart', pile }) },
      { label: 'Shuffle', disabled: busy || count < 2, onClick: () => conn.send({ type: 'shuffle', pile }) },
    ];
    if (count > 0) items.push(el('div', { class: 'float-note' }, pile === 'deck' ? 'Or look at just the top cards, without shuffling:' : 'Or look at just the top cards:'), ...peekRows(seat, pile, count, true, p.name));
    if (pile === 'deck' && count === 0) items.push({ label: `A card effect made me draw: ${CONFIG.turn.deckOutDamage || 1} damage to my Emperor`, danger: true, onClick: () => conn.send({ type: 'deckOut' }) });
    floatingMenu(x, y, `Your ${pileName} · ${count} card${count === 1 ? '' : 's'}${limbo}`, items);
  }

  // ---------------- rolling to cure a status ----------------
  /** The faces that cure this token's status, or null if it isn't cured by a die. */
  function cureFaces(t: Token) {
    const effect = TOKEN_DB.byId.get(t.tokenId)?.effect;
    return CONFIG.turn.statusActs.find((a) => a.effect === effect)?.cure ?? null;
  }
  /** A status has dealt its damage and now waits for its owner's die: a Roll button for them, a "waiting" line for the other player. */
  function cureNotice(v: GameView, t: Token) {
    const faces = cureFaces(t);
    const card = v.field.filter((c) => tokensOnCard(c, [t]).length).sort((a, b) => b.layer - a.layer)[0];
    if (!faces || !card || t.cure === undefined) return null;
    const name = TOKEN_DB.byId.get(t.tokenId)?.name ?? 'The status';
    const where = card.num !== null && card.faceUp ? (CARD_DB.byNumber.get(card.num)?.name ?? `Card ${card.num}`) : 'a face-down card';
    if (watching || (t.cure !== v.you && v.mode !== 'solo')) {
      return el('div', { class: 'notice notice-ask notice-cure' }, el('span', {}, `${name} dealt its damage to ${where}. Waiting for ${v.players[t.cure]?.name ?? 'its owner'} to roll: ${cureWords(faces)} cures it.`));
    }
    return el(
      'div',
      { class: 'notice notice-ask notice-cure' },
      el('span', {}, `${name} dealt its damage to ${where}. Roll to cure it: ${cureWords(faces)} cures it.`),
      el('span', { class: 'notice-buttons' }, button('Roll the die', () => conn.send({ type: 'cureRoll', id: t.id }), 'btn-primary btn-small')),
    );
  }

  /** A card asks for a die as a turn begins (Pestilence Approaching): a Roll button for the roller, a "waiting" line for the other player. */
  function cardRollNotice(v: GameView, c: CardView) {
    const name = c.num !== null ? (CARD_DB.byNumber.get(c.num)?.name ?? `Card ${c.num}`) : 'A card';
    const ends = cureWords(CONFIG.turn.endings.rollEnds);
    if (c.rollFor === 'coin') {
      // Magical Coin: a coin flip made with a die
      const C = CONFIG.coinGerzite;
      const roller = v.players[c.roll ?? 0]?.name ?? 'your opponent';
      const odds = `Even is heads (+${C.heads} Gerzite for ${c.roll === v.you ? 'you' : roller}), odd is tails (+${C.tails} for ${c.roll === v.you ? 'your opponent' : 'their opponent'}).`;
      if (watching || (c.roll !== v.you && v.mode !== 'solo')) return el('div', { class: 'notice notice-ask notice-cure' }, el('span', {}, `${name}: waiting for ${roller} to flip the coin. ${odds}`));
      return el(
        'div',
        { class: 'notice notice-ask notice-cure' },
        el('span', {}, `${name}: flip the coin by rolling the die. ${odds}`),
        el('span', { class: 'notice-buttons' }, button('Roll the die', () => conn.send({ type: 'cardRoll', id: c.id }), 'btn-primary btn-small')),
      );
    }
    if (watching || (c.roll !== v.you && v.mode !== 'solo')) {
      return el('div', { class: 'notice notice-ask notice-cure' }, el('span', {}, `${name}: waiting for ${v.players[c.roll ?? 0]?.name ?? 'your opponent'} to roll. ${ends.replace(/^./, (ch) => ch.toUpperCase())} ends it.`));
    }
    return el(
      'div',
      { class: 'notice notice-ask notice-cure' },
      el('span', {}, `${name}: roll the die. ${ends.replace(/^./, (ch) => ch.toUpperCase())} ends it; anything else and it stays in play.`),
      el('span', { class: 'notice-buttons' }, button('Roll the die', () => conn.send({ type: 'cardRoll', id: c.id }), 'btn-primary btn-small')),
    );
  }

  // ---------------- peeking at the top of a pile ----------------
  /** Your opponent has asked to see the top cards of one of your piles (or you are waiting on yours). */
  function peekRequestNotice(v: GameView, ask: NonNullable<GameView['peekRequest']>) {
    const what = ask.pile === 'deck' ? 'deck' : 'discard pile';
    const cardsText = `the top ${ask.count === 1 ? 'card' : `${ask.count} cards`}`;
    if (ask.from === v.you) {
      const other = v.players[v.you === 0 ? 1 : 0]?.name ?? 'your opponent';
      return el('div', { class: 'notice notice-ask' }, el('span', {}, `Waiting for ${other} to let you see ${cardsText} of their ${what}…`), button('Take it back', () => conn.send({ type: 'peekAnswer', accept: false }), 'btn-ghost btn-small'));
    }
    return el(
      'div',
      { class: 'notice notice-ask' },
      el('span', {}, `${v.players[ask.from]?.name ?? 'Your opponent'} wants to see ${cardsText} of your ${what}.`),
      el('span', { class: 'notice-buttons' }, button('Show them', () => conn.send({ type: 'peekAnswer', accept: true }), 'btn-primary btn-small'), button('Decline', () => conn.send({ type: 'peekAnswer', accept: false }), 'btn-ghost btn-small')),
    );
  }

  let peekClose: (() => void) | null = null;
  let peekKey = '';
  /** The cards you are being shown: top card first, to look at and nothing more. */
  function renderPeek(v: GameView) {
    if (watching) return;
    const key = v.peek ? `${v.peek.owner}|${v.peek.pile}|${v.peek.cards.map((c) => c.id).join(',')}` : '';
    if (key === peekKey) return;
    peekKey = key;
    if (peekClose) {
      const close = peekClose;
      peekClose = null;
      close();
    }
    window.clearTimeout(peekTimer);
    if (!v.peek) return;
    const { owner, pile, cards } = v.peek;
    const what = pile === 'deck' ? 'deck' : 'discard pile';
    const whose = owner === v.you ? 'your' : `${v.players[owner]?.name ?? 'your opponent'}’s`;
    // the cards lift off the pile on the table first (pileLook), then the window with them opens
    const L = CONFIG.pileLook;
    const wait = L.enabled && CONFIG.playEffects.enabled ? (L.windowDelay + Math.min(L.mostCards, cards.length) * L.stagger) * 1000 : 0;
    const open = () => {
      if (leaving || peekKey !== key) return;
      peekClose = showPeek();
    };
    const showPeek = () => modal(
      el(
        'div',
        { class: 'stack' },
        el('h2', {}, `The top ${cards.length === 1 ? 'card' : `${cards.length} cards`} of ${whose} ${what}`),
        el('p', { class: 'muted' }, `${cards.length > 1 ? 'The top card is first. ' : ''}Looking doesn’t move anything: the ${what} stays in the same order. Right-click (or click) a card to see it up close.`),
        el(
          'div',
          { class: 'card-grid card-grid-small search-grid' },
          cards.map((c, i) => {
            const def = CARD_DB.byNumber.get(c.num);
            const t = el(
              'button',
              { class: 'card-tile', type: 'button', title: 'Right-click to enlarge' },
              setImg(el('img', { class: 'card-thumb', alt: def?.name ?? 'Card', width: '500', height: '700' }), cardFrontSrc(c.num)),
              el('span', { class: 'tile-name' }, el('span', { class: 'tile-num' }, `${i + 1}.`), ` ${def?.name ?? `#${c.num}`}`),
            );
            const look = () => enlargeHeld(c, `${i === 0 ? 'Top card' : `Card ${i + 1} from the top`} of ${whose} ${what}`);
            t.addEventListener('click', look);
            t.addEventListener('contextmenu', (e) => {
              e.preventDefault();
              look();
            });
            return t;
          }),
        ),
        el('div', { class: 'row-end' }, button('Done', () => peekClose?.(), 'btn-primary')),
      ),
      {
        wide: cards.length > 3,
        onClose: () => {
          peekClose = null;
          conn.send({ type: 'peekClose' });
        },
      },
    );
    if (wait > 0) peekTimer = window.setTimeout(open, wait);
    else open();
  }
  let peekTimer = 0;

  // ---------------- cards a card effect turns up, shown to both players ----------------
  let showingClose: (() => void) | null = null;
  let showingKey = '';
  /**
   * Occult Sorcerer and the like: the cards turned up from a deck, with what will happen to each, in a window both
   * players see. Nothing happens to them until their player clicks Continue; the other player waits (or hides the
   * window). A bot's go on at once: the window is just for looking.
   */
  function renderShowing(v: GameView) {
    const sh = v.showing;
    const key = sh ? `${sh.id}|${sh.waiting}` : '';
    if (key === showingKey) return;
    const wasId = showingKey.split('|')[0];
    showingKey = key;
    // (a window already open for these cards stays open when they go from waiting to done, for whoever is still looking)
    if (sh && !sh.waiting && wasId === String(sh.id) && showingClose) return;
    if (showingClose) {
      const close = showingClose;
      showingClose = null;
      close();
    }
    if (!sh) return;
    const mine = sh.by === v.you && !watching;
    const who = mine ? 'You' : v.players[sh.by]?.name ?? 'Your opponent';
    const what = (to: string) => (to === 'hand' ? `To ${mine ? 'your' : 'their'} hand${sh.gain ? ` · +${sh.gain} Gerzite` : ''}` : to === 'discard' ? 'Discarded' : 'Stays on top of the deck');
    const pick = v.pick?.showing === sh.id ? v.pick : null;
    showingClose = modal(
      el(
        'div',
        { class: 'stack' },
        el('h2', {}, `${sh.name}: ${who} turn${mine ? '' : 's'} up ${sh.cards.length === 1 ? 'a card' : `${sh.cards.length} cards`}`),
        el('p', { class: 'muted' }, sh.waiting ? (mine ? 'Both players can see these. Click Continue when you have both had a look.' : `Waiting for ${who} to continue…`) : 'Right-click (or click) a card to see it up close.'),
        el(
          'div',
          { class: 'card-grid card-grid-small search-grid' },
          sh.cards.map((c) => {
            const def = CARD_DB.byNumber.get(c.num);
            const t = el(
              'button',
              { class: 'card-tile', type: 'button', title: 'Right-click to enlarge' },
              setImg(el('img', { class: 'card-thumb', alt: def?.name ?? 'Card', width: '500', height: '700' }), cardFrontSrc(c.num)),
              el('span', { class: 'tile-name' }, def?.name ?? `#${c.num}`),
              el('span', { class: `tile-note ${c.to === 'hand' ? 'tile-good' : c.to === 'discard' ? 'tile-bad' : ''}` }, what(c.to)),
            );
            const look = () => showCardLarge(cardFrontSrc(c.num), def ? cardLabel(def) : 'Card', what(c.to), [], { rules: cardRulesNode(def) });
            t.addEventListener('click', look);
            t.addEventListener('contextmenu', (e) => {
              e.preventDefault();
              look();
            });
            return t;
          }),
        ),
        el('div', { class: 'row-end' }, pick ? button('Continue', () => conn.send({ type: 'pick', pick: pick.id, choice: 1 }), 'btn-primary') : button(sh.waiting ? 'Hide' : 'OK', () => showingClose?.(), sh.waiting ? 'btn-ghost' : 'btn-primary')),
      ),
      { wide: sh.cards.length > 3, dismissable: !pick, onClose: () => { showingClose = null; } },
    );
  }

  // ---------------- an Effect Draw with a full hand ----------------
  let overflowClose: (() => void) | null = null;
  // a choice a card effect is waiting for: a banner says what is being chosen (the cards are ringed on the table),
  // or, for a yes / no question, asks it outright
  let pickId = 0;
  let pickClose: (() => void) | null = null;
  const pickBanner = el('div', { class: 'pick-banner', hidden: true });
  document.body.append(pickBanner);
  let splitAmount = 1; // split damage (Dividend): how many points each click puts on a card
  function renderPick(v: GameView) {
    if (watching) return;
    const k = v.pick;
    if ((k?.id ?? 0) === pickId) return;
    pickId = k?.id ?? 0;
    table.previewLanes(null); // (a lane choice that was being pointed at has gone)
    pickBanner.replaceChildren();
    pickBanner.hidden = !k;
    table.setPick(k && !k.yesNo && !k.cards && !k.choices ? { id: k.id, options: k.options } : null);
    if (pickClose) {
      const c = pickClose;
      pickClose = null;
      c();
    }
    if (!k) return;
    if (k.showing !== undefined) { pickBanner.hidden = true; return; } // (the cards turned up have a window of their own: renderShowing)
    closeFloatMenu();
    if (k.arrange && k.cards) {
      // Ephemeral Gaze: put the cards back in the order you like (top first); an Undead one may be discarded instead
      pickBanner.hidden = true;
      const order = k.cards.map((c) => c.id);
      const drop = new Set<string>();
      const list = el('div', { class: 'arrange-list' });
      const draw = () => {
        list.replaceChildren(...order.map((id, i) => {
          const c = k.cards!.find((x) => x.id === id)!;
          const d = CARD_DB.byNumber.get(c.num);
          const thumb = setImgLazy(el('img', { class: 'card-thumb', alt: d?.name ?? 'Card', width: '500', height: '700' }), () => cardFrontSrc(c.num));
          thumb.addEventListener('contextmenu', (ev) => { ev.preventDefault(); enlargeHeld({ ...c, spawned: false }, 'Top of their deck'); });
          const undead = k.undead?.includes(id);
          return el('div', { class: `arrange-row ${drop.has(id) ? 'dropped' : ''}` },
            el('span', { class: 'arrange-pos' }, drop.has(id) ? '—' : String(order.filter((x) => !drop.has(x)).indexOf(id) + 1)),
            thumb,
            el('span', { class: 'arrange-name' }, d?.name ?? 'Card'),
            el('button', { class: 'btn', type: 'button', disabled: i === 0, onclick: () => { [order[i - 1], order[i]] = [order[i], order[i - 1]]; draw(); } }, '▲'),
            el('button', { class: 'btn', type: 'button', disabled: i === order.length - 1, onclick: () => { [order[i + 1], order[i]] = [order[i], order[i + 1]]; draw(); } }, '▼'),
            ...(undead ? [el('button', { class: `btn ${drop.has(id) ? 'primary' : ''}`, type: 'button', onclick: () => { if (drop.has(id)) drop.delete(id); else drop.add(id); draw(); } }, drop.has(id) ? 'Discarded (1 damage to your Emperor)' : 'Discard it (1 damage to your Emperor)')] : []),
          );
        }));
      };
      draw();
      pickClose = modal(
        el('div', { class: 'stack' },
          el('h2', {}, k.text),
          el('p', { class: 'muted' }, 'The first card goes on top. Right-click a card to view it.'),
          list,
          el('div', { class: 'row-end' }, button('Put them back', () => conn.send({ type: 'pick', pick: k.id, order: [...order], drop: [...drop] }), 'primary')),
        ),
        { wide: true, dismissable: false },
      );
      return;
    }
    if (k.cards) {
      // a search: the cards that may be taken, to pick one from (or none)
      pickBanner.hidden = true;
      const take = (card?: string) => conn.send({ type: 'pick', pick: k.id, ...(card ? { card } : {}) });
      pickClose = modal(
        el(
          'div',
          { class: 'stack' },
          el('h2', {}, k.text),
          el('p', { class: 'muted' }, 'Left-click a card to choose it; right-click to view it.'),
          el(
            'div',
            { class: 'card-grid card-grid-small search-grid' },
            ...k.cards.map((c) => {
              const d = CARD_DB.byNumber.get(c.num);
              const t = el('button', { class: 'card-tile', type: 'button', title: d?.name ?? 'Card' }, setImgLazy(el('img', { class: 'card-thumb', alt: d?.name ?? 'Card', width: '500', height: '700' }), () => cardFrontSrc(c.num)));
              t.addEventListener('click', () => take(c.id));
              t.addEventListener('contextmenu', (ev) => { ev.preventDefault(); enlargeHeld({ ...c, spawned: false }, 'In the pile'); });
              return t;
            }),
          ),
          ...(k.optional ? [el('div', { class: 'row-end' }, button(k.skip ?? 'Take nothing', () => take()))] : []),
        ),
        { wide: true, dismissable: false },
      );
      return;
    }
    const answer = (yes?: boolean) => conn.send({ type: 'pick', pick: k.id, ...(yes === undefined ? {} : { yes }) });
    if (k.numbers) {
      // numbers to set (Dark Gerzite Titan's payment, Volcanic Disciple's boost): a stepper for each, then confirm
      const vals = k.numbers.map((n) => n.value);
      const rows = k.numbers.map((n, i) => {
        const out = el('span', { class: 'pick-num' }, String(vals[i]));
        const step = (d: number) => { vals[i] = Math.max(n.min, Math.min(n.max, vals[i] + d)); out.textContent = String(vals[i]); };
        return el('span', { class: 'pick-stepper' }, el('span', { class: 'pick-num-label' }, `${n.label} (0–${n.max})`), el('button', { class: 'btn', type: 'button', onclick: () => step(-1) }, '−'), out, el('button', { class: 'btn', type: 'button', onclick: () => step(1) }, '+'));
      });
      pickBanner.append(
        el('span', { class: 'pick-text' }, `${k.text}:`),
        ...rows,
        el('button', { class: 'btn primary', type: 'button', onclick: () => conn.send({ type: 'pick', pick: k.id, numbers: [...vals] }) }, k.confirm ?? 'OK'),
      );
      return;
    }
    const perClick: HTMLElement[] = [];
    if (k.left !== undefined) {
      // split damage: how many points a click deals (1 a click, or all of it at once)
      const max = k.left;
      splitAmount = Math.max(1, Math.min(splitAmount, max));
      const out = el('span', { class: 'pick-num' }, String(splitAmount));
      const step = (d: number) => { splitAmount = Math.max(1, Math.min(max, splitAmount + d)); out.textContent = String(splitAmount); };
      perClick.push(el('span', { class: 'pick-stepper' }, el('span', { class: 'pick-num-label' }, 'Per click'), el('button', { class: 'btn', type: 'button', onclick: () => step(-1) }, '−'), out, el('button', { class: 'btn', type: 'button', onclick: () => step(1) }, '+'), el('button', { class: 'btn', type: 'button', onclick: () => step(max) }, 'All')));
    }
    pickBanner.append(
      el('span', { class: 'pick-text' }, k.yesNo || k.left !== undefined ? k.text : k.choices ? `${k.text}:` : `${k.text}. Click one of the ringed cards.`),
      ...perClick,
      ...(k.choices ?? []).map((label, i) => {
        const b = el('button', { class: 'btn', type: 'button', onclick: () => { table.previewLanes(null); conn.send({ type: 'pick', pick: k.id, choice: i + 1 }); } }, label);
        // a choice of lanes: pointing at one lights those lanes up on the table (the far side is mirrored, so "Lanes 1
        // to 3" there is hard to read otherwise)
        const hint = k.laneHints?.[i];
        if (hint?.length) {
          b.addEventListener('pointerenter', () => table.previewLanes(hint));
          b.addEventListener('pointerleave', () => table.previewLanes(null));
          b.addEventListener('focus', () => table.previewLanes(hint));
          b.addEventListener('blur', () => table.previewLanes(null));
        }
        return b;
      }),
      ...(k.yesNo ? [el('button', { class: 'btn primary', type: 'button', onclick: () => answer(true) }, 'Yes'), el('button', { class: 'btn', type: 'button', onclick: () => answer(false) }, 'No')] : []),
      ...(k.optional ? [el('button', { class: 'btn', type: 'button', onclick: () => answer() }, k.skip ?? 'Skip')] : []),
    );
  }
  // ---------------- Dark Bargain: an amount both players lose, which either can change and both must agree to ----------------
  const bargainBanner = el('div', { class: 'pick-banner bargain-banner', hidden: true });
  document.body.append(bargainBanner);
  let bargainKey = '';
  function renderBargain(v: GameView) {
    const b = v.bargain;
    const key = b ? `${b.value}:${b.max}:${b.agreed.join(',')}` : '';
    if (key === bargainKey) return;
    bargainKey = key;
    bargainBanner.replaceChildren();
    bargainBanner.hidden = !b || watching;
    if (!b || watching) return;
    const mine = b.agreed.includes(v.you);
    const them = v.players.filter((_, i) => i !== v.you && b.agreed.includes(i as Seat)).length > 0;
    bargainBanner.append(
      el('span', { class: 'pick-text' }, `${b.by}: both players lose`),
      el('span', { class: 'pick-stepper' },
        el('button', { class: 'btn', type: 'button', disabled: b.value <= 1, onclick: () => conn.send({ type: 'bargain', delta: -1 }) }, '−'),
        el('span', { class: 'pick-num' }, String(b.value)),
        el('button', { class: 'btn', type: 'button', disabled: b.value >= b.max, onclick: () => conn.send({ type: 'bargain', delta: 1 }) }, '+')),
      el('span', { class: 'pick-num-label' }, `Gerzite (up to ${b.max})${them ? ' · your opponent has agreed' : ''}`),
      el('button', { class: 'btn primary', type: 'button', disabled: mine, onclick: () => conn.send({ type: 'bargain', deal: true }) }, mine ? 'Waiting…' : 'Deal'),
    );
  }

  let overflowId = '';
  /** You drew a card that can't stay in your hand: discard it for Gerzite, or bottom of the deck for a Fractal. */
  function renderOverflow(v: GameView) {
    if (watching) return;
    const id = v.overflow?.id ?? '';
    if (id === overflowId) return;
    overflowId = id;
    if (overflowClose) {
      const close = overflowClose;
      overflowClose = null;
      close();
    }
    const card = v.overflow;
    if (!card) return;
    closeFloatMenu();
    const def = CARD_DB.byNumber.get(card.num);
    const O = CONFIG.turn.effectDrawOverflow;
    const img = setImg(el('img', { class: 'overflow-card', alt: def?.name ?? 'Card', width: '500', height: '700', title: 'Click to enlarge' }), cardFrontSrc(card.num));
    img.addEventListener('click', () => enlargeHeld(card, 'Drawn with a full hand'));
    overflowClose = modal(
      el(
        'div',
        { class: 'stack overflow-choice' },
        el('h2', {}, 'Your hand is full'),
        el('p', { class: 'muted' }, `You drew ${def?.name ?? 'a card'} with ${CONFIG.handLimit} cards in your hand, so it can’t stay there. Choose where it goes.`),
        img,
        el(
          'div',
          { class: 'row-end' },
          button(`Discard it · gain ${O.discardGerzite} Gerzite`, () => conn.send({ type: 'overflowChoice', to: 'discard' }), 'btn-primary'),
          button(`Bottom of your deck · gain ${O.bottomFractals} Fractal${O.bottomFractals === 1 ? '' : 's'}`, () => conn.send({ type: 'overflowChoice', to: 'bottom' }), 'btn-primary'),
        ),
      ),
      { dismissable: false },
    );
  }

  let spanClose: (() => void) | null = null;
  let spanKey = '';
  /** Your Lanespanner has its middle unit down: choose which lanes the rest of its units go in. */
  function renderSpan(v: GameView) {
    if (watching) return;
    const key = v.span ? `${v.span.card}|${v.span.choices.length}` : '';
    if (key === spanKey) return;
    spanKey = key;
    if (spanClose) {
      const close = spanClose;
      spanClose = null;
      close();
    }
    const span = v.span;
    if (!span) return;
    closeFloatMenu();
    const def = CARD_DB.byNumber.get(span.num);
    const middle = v.field.find((c) => c.id === span.card);
    const here = middle ? slotAt(middle)?.slot.name : undefined;
    const lanes = (names: string[]) => names.map((n) => (n === here ? `${n.replace(/^Lane\s*/, '')} (this one)` : n.replace(/^Lane\s*/, ''))).join(' · ');
    spanClose = modal(
      el(
        'div',
        { class: 'stack overflow-choice' },
        el('h2', {}, `Spread ${def?.name ?? 'the Lanespanner'}`),
        el('p', { class: 'muted' }, `${def?.name ?? 'It'} is ${def?.count ?? 'several'} units, one in each lane. The one you played is in ${here ?? 'its lane'}. Choose which lanes the rest go in (they may share a lane with another unit).`),
        el('div', { class: 'stack' }, ...span.choices.map((names, i) => button(`Lanes ${lanes(names)}`, () => conn.send({ type: 'spanPlace', start: laneStart(names[0]) }), i === 0 ? 'btn-primary' : ''))),
      ),
      { dismissable: false },
    );
  }
  /** Lane 1 = 0, Lane 2 = 1… (left to right as their owner sees them). */
  function laneStart(name: string) {
    return laneOrder().findIndex((l) => l.name === name);
  }

  function askWhere(pile: PileName, x: number, y: number): Promise<Where | null> {
    return new Promise((resolve) => {
      let answered = false;
      const pick = (w: Where | null) => {
        if (answered) return;
        answered = true;
        resolve(w);
      };
      floatingMenu(
        x,
        y,
        pile === 'deck' ? 'Put it in your deck (face-down)' : 'Put it in your discard pile',
        [
          { label: 'On top', onClick: () => pick('top') },
          { label: 'On the bottom', onClick: () => pick('bottom') },
          { label: 'In the middle', onClick: () => pick('middle') },
          { label: 'In any order…', onClick: () => void choosePlace(pile).then(pick) },
        ],
        () => pick(null),
      );
    });
  }

  /**
   * "In any order": choose the exact spot in the pile. Your deck shows only face-down cards (you don't learn
   * what they are or where any card is); your discard pile is face-up anyway. Closing it puts nothing anywhere
   * and doesn't shuffle.
   */
  function choosePlace(pile: PileName): Promise<Where | null> {
    return new Promise((resolve) => {
      const v = view;
      if (!v) return resolve(null);
      const me = v.players[v.you];
      const faces: (HeldCard | null)[] = pile === 'deck' ? Array.from({ length: me.deckCount }, () => null) : me.discard;
      let done = false;
      const finish = (w: Where | null) => {
        if (done) return;
        done = true;
        close();
        resolve(w);
      };
      const n = faces.length;
      const slot = (i: number) =>
        el('button', { class: 'place-slot', type: 'button', title: i === 0 ? 'On top' : i === n ? 'On the bottom' : `${i} card${i === 1 ? '' : 's'} above it`, 'aria-label': `Put it at spot ${i + 1}`, onclick: () => finish(i === 0 ? 'top' : i === n ? 'bottom' : i) });
      const row: Node[] = [el('span', { class: 'place-end' }, 'Top')];
      faces.forEach((c, i) => {
        row.push(slot(i));
        const img = el('img', { class: 'place-card', alt: c ? (CARD_DB.byNumber.get(c.num)?.name ?? 'card') : 'face-down card' }) as HTMLImageElement;
        setImg(img, c ? cardFrontSrc(c.num) : cardBackSrc());
        row.push(img);
      });
      row.push(slot(n), el('span', { class: 'place-end' }, 'Bottom'));
      const input = el('input', { type: 'number', min: '1', max: String(n + 1), value: String(Math.ceil((n + 1) / 2)), class: 'place-num' }) as HTMLInputElement;
      const go = () => {
        const k = Math.max(1, Math.min(n + 1, Math.round(Number(input.value) || 1))) - 1;
        finish(k === 0 ? 'top' : k === n ? 'bottom' : k);
      };
      input.addEventListener('keydown', (e) => { if (e.key === 'Enter') go(); });
      const close = modal(
        el(
          'div',
          { class: 'stack place-picker' },
          el('h2', {}, pile === 'deck' ? 'Put it anywhere in your deck' : 'Put it anywhere in your discard pile'),
          el('p', { class: 'muted' }, pile === 'deck'
            ? `Your deck has ${n} card${n === 1 ? '' : 's'}, face-down: you don't see what they are. Click a gap to put the card there (top on the left). Closing this puts it nowhere and doesn't shuffle your deck.`
            : `Click a gap to put the card there (top on the left). Closing this puts it nowhere.`),
          el('div', { class: 'place-row' }, row),
          el('div', { class: 'row' }, el('label', {}, 'Or spot number (1 = top, ', String(n + 1), ' = bottom): ', input), button('Put it there', go, 'btn-primary'), button('Cancel', () => finish(null))),
        ),
        { wide: true, onClose: () => finish(null) },
      );
    });
  }

  /**
   * A cost that is settled as the card is played. Three kinds of question, and a card can need more than one:
   *   - two costs to choose from (Forgotten Monarch: 3 Gerzite for 16 health, or 7 for 20)
   *   - a number to give (Gerzite Dagger: how much Gerzite; Bloodburst: how much health was removed)
   *   - a Unit to pick from your hand (Tribunal Trigger: that Unit's cost + 1)
   */
  function askCost(cardId: string, def: CardDef, x: number, y: number, how: { free: boolean; dark: boolean; minion?: boolean }): Promise<{ form?: number; choice?: number; unit?: string; spread?: boolean } | null> {
    return new Promise((resolve) => {
      const v = view;
      if (!v) return resolve(null);
      let answered = false;
      const done = (a: { form?: number; choice?: number; unit?: string; spread?: boolean } | null) => {
        if (answered) return;
        answered = true;
        closeFloatMenu();
        resolve(a);
      };
      const me = v.players[v.you];
      const times = how.dark ? darkgaleMultiplier(def) : 1;
      const f = def.costFormula;
      const usesDark = how.dark || def.costDark || (!!f?.uses.includes('last_card') && v.costFacts.lastDark);
      const coin = usesDark ? 'Dark Gerzite' : 'Gerzite';
      const have = usesDark ? me.darkGerzite : me.gerzite;
      const pays = CONFIG.turn.payToPlay && !how.free;
      const price = (n: number) => (pays ? `${n * times} ${coin}` : 'free');
      // --- several ways to summon it (two costs, or one cost and two shapes): one button each. A shape of several
      // units that may be a Lanespanner ("Ground/Lanespanner") gets two: all in one lane, or spread across lanes.
      if (def.forms) {
        const mode = spanMode(def, CONFIG.tokenRules.lanespanTrait);
        const ways = def.forms.flatMap((form, k) => {
          const many = (form.count ?? 1) > 1;
          const spreads = many && mode !== 'never' ? (mode === 'always' ? [true] : [false, true]) : [false];
          return spreads.map((spread) => ({ form, k, spread, many }));
        });
        floatingMenu(
          x,
          y,
          `${def.name}: summon it as…`,
          [
            ...ways.map(({ form, k, spread, many }) => ({
              label: `${price(form.cost)} · ${formWords(form)}${spread ? ` · across ${form.count} lanes` : many ? ' · in one lane' : ''}${spread && how.minion ? ' (not from the Minion slot)' : ''}`,
              disabled: (pays && have < form.cost * times) || (spread && !!how.minion), // a Lanespanner can't go in the Minion slot
              onClick: () => done(spread ? { form: k, spread: true } : { form: k }),
            })),
            el('div', { class: 'float-note' }, pays ? `You have ${have} ${coin}.${how.dark ? ' This is a Darkgale summon.' : ''}` : 'Played for free: choose which way it comes in.'),
          ],
          () => done(null),
        );
        return;
      }
      if (!f) return resolve({});
      // --- a Unit from your hand: its cost sets this card's cost
      const pickUnit = (then: (unit: string | undefined, unitCost: number | null) => void) => {
        if (!f.uses.includes('unit')) return then(undefined, null);
        const units = v.hand.filter((c) => c.id !== cardId).map((c) => ({ c, d: CARD_DB.byNumber.get(c.num) })).filter(({ d }) => d && (d.types.includes('troop') || d.types.includes('building')));
        floatingMenu(
          x,
          y,
          `${def.name}: which Unit from your hand?`,
          units.length
            ? [
                el('div', { class: 'float-note' }, `Its cost sets this card’s cost (${describeFormula(f)}). Play that Unit yourself afterwards.`),
                ...units.map(({ c, d }) => {
                  const total = priceOf(f, v.costFacts, 0, Math.max(0, d!.cost ?? 0));
                  const n = typeof total === 'number' ? total : 0;
                  return { label: `${d!.name} (cost ${d!.cost ?? 0}) → ${price(n)}`, disabled: !!f.choice ? false : pays && have < n * times, onClick: () => then(c.id, Math.max(0, d!.cost ?? 0)) };
                }),
              ]
            : [el('div', { class: 'float-note' }, 'There is no Troop or Building in your hand to pick.')],
          () => done(null),
        );
      };
      pickUnit((unit, unitCost) => {
        if (!f.choice) return done(unit ? { unit } : {});
        // --- a number: − and +, with the cost it comes to on the button
        let n = 1;
        const shown = el('span', { class: 'counter-value' }, String(n));
        const go = el('button', { class: 'float-item peek-go', type: 'button' });
        const refresh = () => {
          shown.textContent = String(n);
          const total = priceOf(f, v.costFacts, n, unitCost);
          const cost = typeof total === 'number' ? total * times : 0;
          go.disabled = typeof total === 'string' || (pays && have < cost);
          go.textContent = typeof total === 'string' ? total : !pays ? 'Play it for free' : have < cost ? `Costs ${cost} ${coin}: you have ${have}` : `Play it for ${cost} ${coin}`;
        };
        const step = (d: number) => {
          n = Math.max(0, Math.min(99, n + d));
          refresh();
        };
        go.addEventListener('click', () => done({ choice: n, ...(unit ? { unit } : {}) }));
        refresh();
        floatingMenu(
          x,
          y,
          `${def.name}: ${wordsOf(f.choice)}?`,
          [
            el('div', { class: 'float-note' }, `Cost: ${describeFormula(f)}.`),
            el('div', { class: 'counter-row' }, button('−', () => step(-1), 'btn-small', { 'aria-label': 'One fewer' }), shown, button('+', () => step(1), 'btn-small', { 'aria-label': 'One more' })),
            go,
          ],
          () => done(null),
        );
      });
    });
  }

  // ---------------- searching a pile ----------------
  let searchClose: (() => void) | null = null;
  let searchGrid: HTMLElement | null = null;
  let searchTitle: HTMLElement | null = null;
  function renderSearch(v: GameView) {
    if (watching) return; // a replay doesn't open the window a player had open
    if (!v.search) {
      if (searchClose) {
        const c = searchClose;
        searchClose = null;
        c();
      }
      return;
    }
    const pileName = v.search.pile === 'deck' ? 'deck' : 'discard pile';
    if (!searchClose) {
      searchGrid = el('div', { class: 'card-grid card-grid-small search-grid' });
      searchTitle = el('h2', {});
      const note = `Cards are ordered top of ${pileName} to bottom. Left-click to take a card; right-click to view a card.${v.search.pile === 'deck' ? ' Closing this shuffles your deck.' : ''}`;
      searchClose = modal(
        el(
          'div',
          { class: 'stack' },
          searchTitle,
          el('p', { class: 'muted' }, note),
          searchGrid,
          el('div', { class: 'row-end' }, button(v.search.pile === 'deck' ? 'Done — shuffle deck' : 'Done', () => searchClose?.(), 'btn-primary')),
        ),
        {
          wide: true,
          onClose: () => {
            searchClose = null;
            conn.send({ type: 'searchEnd' });
          },
        },
      );
    }
    searchTitle!.textContent = `Searching your ${pileName} · ${v.search.cards.length} card${v.search.cards.length === 1 ? '' : 's'}`;
    searchGrid!.replaceChildren(
      ...v.search.cards.map((c) => {
        const def = CARD_DB.byNumber.get(c.num);
        const t = el(
          'button',
          { class: 'card-tile', type: 'button', title: 'Left-click to take · right-click to view · hold R and click to reveal it to your opponent' },
          setImgLazy(el('img', { class: 'card-thumb', alt: def?.name ?? 'Card', width: '500', height: '700' }), () => cardFrontSrc(c.num)),
          el('span', { class: 'tile-name' }, def?.name ?? `#${c.num}`),
        );
        t.addEventListener('click', () => { if (!revealClick(c.id)) confirmTake(c, pileName); });
        t.addEventListener('contextmenu', (e) => {
          e.preventDefault();
          enlargeHeld(c, `In your ${pileName}`);
        });
        return t;
      }),
    );
    if (!v.search.cards.length) searchGrid!.append(el('p', { class: 'empty-note' }, 'This pile is empty.'));
  }

  /** "Take Hexed Mummy?" — a click in the search window asks first, in case it landed on the wrong card. */
  function confirmTake(c: HeldCard, pileName: string) {
    if (view && view.hand.length >= CONFIG.handLimit) {
      toast(`Your hand is full (${CONFIG.handLimit} cards).`, 'info');
      return;
    }
    const def = CARD_DB.byNumber.get(c.num);
    const name = def?.name ?? `Card ${c.num}`;
    let close = () => {};
    const take = button('Take It', () => {
      close();
      conn.send({ type: 'searchTake', id: c.id });
    }, 'btn-primary');
    close = modal(
      el(
        'div',
        { class: 'stack' },
        el('h2', {}, `Take ${name}?`),
        el(
          'div',
          { class: 'take-row' },
          setImg(el('img', { class: 'take-thumb', alt: name, width: '500', height: '700' }), cardFrontSrc(c.num)),
          el('p', { class: 'muted' }, `It goes from your ${pileName} into your hand.`),
        ),
        el('div', { class: 'row-end' }, take, button('Cancel', () => close(), 'btn-ghost')),
      ),
    );
    take.focus();
  }

  // ---------------- spawn drawer (right) ----------------
  let spawnOpen = false;
  const spawnBody = el('div', { class: 'spawn-body' });
  const tabTokens = el('button', { class: 'tab', type: 'button', role: 'tab', 'aria-selected': 'true' }, 'Counters');
  const tabCards = el('button', { class: 'tab', type: 'button', role: 'tab', 'aria-selected': 'false' }, 'Summon');
  const drawer = el(
    'aside',
    { class: 'spawn', 'aria-label': 'Counters and Summon menu', hidden: true },
    el(
      'div',
      { class: 'spawn-head' },
      el('div', { class: 'tabs', role: 'tablist' }, tabTokens, tabCards),
      button('×', () => toggleSpawn(false), 'btn-ghost btn-icon', { 'aria-label': 'Close the Counters and Summon menu' }),
    ),
    spawnBody,
  );
  tabTokens.addEventListener('click', () => showSpawnTab('tokens'));
  tabCards.addEventListener('click', () => showSpawnTab('cards'));

  /**
   * Right-click on a counter or token in the menu: bring in several at once. A stepper for how many, and,
   * for a counter, one for the number each of them starts at ("3 Damage Counters, each at 2").
   */
  function spawnMany(t: (typeof TOKEN_DB.list)[number], x: number, y: number) {
    const most = CONFIG.tokenRules.spawnManyMax;
    let count = 2;
    let value = Math.max(0, t.start);
    let turns = 0; // a turn clock on each one (0 = none)
    const clock = canClock(t);
    const stepper = (label: string, get: () => number, set: (n: number) => void, least: number, top: number) => {
      const shown = el('span', { class: 'counter-value' }, String(get()));
      const change = (d: number) => {
        set(Math.max(least, Math.min(top, get() + d)));
        shown.textContent = String(get());
        refresh();
      };
      return [
        el('div', { class: 'float-note' }, label),
        el(
          'div',
          { class: 'counter-row' },
          el('button', { class: 'btn counter-btn', type: 'button', onclick: () => change(-1), 'aria-label': 'One fewer' }, '−'),
          shown,
          el('button', { class: 'btn counter-btn', type: 'button', onclick: () => change(1), 'aria-label': 'One more' }, '+'),
        ),
      ];
    };
    const go = el('button', { class: 'float-item peek-go', type: 'button' });
    // Gerzite and Dark Gerzite: straight onto your bar, or onto the table as counters (an amount, and a turn clock)
    const bar = !!t.bar;
    const toBar = el('button', { class: 'float-item peek-go', type: 'button' });
    toBar.addEventListener('click', () => {
      closeFloatMenu();
      conn.send({ type: 'spawnToken', tokenId: t.id, count, toBar: true });
    });
    if (bar) value = 1;
    const refresh = () => {
      if (bar) {
        toBar.textContent = `Put ${count} ${t.name} on my bar`;
        go.textContent = `Bring in ${count} ${t.name} counter${count === 1 ? '' : 's'} of ${value}${turns > 0 ? ` for ${turns} turn${turns === 1 ? '' : 's'}` : ''}`;
        return;
      }
      go.textContent = `Bring in ${count} × ${t.name}${t.counter ? `, each at ${value}` : ''}${turns > 0 ? ` for ${turns} turn${turns === 1 ? '' : 's'}` : ''}`;
    };
    go.addEventListener('click', () => {
      closeFloatMenu();
      conn.send({ type: 'spawnToken', tokenId: t.id, count, ...(t.counter || bar ? { value } : {}), ...((clock || bar) && turns > 0 ? { turns } : {}), ...lookingAt() });
    });
    refresh();
    if (bar) {
      floatingMenu(x, y, t.name, [
        ...stepper(`How many? (up to ${most})`, () => count, (n) => (count = n), 1, most),
        toBar,
        el('div', { class: 'float-note' }, `Or bring them onto the table as counters. A ${t.name} counter can add to or take from your bar as each of your turns begins or ends (click it to set that up).`),
        ...stepper('The amount each counter holds', () => value, (n) => (value = n), 1, 999),
        ...(CONFIG.turn.durations.enabled ? stepper('Turns each one lasts (0 = until removed)', () => turns, (n) => (turns = n), 0, CONFIG.turn.durations.max) : []),
        go,
      ]);
      return;
    }
    floatingMenu(x, y, t.name, [
      ...stepper(`How many? (up to ${most})`, () => count, (n) => (count = n), 1, most),
      ...(t.counter && t.category !== 'dice' ? stepper('The number each one starts at', () => value, (n) => (value = n), 0, 999) : []),
      ...(clock ? stepper('Turns each one lasts (0 = until removed)', () => turns, (n) => (turns = n), 0, CONFIG.turn.durations.max) : []),
      go,
    ]);
  }

  /** Where you are looking on the table right now: what you bring in appears near there. */
  function lookingAt(): { at?: { x: number; z: number } } {
    const at = table.lookPoint(handBar.getBoundingClientRect().top);
    return at ? { at: { x: Math.round(at.x * 100) / 100, z: Math.round(at.z * 100) / 100 } } : {};
  }

  function toggleSpawn(force?: boolean) {
    spawnOpen = force ?? !spawnOpen;
    drawer.hidden = !spawnOpen;
    spawnBtn.setAttribute('aria-pressed', String(spawnOpen));
    if (spawnOpen && !spawnBody.childElementCount) showSpawnTab('tokens');
  }

  let cardQuery = '';
  let tokenQuery = '';
  function showSpawnTab(tab: 'tokens' | 'cards') {
    tabTokens.setAttribute('aria-selected', String(tab === 'tokens'));
    tabCards.setAttribute('aria-selected', String(tab === 'cards'));
    if (tab === 'tokens') {
      // a search box, as on the Cards tab: type part of a name ("burn"), or a kind ("status", "counter")
      const input = el('input', { id: 'token-search', class: 'text-input', type: 'search', placeholder: 'Search tokens and counters', value: tokenQuery });
      const results = el('div', { class: 'spawn-results' });
      const known = new Set<string>(TOKEN_CATEGORIES);
      const categories = [...TOKEN_CATEGORIES, ...new Set(TOKEN_DB.list.map((t) => t.category).filter((c) => !known.has(c)))];
      const render = () => {
        const words = tokenQuery.toLowerCase().split(/\s+/).filter(Boolean);
        const matches = (t: (typeof TOKEN_DB.list)[number]) => {
          const hay = `${t.name} ${t.id} ${t.category} ${t.effect ?? ''} ${t.counter ? 'counter' : ''}`.toLowerCase();
          return words.every((w) => hay.includes(w));
        };
        const sections = categories.flatMap((cat) => {
          // in each group, A to Z by name; the plain Misc Token and Misc Counter first in theirs
          const pinned = (t: (typeof TOKEN_DB.list)[number]) => (t.id.startsWith('misc-') ? 0 : 1);
          const list = TOKEN_DB.list.filter((t) => t.category === cat && matches(t)).sort((a, b) => pinned(a) - pinned(b) || a.name.localeCompare(b.name, undefined, { numeric: true }));
          if (!list.length) return [];
          return [
            el(
              'section',
              { class: 'spawn-section' },
              el('h3', {}, titleCase(cat)),
              el(
                'div',
                { class: 'token-grid' },
                list.map((t) =>
                  el(
                    'button',
                    {
                      class: 'token-tile',
                      type: 'button',
                      title: t.bar ? `Click: a ${t.name} counter on the table (drag it onto your bar to use it as ${t.name}) · right-click: straight onto your bar, or several at once` : 'Click for one · right-click for several at once',
                      onclick: () => conn.send({ type: 'spawnToken', tokenId: t.id, ...lookingAt() }),
                      oncontextmenu: (e: MouseEvent) => {
                        e.preventDefault();
                        spawnMany(t, e.clientX, e.clientY);
                      },
                    },
                    setImg(el('img', { alt: '', class: t.shape === 'cutout' ? 'cutout' : '' }), tokenSrc(t.id)),
                    el('span', {}, t.name),
                    t.counter ? el('span', { class: 'tag' }, 'counter') : '',
                  ),
                ),
              ),
            ),
          ];
        });
        results.replaceChildren(...sections);
        if (!sections.length) results.append(el('p', { class: 'empty-note' }, 'No tokens match.'));
      };
      input.addEventListener('input', () => {
        tokenQuery = input.value.trim();
        render();
      });
      // Enter with one match left spawns it, so "burn" + Enter is all it takes
      input.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter') return;
        const tiles = results.querySelectorAll<HTMLButtonElement>('.token-tile');
        if (tiles.length === 1) tiles[0].click();
      });
      spawnBody.replaceChildren(el('p', { class: 'muted small' }, 'Right-click one to bring in several at once, and to choose the number a counter starts at.'), input, results);
      render();
    } else {
      const input = el('input', { id: 'spawn-search', class: 'text-input', type: 'search', placeholder: 'Search name or #number', value: cardQuery });
      const results = el('div', { class: 'spawn-results' });
      const tile = (c: CardDef) => {
        const t = el(
          'button',
          { class: 'card-tile', type: 'button', title: 'Click to spawn on your side · right-click to enlarge' },
          setImgLazy(el('img', { class: 'card-thumb', alt: c.name, width: '500', height: '700' }), () => cardFrontSrc(c.number)),
          el('span', { class: 'tile-name' }, c.spawnOnly ? '' : el('span', { class: 'tile-num' }, cardNo(c)), c.spawnOnly ? c.name : ` ${c.name}`),
        );
        t.addEventListener('click', () => conn.send({ type: 'spawnCard', num: c.number, ...lookingAt() }));
        t.addEventListener('contextmenu', (e) => {
          e.preventDefault();
          showCardLarge(cardFrontSrc(c.number), cardLabel(c), '', [], { rules: cardRulesNode(c) });
        });
        return t;
      };
      const render = () => {
        const q = cardQuery.toLowerCase().replace('#', '');
        // summoned units have no set number: they are found by name, and listed first
        const only = CARD_DB.spawnOnly.filter((c) => !q || c.name.toLowerCase().includes(q));
        const list = CARD_DB.list.filter((c) => !q || c.name.toLowerCase().includes(q) || String(c.number) === q).slice(0, 80);
        const grid = (cards: CardDef[]) => el('div', { class: 'card-grid card-grid-small' }, cards.map(tile));
        results.replaceChildren(
          ...(only.length ? [el('section', { class: 'spawn-section' }, el('h3', {}, 'Summoned units · spawn only'), grid(only))] : []),
          ...(list.length ? [el('section', { class: 'spawn-section' }, CARD_DB.spawnOnly.length ? el('h3', {}, 'Cards of the set') : null, grid(list))] : []),
        );
        if (!list.length && !only.length) results.append(el('p', { class: 'empty-note' }, 'No cards match.'));
      };
      input.addEventListener('input', () => {
        cardQuery = input.value.trim();
        render();
      });
      spawnBody.replaceChildren(el('p', { class: 'muted small' }, `Spawned cards appear on your side of the table: drag one into a slot its type allows. They can be moved by both players and deleted from their right-click view.${CARD_DB.spawnOnly.length ? ' Summoned units are cards that other cards bring in: they are found only here, never in the deck builder.' : ''}`), input, results);
      render();
      input.focus();
    }
  }

  // ---------------- controls help ----------------
  function showControls() {
    // Grouped by what you are doing. The README lists the same controls, in the same order.
    const sections: [string, [string, string][]][] = [
      [
        'The basics',
        [
          ['See what something is', 'Rest the cursor on it, on the table or in your hand: its name appears, with its health'],
          ['Look at a card up close', 'Right-click it: on the table, in your hand, or in a pile you are searching. Its close-up lists every effect on it · scroll to zoom, drag to move around'],
          ['Move a card or a token', 'Solo table: left-drag it · in a game the table moves them, and only a card effect moves a card once it is played'],
          ['See what a phase involves', 'Click “guide” under the turn bar'],
        ],
      ],
      [
        'Playing cards from your hand',
        [
          ['Play a card', 'Drag it onto the table · or left-click it (it stands up with a gold edge), then click the spot where it goes. Esc, or a second click on it, lets go'],
          ['When cards can be played', 'Any card in your Summon Phase. After that only Spells, in your Attack Phase'],
          ['Where a card can go', 'While you hold it, the slots it is allowed in light up'],
          ['Play it face-down', 'Shift + drop, or Shift + click the spot · Triggers always go face-down'],
          ['Play it for free', 'Hold Ctrl (⌘ on a Mac) as you drop it or click its spot, in any phase. While the key is held the selected card is light blue instead of gold · a light blue beacon rises from a card played free'],
          ['Darkgale summon', 'Press “Darkgale summon” on your hand bar, then play the card: paid in Dark Gerzite (Spells cost double)'],
          ['Tier a card up', 'Drop a copy onto the same card · to take one back off: right-click the card, then Separate one copy'],
        ],
      ],
      [
        'Your cards on the table',
        [
          ['Attack with a unit', 'Left-click it, then click one of the ringed cards: the table rolls any miss, deals the damage (Shield, Buffer, Thorns, statuses) and taps it · Esc or right-click cancels'],
          ['Use a Tap ability', 'Left-click the unit and choose Use Tap ability · a unit that attacked or used its Tap stays tapped until your next turn'],
          ['Tap or untap a card by hand', 'Solo table only: Shift + left-click it (in a game cards turn when they attack or use their abilities)'],
          ['Flip a card', 'Solo table only: point at it and press F · or Flip in its close-up (in a game only a card effect flips one)'],
          ['Point at something', `Hold Alt (Option on a Mac) and drag from your card to its target. The arrow shows for ${CONFIG.pointers.arrowSeconds} seconds · click your own arrow to take it away sooner`],
          ['Sacrifice a unit for Dark Gerzite', 'In your Summon or Attack Phase, right-click your Troop or Building, then Sacrifice (it shows what you will get)'],
          ['Collect a sprung Trigger’s Gerzite', 'Flip it face-up, point at it and click the Payout chip above it'],
          ['Remove a Biome from play', 'Left-click it, yours or your opponent’s, and pay its removal cost · one whose turns are over is cleared away for free'],
          ['Restart a Tome whose turns are over', 'Left-click your Tome: pay its replay cost for a run 1 turn shorter, or discard it'],
          ['Discard one of your cards', 'Hold X and click it · or point at it and press Delete'],
        ],
      ],
      [
        'Your Emperor',
        [
          ['Use an ability', 'In your Attack Phase, left-click your Emperor and pick Ability 1 or 2: its cost comes off your bar and a timer goes on the Emperor as its cooldown · not while it is Frozen, Stunned or Entrenched'],
          ['Read your opponent’s abilities', 'Left-click their Emperor'],
          ['Attack with your Emperor', 'Left-click it and choose Attack, then click its target'],
          ['Lock an ability, or put one on cooldown by hand (solo table)', 'Drag a Locked token, or a turn timer, onto the Emperor: you are asked which ability it is for · right-click the token to change that'],
        ],
      ],
      [
        'Deck and discard pile',
        [
          ['Overdraw', 'In your Overdraw Phase, right-click your deck, then Overdraw (it costs Gerzite) · every other draw, search or look is done for you by the card that calls for it'],
          ['Make room in your hand', 'In your Overdraw Phase, drag cards from your hand onto your discard pile · you gain nothing for them'],
          ['Draw, take back a discarded card, or move a card between piles', 'Solo table only: right-click the pile'],
          ['Search, shuffle, or look at the top cards', 'Solo table only: left-click the pile (in a game a card that lets you search opens the pile for you)'],
          ['Take a card out of a pile you are searching', 'Left-click the card, then Take It'],
          ['Put a card from the table into a pile', 'Drag it onto the pile (a spent card, or anything on the solo table) · Shift + drop on your discard pile to choose bottom, middle or any spot'],
        ],
      ],
      [
        'Counters and tokens',
        [
          ['Bring in a counter, token or summoned unit', 'Counters/Summon, top right (solo table) · type in the search box to find one'],
          ['Bring in several at once', 'Right-click a counter in that menu: choose how many, the number each starts at, and how many turns each lasts'],
          ['Change a counter’s number (solo table)', 'Left-click +1 · Shift + click −1 · right-click for more · in a game the table keeps every counter itself'],
          ['Count turns on anything', 'Put a Start of Turn Timer or End of Turn Timer on or beside a card: it goes down by itself'],
          ['Make a counter last a set number of turns (solo table)', 'Right-click it, then Turns it lasts: a little clock on the counter counts them (Weakness, Susceptible, Damage Boost, Buffer, Thorns, Shield, Range)'],
          ['Roll to cure Burn', 'Press Roll the die when it asks, in your Attack Phase · an even number cures it'],
          ['Put a cost on a Lock (solo table)', 'Right-click the Locked token'],
          ['Roll a die', 'Click it · or drag it and let go while it is still moving, to throw it'],
          ['Delete a token or a spawned card (solo table)', 'Drag it off the table · or hold X and click it · or point at it and press Delete'],
          ['Copy a token or counter (solo table)', 'Hold C and click it: an exact copy (same number and timers) is set down just beside it'],
          ['Reveal a card to your opponent', 'Hold R and click a card in your hand, in a pile you are searching, or on top of your discard pile: your opponent is shown it and the log says so'],
        ],
      ],
      [
        'Several things at once',
        [
          ['Select', 'Ctrl + click each one · or Ctrl + drag a box around them'],
          ['Move them (solo table)', 'Drag any one of them: they all move together'],
          ['Delete them all', 'Press Delete · or hold X and click one · or right-click one, then Delete (tokens and spawned cards are deleted, your own cards are discarded)'],
          ['Let go of the selection', 'Esc, or click the table'],
        ],
      ],
      [
        'Pointing and looking around',
        [
          ['Point something out', 'Alt (Option) + click anywhere on the table'],
          ['Move the camera', 'W A S D · Q / E down and up · Shift to go faster · mouse wheel to zoom'],
          ['Look around / slide the view', 'Hold the middle mouse button and move / left-drag on empty table'],
          ['Top-down view / back to your seat', 'T / R'],
        ],
      ],
      [
        'The game',
        [
          ['Change the background', 'Game Menu, then Background. Only you see it'],
          ['Save, call or end the game', 'Game Menu, top right'],
          ['In a replay', 'Space = play or pause · → = next action · drag the slider to jump'],
        ],
      ],
    ];
    // on a phone or tablet: what a finger does in place of the mouse (the rest of the list still applies)
    if (isTouchScreen()) {
      sections.unshift([
        'On a touch screen',
        [
          ['Play a card', 'Tap it in your hand (it stands up with a gold edge), then tap the spot where it goes · or drag it there'],
          ['Right-click (close-ups and menus)', 'Press and hold: a card, a pile, a token, a card in your hand'],
          ['Zoom the table', 'Pinch with two fingers'],
          ['Move the view', 'Slide two fingers · or drag one finger across an empty part of the table'],
          ['Attack with a unit', 'Tap it, then tap one of the ringed cards'],
          ['Zoom a close-up', 'Pinch it · double-tap to reset'],
        ],
      ]);
    }
    let close = () => {};
    close = modal(
      el(
        'div',
        { class: 'stack' },
        el('h2', {}, 'Controls'),
        el(
          'div',
          { class: 'controls-sections' },
          sections.map(([title, rows]) =>
            el('section', { class: 'controls-section' }, el('h3', {}, title), el('table', { class: 'controls' }, el('tbody', {}, rows.map(([a, b]) => el('tr', {}, el('th', { scope: 'row' }, a), el('td', {}, b)))))),
          ),
        ),
        el('p', { class: 'muted small' }, 'Card facing you = active, sideways = inactive, facing your opponent = entrenched. Troops and buildings arrive entrenched unless they have Ambush. Biomes lie sideways in their slot and are never tapped: flip one if something happens to it.'),
        el('p', { class: 'muted small' }, 'The table looks after: card costs, Darkgale summons, Gerzite each turn, untapping, the end-of-turn draw, turn counters and timers, Bleed, Burn, Decay and Regeneration, Comatose ending on damage, Fractals, slot rules, paid Locks, the Emperor–Minion link and Overtime. Everything else is yours to play.'),
        el('div', { class: 'row-end' }, button('Got it', () => close(), 'btn-primary')),
      ),
      { wide: true, className: 'controls-modal' },
    );
  }

  // ---------------- leaving ----------------
  function confirmLeave() {
    let close = () => {};
    close = modal(
      el(
        'div',
        { class: 'stack' },
        el('h2', {}, 'Leave the table?'),
        el(
          'p',
          { class: 'muted' },
          online
            ? `The game stays open: come back to it from Play → 1v1 (or 2v2) → Rejoin Game (code ${online.code}) within ${mmss(CONFIG.reconnectSeconds * 1000)}, or ${view?.teams && !view.players[(view.you + 2) % 4]?.out ? 'you are out and your teammate takes over your side' : `you lose by forfeit${view?.bots?.length ? ' and the bot wins' : ''}`}. To end it now, use Forfeit or Call it here in the game menu instead.`
            : 'The game ends and the table is cleared. Your saved deck stays saved.',
        ),
        el('div', { class: 'row-end' }, button('Leave Table', () => { close(); leave(); }, 'btn-danger'), button('Stay', () => close(), 'btn-ghost')),
      ),
    );
  }

  function teardown() {
    if (leaving) return;
    leaving = true;
    window.clearInterval(timerTick);
    chat?.dispose();
    window.removeEventListener('keydown', onKey);
    window.removeEventListener('keydown', onFreeKey);
    window.removeEventListener('keydown', onRevealKey);
    window.removeEventListener('keyup', onRevealKey);
    window.removeEventListener('blur', onRevealBlur);
    window.removeEventListener('keyup', onFreeKey);
    window.removeEventListener('blur', onBlur);
    handTip(null);
    closeFloatMenu();
    document.querySelectorAll('.backdrop').forEach((b) => b.remove());
    // online: a finished game is forgotten here; one left part-way can be rejoined from the menu
    if (online) online.close(!!view?.result);
    else conn.close();
    table.dispose();
    pickBanner.remove();
    document.body.classList.remove('in-game');
  }
  function leave() {
    if (leaving) return;
    teardown();
    opts.onExit();
  }

  // ---------------- keyboard shortcuts ----------------
  const onKey = (e: KeyboardEvent) => {
    const a = document.activeElement;
    if (a instanceof HTMLInputElement || a instanceof HTMLTextAreaElement) return;
    if (document.querySelector('.backdrop')) return;
    if (e.key === 'Escape' && selectedCard) selectCard(null);
    else if (e.code === 'KeyT') topView();
    // (R resets the camera as it is let go, unless it was held for a reveal: see onRevealKey)
    else if (e.key === '?') showControls();
    else if (replay && e.code === 'Space') {
      e.preventDefault();
      if (replay.status.playing) replay.pause();
      else replay.play();
    } else if (replay && e.key === 'ArrowRight') replay.step();
  };
  window.addEventListener('keydown', onKey);

  // ---------------- wire it up ----------------
  overlay.append(logPanel, turnStack, toolbar, drawer, replayBar, handBar);
  if (isTouchScreen() && !touchTipShown) {
    touchTipShown = true;
    toast('Tap a card to pick it, then tap where it goes · press and hold anything for its close-up and options · pinch to zoom, two fingers to move the view.', 'info');
  }

  if (online) {
    // who is at the table: say when the other player drops out or comes back, and when we lose the server
    let was: boolean[] = [...online.connected];
    let watchers = online.watchers;
    if (spectating && online.joinedToWatch) toast('This game is already being played, so you’re watching it. The players’ hands stay hidden.', 'info');
    let wasOnline = true;
    online.onPresence((connected, isOnline) => {
      if (isOnline !== wasOnline) toast(isOnline ? 'Reconnected.' : 'Lost the connection to the game server — reconnecting…', isOnline ? 'ok' : 'error');
      wasOnline = isOnline;
      if (online.watchers !== watchers) {
        const more = online.watchers > watchers;
        if (!spectating) toast(more ? `Someone started watching (${online.watchers} watching).` : online.watchers ? `Someone stopped watching (${online.watchers} watching).` : 'Nobody is watching now.', 'info');
        watchers = online.watchers;
      }
      watchCount.textContent = spectating ? ` · ${online.watchers} watching` : '';
      connected.forEach((here, i) => {
        if ((!spectating && i === online.seat) || here === was[i]) return;
        const name = online.names[i] ?? 'Your opponent';
        toast(here ? `${name} is back at the table.` : `${name} left the table. They have ${mmss(CONFIG.reconnectSeconds * 1000)} to come back, or ${view ? goneOutcome(view, i as Seat) : 'they lose by forfeit'}.`, here ? 'ok' : 'info');
      });
      was = [...connected];
    });
    online.onClosed((reason) => {
      toast(reason, 'error');
      leave();
    });
  }
  let lastRefusal = 0;
  conn.onError((msg) => {
    // in a replay every attempt to change something gets the same answer: say it once in a while, not every time
    if (watching && Date.now() - lastRefusal < 6000) return;
    lastRefusal = Date.now();
    toast(msg, watching ? 'info' : 'error');
  });
  let tutorial: ReturnType<typeof startTutorial> | null = null;
  let callTimer = 0;
  conn.onView((v) => {
    view = v;
    showCode();
    // the Counters/Summon menu is for the solo table only: in a game against anyone (or a bot) it isn't there
    spawnBtn.hidden = v.mode !== 'solo';
    if (v.mode !== 'solo' && spawnOpen) toggleSpawn(false);
    if (spectating) {
      const res = v.result;
      replayNote = res ? `The game is over. ${res.winner === null ? 'It was a draw.' : `${v.players[res.winner]?.name ?? 'A player'} won.`}` : '';
    }
    table.update(v);
    // Calling Pillars are erupting: once the effect has played, this table asks for what follows (the hand is
    // discarded, The Remaining comes, the Pillars go). Every table at the game asks; the first one counts.
    if (v.calling?.length && !callTimer && !watching) {
      callTimer = window.setTimeout(() => {
        callTimer = 0;
        if (view?.calling?.length) conn.send({ type: 'callingResolve' });
      }, CONFIG.secretRares.callDelay * 1000);
    }
    renderLog(v);
    renderTurn(v);
    renderGuide(v);
    renderEndgame(v);
    tickTimers();
    renderHand(v);
    renderPledged(v);
    renderOpenHands(v);
    renderSearch(v);
    renderPeek(v);
    renderShowing(v);
    renderOverflow(v);
    renderPick(v);
    renderBargain(v);
    renderSpan(v);
    // a tutorial game against Martin: its hints, as the game gets to each thing
    if (v.tutorial && !watching) (tutorial ??= startTutorial(overlay, (panel) => table.keepClearOf(panel))).update(v);
  });
  if (replay && opts.replay?.playing !== false) replay.play();
}
